import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { flushSync } from "react-dom";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { fetchCatalog, previewQuote, createQuote, createColorRequest, listQuotes, estimateDelivery, analyzeSlopeImage, saveQuoteDraft, getQuote, formatEuro } from "@/lib/api";
import { generateQuotePDF } from "@/lib/pdf";
import { useAuth, formatApiError } from "@/lib/auth";
import { requiresDoubleDoor } from "@/lib/configurator/doorRules";
import { structureLabel } from "@/lib/utils";
import { AssemblyOverview } from "@/components/AssemblyOverview";
import AssemblyVisualizer3D from "@/components/AssemblyVisualizer3D";
import CabinetVisualizer from "@/components/CabinetVisualizer";
import CabinetVisualizer3D from "@/components/CabinetVisualizer3D";
import SectionEditor from "@/components/SectionEditor";
import { MiniIcon } from "@/components/MiniIcon";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, Plus, Minus, ArrowRight, ArrowLeftRight, Eye, Mail, X, Layers, Hand, MousePointer2, Home, Trash2, Lock, Copy, Pencil, Info, Link2, Unlink, Save, Mountain, TriangleAlert, Sparkles } from "lucide-react";

const debounce = (fn, ms) => {
  let t;
  return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
};

const uuid4 = () => Math.random().toString(36).slice(2, 6).toUpperCase();

function defaultSection(heightCm = 200, widthCm = null) {
  return {
    id: "A",
    has_door: false,
    door_mode: "none",
    door_standard: "single_right",
    door_double_master: "left",
    door_coverage: "full",
    door_height_mm: null,
    width_cm: widthCm,
    // Caisson volontairement vide : tous les éléments sont posés par l’artisan.
    elements: [],
    handle_id: null,
    door_mirror: false,
    drawers: 0,
    drawers_position: 0.0,
    has_penderie_bar: false,
    shelves_fixed: 0,
    shelves_mobile: 0,
  };
}

/** Génère N sections dont width_cm total = totalWidth (répartition égale). */
function equalSections(count, heightCm, totalWidth) {
  const each = Math.round((totalWidth / count) * 10) / 10; // précision 1 mm
  return Array.from({ length: count }, (_, i) => {
    // La dernière absorbe le reste pour totaliser exactement totalWidth
    const w = i === count - 1 ? Math.round((totalWidth - each * (count - 1)) * 10) / 10 : each;
    return defaultSection(heightCm, w);
  });
}

/** Auto-guérison des brouillons client (localStorage/?edit) : un ancien brouillon peut
 * contenir des cotes de sous-pente devenues hors bornes backend (ex: sous-pente arrière
 * <300mm, acceptée par un ancien frontend mais rejetée en 422 par le backend actuel) — on
 * les clampe silencieusement aux bornes réellement acceptées avant de les appliquer à l'état. */
function sanitizeOptions(opts) {
  if (!opts) return opts;
  const next = { ...opts };
  if (next.under_slope_low_height_mm != null) next.under_slope_low_height_mm = Math.max(100, Math.min(2700, next.under_slope_low_height_mm));
  if (next.under_slope_rear_low_height_mm != null) next.under_slope_rear_low_height_mm = Math.max(300, Math.min(2400, next.under_slope_rear_low_height_mm));
  return next;
}

/** Force les IDs de sections en A/B/C/D... */
function relabelSections(sections) {
  return sections.map((s, i) => ({ ...s, id: String.fromCharCode(65 + i) }));
}

/** Garantit des largeurs positives et une somme strictement égale au meuble. */
function normalizeSectionWidths(sections, totalWidthCm, minimumCm = 20) {
  if (!sections.length) return sections;
  const minimumTotal = sections.length * minimumCm;
  if (totalWidthCm < minimumTotal) return sections;
  const current = sections.map((section) => Number(section.width_cm));
  const hasInvalidWidth = current.some((value) => !Number.isFinite(value) || value < minimumCm);
  const currentTotal = current.reduce((sum, value) => sum + (Number.isFinite(value) && value > 0 ? value : 0), 0);
  if (!hasInvalidWidth && Math.abs(currentTotal - totalWidthCm) < 0.01) return sections;
  const weights = current.map((value) => Number.isFinite(value) && value > 0 ? value : 1);
  const weightsTotal = weights.reduce((sum, value) => sum + value, 0);
  const available = totalWidthCm - minimumTotal;
  let remaining = totalWidthCm;
  return sections.map((section, index) => {
    const sectionWidth = index === sections.length - 1
      ? Math.round(remaining * 10) / 10
      : Math.round((minimumCm + available * (weights[index] / weightsTotal)) * 10) / 10;
    remaining -= sectionWidth;
    return { ...section, width_cm: Math.max(minimumCm, sectionWidth) };
  });
}

export default function Configurator({ embed = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [recentQuotes, setRecentQuotes] = useState([]);

  const [templateId, setTemplateId] = useState("template-battant");
  const [categoryId, setCategoryId] = useState("dressing");
  const [width, setWidth] = useState(100);
  const [height, setHeight] = useState(200);
  const [depth, setDepth] = useState(50);
  const [sections, setSections] = useState(relabelSections(equalSections(1, 200, 100)));
  const [viewMode, setViewMode] = useState("2d");
  const [twoDView, setTwoDView] = useState("front");
  const [activeElementTool, setActiveElementTool] = useState(null);
  const [selectedPlacedElement, setSelectedPlacedElement] = useState(null);
  // Chantier 3.3 : légende d'actions flottante (dupliquer/supprimer) pour l'élément sélectionné
  // sur le schéma — résolution défensive au cas où l'élément aurait été retiré entre-temps.
  const selectedElementInfo = (() => {
    if (!selectedPlacedElement) return null;
    const sec = sections[selectedPlacedElement.sectionIdx];
    const el = (sec?.elements || []).find((e) => e.id === selectedPlacedElement.elId);
    return el ? { sectionIdx: selectedPlacedElement.sectionIdx, el } : null;
  })();
  const [options, setOptions] = useState({
    has_back_panel: true,
    plinth: true,
    plinth_height_mm: 80,
    fileur_left: false, fileur_left_width: 15,
    fileur_right: false, fileur_right_width: 15,
    fileur_top: false, fileur_top_width: 15,
    side_left_facade: false,
    side_right_facade: false,
    feet_visible: true,
    prepared_for_coulissant: false,
    drawer_gamme: "M",
    under_slope_enabled: false,
    under_slope_direction: "left",
    under_slope_low_height_mm: 1200,
    under_slope_bevel_width_mm: 600,
    under_slope_rear_enabled: false,
    under_slope_rear_low_height_mm: 1200,
    under_slope_rear_bevel_depth_mm: 450,
  });
  const [carcassFinishId, setCarcassFinishId] = useState("blanc_premium");
  // Chantier 3.1 : "none" = Sans façade (pas de porte chiffrée, choix par défaut à l'ouverture),
  // "" = Identique au caisson (une porte existe, même couleur que le caisson),
  // "<finishId>" = façade différenciée (couleur distincte, +30/40% suivant la gamme).
  const [doorsFinishId, setDoorsFinishId] = useState("none");
  const [carcassMatchesFacade, setCarcassMatchesFacade] = useState(false);
  const [defaultHandleId, setDefaultHandleId] = useState("sdb_profil_alu");
  const [activeMode, setActiveMode] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showDoors, setShowDoors] = useState(true);
  // Sélection de l'univers (Dressing / SdB / Cuisine / Autres) — persistée en session
  const [universe, setUniverse] = useState(() => {
    try {
      const u = new URLSearchParams(window.location.search).get("u");
      if (u && ["dressing", "salle_de_bain", "cuisine", "autres"].includes(u)) {
        sessionStorage.setItem("aih-universe", u);
        return u;
      }
      return sessionStorage.getItem("aih-universe") || null;
    } catch { return null; }
  });
  // Applique les defaults au 1er rendu si l'univers est déjà connu (arrivée directe via ?u=...)
  const universeInitApplied = useRef(false);
  const chooseUniverse = (u) => {
    setUniverse(u);
    try { sessionStorage.setItem("aih-universe", u); } catch { /* noop */ }
  };
  const resetUniverse = () => {
    setUniverse(null);
    try { sessionStorage.removeItem("aih-universe"); } catch { /* noop */ }
  };
  const isSdb = universe === "salle_de_bain";
  const canUseUnderSlope = universe === "dressing" || universe === "autres";
  // Le configurateur reste immédiatement manipulable : le guide n'ouvre plus de calque bloquant au chargement.
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const dismissWelcome = () => {
    setWelcomeOpen(false);
    try { localStorage.setItem("aih-welcome-seen", "1"); } catch { /* noop */ }
  };

  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [applyInstallationFee, setApplyInstallationFee] = useState(false);
  const [deliveryPostalCode, setDeliveryPostalCode] = useState("");
  const [deliveryEstimate, setDeliveryEstimate] = useState(null); // { city, distance_km, tier, price_ht } | null
  const [deliveryChecking, setDeliveryChecking] = useState(false);
  const [applyDelivery, setApplyDelivery] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [colorForm, setColorForm] = useState({ name: "", finish_type: "", contact_name: "", contact_email: "", notes: "" });
  const [colorSending, setColorSending] = useState(false);
  const [client, setClient] = useState({ company: "", contact_name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  // Multi-meubles : liste de meubles supplémentaires snapshoteés dans ce projet.
  const [additionalItems, setAdditionalItems] = useState([]);
  const [linkedAdditionalItemId, setLinkedAdditionalItemId] = useState(null);
  const [assemblyModuleOrder, setAssemblyModuleOrder] = useState("a_left");
  const [assemblyDialogOpen, setAssemblyDialogOpen] = useState(false);
  const [assemblyCandidateId, setAssemblyCandidateId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null); // id du meuble en attente de confirmation de suppression
  const [customColorRequested, setCustomColorRequested] = useState(false); // +20% couleur/décor hors nuancier
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [serverDraftId, setServerDraftId] = useState(null);
  const [savingProject, setSavingProject] = useState(false);
  const [editingQuoteId, setEditingQuoteId] = useState(null);
  const [slopeImageName, setSlopeImageName] = useState("");
  const [slopeAnalysisNote, setSlopeAnalysisNote] = useState("");
  const [slopeAnalysisStatus, setSlopeAnalysisStatus] = useState("idle");
  const [slopeAnalyzing, setSlopeAnalyzing] = useState(false);
  const slopePhotoInputRef = useRef(null);

  useEffect(() => {
    fetchCatalog().then(setCatalog).catch((e) => toast.error("Impossible de charger le catalogue", { description: e?.message }));
  }, []);

  // Charger les devis récents pour la carte de bienvenue (uniquement si connecté)
  useEffect(() => {
    if (!user) { setRecentQuotes([]); return; }
    listQuotes()
      .then((q) => setRecentQuotes((q || []).slice(0, 3)))
      .catch(() => setRecentQuotes([]));
  }, [user]);

  // Estimation livraison : débounce sur la saisie du code postal client (calcul auto de la distance
  // depuis l'atelier). Une zone >100km ou un code postal invalide désactive l'ajout de la livraison.
  useEffect(() => {
    const cp = deliveryPostalCode.trim();
    if (!/^\d{5}$/.test(cp)) {
      setDeliveryEstimate(null);
      setApplyDelivery(false);
      return;
    }
    setDeliveryChecking(true);
    const t = setTimeout(() => {
      estimateDelivery(cp)
        .then((est) => {
          setDeliveryEstimate(est);
          setApplyDelivery(false);
        })
        .catch(() => setDeliveryEstimate({ tier: "unavailable" }))
        .finally(() => setDeliveryChecking(false));
    }, 600);
    return () => clearTimeout(t);
  }, [deliveryPostalCode]);

  // Restore Draft on mount
  useEffect(() => {
    if (!catalog || draftLoaded) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const urlUniverse = params.get("u");
      if (params.get("edit")) {
        setDraftLoaded(true);
        return;
      }
      if (params.get("new") === "1") {
        localStorage.removeItem("aih-config-draft");
        setDraftLoaded(true);
        return;
      }
      const saved = localStorage.getItem("aih-config-draft");
      if (saved) {
        const parsed = JSON.parse(saved);
        // Univers explicitement demandé dans l'URL (ex: clic "Meuble de salle de bain" depuis
        // l'accueil) : il doit toujours gagner. Un brouillon d'un AUTRE univers ne doit jamais
        // être restauré ici (sinon on se retrouve avec un template Dressing/porte sous un univers
        // SdB — redirection incohérente).
        if (urlUniverse && parsed.universe && parsed.universe !== urlUniverse) {
          setDraftLoaded(true);
          return;
        }
        if (parsed.templateId) setTemplateId(parsed.templateId);
        // categoryId doit toujours rester synchronisé avec universe (même id-space :
        // dressing/salle_de_bain/cuisine/autres) — on ignore un categoryId de brouillon
        // incohérent avec l'univers restauré pour éviter le bug de breadcrumb.
        if (parsed.universe) setCategoryId(parsed.universe);
        else if (parsed.categoryId) setCategoryId(parsed.categoryId);
        if (parsed.width) setWidth(parsed.width);
        if (parsed.height) setHeight(parsed.height);
        if (parsed.depth) setDepth(parsed.depth);
        if (parsed.sections) setSections(parsed.sections);
        if (parsed.options) setOptions(sanitizeOptions(parsed.options));
        if (parsed.carcassFinishId) setCarcassFinishId(parsed.carcassFinishId);
        if (parsed.doorsFinishId !== undefined) setDoorsFinishId(parsed.doorsFinishId);
        if (parsed.additionalItems) setAdditionalItems(parsed.additionalItems);
        if (parsed.linkedAdditionalItemId) setLinkedAdditionalItemId(parsed.linkedAdditionalItemId);
        if (parsed.assemblyModuleOrder) setAssemblyModuleOrder(parsed.assemblyModuleOrder);
        if (parsed.universe) setUniverse(parsed.universe);
        if (parsed.customColorRequested) setCustomColorRequested(parsed.customColorRequested);
        if (parsed.serverDraftId) setServerDraftId(parsed.serverDraftId);
        universeInitApplied.current = true; // Empêche l'override par defaut
      }
    } catch (e) {
      // ignore
    }
    setDraftLoaded(true);
  }, [catalog, draftLoaded]);

  useEffect(() => {
    const editId = new URLSearchParams(window.location.search).get("edit");
    if (!editId || !catalog || !user || editingQuoteId === editId) return;
    getQuote(editId).then((quote) => {
      const config = quote.config;
      setEditingQuoteId(quote.id);
      setUniverse(config.universe || "dressing");
      setCategoryId(config.universe || "dressing");
      setTemplateId(config.template_id);
      setWidth(config.total_width);
      setHeight(config.height);
      setDepth(config.depth);
      setSections(config.sections);
      setOptions(sanitizeOptions(config.options));
      setCarcassFinishId(config.carcass_finish_id);
      setDoorsFinishId(config.doors_finish_id || config.carcass_finish_id);
      setDefaultHandleId(config.default_handle_id || "sdb_profil_alu");
      setQuantity(config.quantity || 1);
      setCustomColorRequested(!!config.custom_color_requested);
      setAdditionalItems((quote.items || []).map((item) => ({ ...item, id: item.id, label: item.label, config: item.config, breakdown: item.breakdown })));
      setLinkedAdditionalItemId(quote.assembly?.module_b_item_id || null);
      setAssemblyModuleOrder(quote.assembly?.module_order || "a_left");
      setProjectName(quote.project_name || "");
      setApplyInstallationFee(!!quote.installation_fee_applied);
      setApplyDelivery(!!quote.delivery_applied);
      setDeliveryPostalCode("");
      toast.success("Devis chargé pour modification", { description: "Vos changements remplaceront ce devis après confirmation." });
    }).catch((error) => toast.error("Impossible de charger le devis", { description: formatApiError(error) }));
  }, [catalog, user, editingQuoteId]); // eslint-disable-line

  // Save Draft automatically
  useEffect(() => {
    if (!draftLoaded) return;
    const timer = setTimeout(() => {
      const draft = {
        templateId, categoryId, width, height, depth, sections, options,
        carcassFinishId, doorsFinishId, additionalItems, linkedAdditionalItemId, assemblyModuleOrder, universe, customColorRequested, serverDraftId
      };
      localStorage.setItem("aih-config-draft", JSON.stringify(draft));
    }, 1000);
    return () => clearTimeout(timer);
  }, [templateId, categoryId, width, height, depth, sections, options, carcassFinishId, doorsFinishId, additionalItems, linkedAdditionalItemId, assemblyModuleOrder, universe, customColorRequested, serverDraftId, draftLoaded]);

  const template = useMemo(() => catalog?.templates.find((t) => t.id === templateId), [catalog, templateId]);
  const hasHandleEligibleElement = sections.some((section) => section.has_door || (section.elements || []).some((element) => element.kind === "drawer"));
  const carcassFinish = useMemo(() => catalog?.finishes.find((f) => f.id === carcassFinishId), [catalog, carcassFinishId]);
  const doorsFinish = useMemo(
    () => catalog?.finishes.find((f) => f.id === (doorsFinishId || carcassFinishId)),
    [catalog, doorsFinishId, carcassFinishId],
  );
  const hasDifferentiatedFacade = !!doorsFinishId && doorsFinishId !== "none" && doorsFinishId !== "";
  const selectedHandle = useMemo(
    () => (isSdb ? catalog?.sdb_handles : catalog?.handles)?.find((h) => h.id === defaultHandleId),
    [catalog, isSdb, defaultHandleId],
  );

  const category = useMemo(() => catalog?.categories?.find((c) => c.id === categoryId), [catalog, categoryId]);

  // Sélection catégorie : applique les dimensions par défaut et le template. Ne touche pas aux sections existantes.
  const applyCategory = (catId) => {
    setCategoryId(catId);
    const cat = catalog?.categories?.find((c) => c.id === catId);
    if (!cat) return;
    setTemplateId(cat.template_id || "template-battant");
    setWidth(cat.default_width);
    setHeight(cat.default_height);
    setDepth(cat.default_depth);
    // Lot 15 iter3 : toujours démarrer avec 1 seule section.
    setSections(relabelSections(equalSections(1, cat.default_height, cat.default_width)));
  };

  const templateVariant = options.prepared_for_coulissant ? "coulissant" : (template?.variant || "battant");

  // Salle de Bain : verrouille automatiquement hauteur/profondeur selon le template SdB
  const SDB_HEIGHT_BY_TEMPLATE = { "sdb-1tiroir": 30, "sdb-2tiroirs": 60, "sdb-3tiroirs": 80, "sdb-colonne-2portes": 140 };
  const SDB_DEPTH_BY_TEMPLATE = { "sdb-1tiroir": 47, "sdb-2tiroirs": 47, "sdb-3tiroirs": 47, "sdb-colonne-2portes": 20 };
  const SDB_MIN_WIDTH = 55;
  const SDB_MAX_WIDTH = 120;
  const SDB_FINISH_IDS = ["blanc_premium", "gris_graphite", "chene_davos", "beton_chicago"];
  useEffect(() => {
    if (SDB_HEIGHT_BY_TEMPLATE[templateId] !== undefined) {
      setHeight(SDB_HEIGHT_BY_TEMPLATE[templateId]);
      setDepth(SDB_DEPTH_BY_TEMPLATE[templateId]);
      if (templateId === "sdb-colonne-2portes") setWidth(30);
      else if (width < SDB_MIN_WIDTH) setWidth(SDB_MIN_WIDTH);
      else if (width > SDB_MAX_WIDTH) setWidth(SDB_MAX_WIDTH);
    }
  }, [templateId]); // eslint-disable-line

  // Garde-fou : une config chargée (ancien brouillon, devis en édition, changement d'univers)
  // peut dépasser la largeur max du module courant. Plutôt que de laisser un état invalide qui
  // bloque le calcul du devis (erreur serveur au-delà de la limite atelier), on ramène automatiquement
  // la largeur — et les sections, au prorata — à la limite autorisée.
  useEffect(() => {
    const maxCm = 270;
    if (width > maxCm) {
      setWidth(maxCm);
      setSections((previous) => relabelSections(normalizeSectionWidths(previous, maxCm)));
      toast.info(`Largeur ramenée à ${maxCm * 10} mm`, { description: "Limite pour un seul module — ajoutez un second meuble au projet pour aller plus loin." });
    }
  }, [universe, width]); // eslint-disable-line

  // Auto-cast finition vers palette SdB si univers SdB et finition actuelle non compatible.
  // La SdB n'a ni "Sans façade" ni "Identique au caisson" (toujours une façade réelle, propre
  // au tiroir) : on force systématiquement une finition concrète de la palette SdB.
  useEffect(() => {
    if (isSdb && carcassFinishId && !SDB_FINISH_IDS.includes(carcassFinishId)) {
      setCarcassFinishId("blanc_premium");
    }
    if (isSdb && (!doorsFinishId || doorsFinishId === "none" || doorsFinishId === "" || !SDB_FINISH_IDS.includes(doorsFinishId))) {
      setDoorsFinishId("blanc_premium");
    }
    // Poignées : catalogue unique (4 références exclusives) pour l'ensemble du projet —
    // plus de distinction/réinitialisation par univers.
    // Reset la plus-value "hors nuancier" si on passe en SdB (le toggle est masqué,
    // sinon le +20% resterait invisiblement appliqué au calcul).
    if (isSdb && customColorRequested) {
      setCustomColorRequested(false);
    }
  }, [isSdb, carcassFinishId, doorsFinishId, defaultHandleId, customColorRequested]); // eslint-disable-line

  // Double vasque : option réservée aux meubles ≥1200 mm de large. Si la largeur redescend
  // sous ce seuil (redimensionnement), elle se désactive automatiquement pour rester cohérente.
  useEffect(() => {
    if (isSdb && options.sdb_double_vasque && width * 10 < 1200) {
      setOptions((previous) => ({ ...previous, sdb_double_vasque: false }));
    }
  }, [isSdb, options.sdb_double_vasque, width]); // eslint-disable-line

  // Chantier 3.2 : case "Identique à la façade" (caisson) — fait suivre la finition du caisson
  // sur celle de la façade dès qu'une couleur de façade différenciée réelle est choisie.
  useEffect(() => {
    if (carcassMatchesFacade && doorsFinishId && doorsFinishId !== "none" && doorsFinishId !== "" && doorsFinishId !== carcassFinishId) {
      setCarcassFinishId(doorsFinishId);
    }
  }, [carcassMatchesFacade, doorsFinishId]); // eslint-disable-line

  // Apply universe defaults on first mount (arrivée via ?u=... depuis la landing)
  useEffect(() => {
    if (universeInitApplied.current || !universe || !catalog || !catalog.templates?.length) return;
    universeInitApplied.current = true;
    if (universe === "salle_de_bain") {
      setCategoryId("salle_de_bain");
      setTemplateId("sdb-2tiroirs");
      setCarcassFinishId("blanc_premium");
      setDoorsFinishId("blanc_premium");
      setDefaultHandleId("sdb_profil_alu");
      setOptions((o) => ({ ...o, plinth: false, feet_visible: true, sdb_foot_ellipse_height: o.sdb_foot_ellipse_height || 100 }));
    } else if (universe === "cuisine") {
      setCategoryId("cuisine");
      setTemplateId("template-battant");
      setWidth(60);
      setHeight(75);
      setDepth(56);
      setOptions((o) => ({ ...o, feet_visible: false, plinth: true }));
      setSections(relabelSections(equalSections(1, 75, 60)));
    } else if (universe === "autres") {
      setCategoryId("autres");
      setTemplateId("template-open");
      setWidth(120);
      setHeight(200);
      setDepth(45);
      setSections(relabelSections(equalSections(1, 200, 120)));
    }
    // Dressing garde les valeurs par défaut
  }, [universe, catalog]); // eslint-disable-line
  // Ne remplace jamais les sections ni leurs éléments (règle d'or drag & drop) SAUF pour SdB.
  useEffect(() => {
    if (!template) return;
    const wantsDoors = template.variant === "battant" || template.variant === "sdb_column";

    // Les meubles SdB imposent une structure stricte à 1 section (et suppriment les anciens éléments intérieurs).
    // Important : on recalcule ici la largeur/hauteur SdB à partir du template (et non des states
    // `width`/`height`), qui peuvent encore contenir l'ancienne valeur le temps que l'autre effet
    // de verrouillage des dimensions se propage — sinon `width_cm` reste périmé un render de trop,
    // ce qui fausse la règle des 600 mm (simple/double porte) juste après un changement de modèle.
    if (template.variant === "sdb" || template.variant === "sdb_column") {
      const sdbWidthCm = template.id === "sdb-colonne-2portes"
        ? 30
        : Math.max(SDB_MIN_WIDTH, Math.min(SDB_MAX_WIDTH, width));
      const sdbHeightCm = SDB_HEIGHT_BY_TEMPLATE[template.id] ?? template.default_height ?? height;
      setSections([{ ...defaultSection(sdbHeightCm, sdbWidthCm), has_door: wantsDoors, elements: [] }]);
    } else {
      // Les dressings/caissons démarrent toujours sans façade ; la porte se pose depuis la palette.
      setSections((prev) => prev.map((s) => ({ ...s, has_door: false })));
    }

  }, [templateId]); // eslint-disable-line

  const fixedShelfRequired = height >= 121 ? 1 : 0;

  // Règle métier menuiserie : dès que la hauteur atteint 121 cm (1210 mm), chaque section
  // doit avoir 1 tablette fixe structurelle verrouillée. Elle est déplaçable (drag) mais
  // non-supprimable. Non applicable en Salle de Bain (structure imposée par le produit).
  useEffect(() => {
    if (isSdb) return;
    setSections((prev) => {
      const needsStructural = height >= 121;
      let changed = false;
      const next = prev.map((sec) => {
        const els = sec.elements || [];
        const hasStructural = els.some((e) => e.locked && e.kind === "shelf_fixed");
        if (needsStructural && !hasStructural) {
          changed = true;
          return {
            ...sec,
            elements: [
              { id: `structural-shelf-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, kind: "shelf_fixed", y_mm: Math.round((height * 10) / 2), locked: true },
              ...els,
            ],
          };
        }
        if (!needsStructural && hasStructural) {
          changed = true;
          return { ...sec, elements: els.filter((e) => !(e.locked && e.kind === "shelf_fixed")) };
        }
        return sec;
      });
      return changed ? next : prev;
    });
  }, [height, isSdb, sections]);

  // Règle de stabilité atelier : au-delà de 800 mm, une section unique devient
  // automatiquement deux vraies travées. Cela crée un montant structurel réel,
  // réglable sur le plan, plutôt qu'un trait central artificiel.
  useEffect(() => {
    if (isSdb || width <= 80 || sections.length !== 1) return;
    const [onlySection] = sections;
    // Règle des séparations > 800 mm : si l'artisan a explicitement supprimé la séparation
    // (section marquée manual_wide_section), on respecte son choix et on ne la restaure pas.
    if (onlySection.manual_wide_section) return;
    const leftWidth = Math.round((width / 2) * 10) / 10;
    const rightWidth = Math.round((width - leftWidth) * 10) / 10;
    setSections(relabelSections([
      { ...onlySection, width_cm: leftWidth },
      { ...defaultSection(height, rightWidth), has_door: false },
    ]));
    toast.info("Section de soutien ajoutée", {
      description: "Les meubles de plus de 800 mm sont séparés en deux travées structurelles.",
    });
  }, [width, height, isSdb, sections]);

  // Normalisation canonique des façades : la règle des 600 mm s'applique dans les deux sens
  // (simple ↔ double) mais le côté de charnière choisi (gauche/droite) est toujours conservé.
  // `door_standard` fait foi pour la façade pleine ; seules les parties haute/basse peuvent
  // porter un sens propre.
  useEffect(() => {
    setSections((previous) => {
      let changed = false;
      const next = previous.map((section) => {
        if (!section.has_door) return section;
        const shouldDouble = requiresDoubleDoor(section.width_cm);
        // Au-delà de 600 mm, la double porte est imposée. En dessous, elle reste un choix
        // client explicite (au même titre que 1G/1D) : on ne doit pas l'écraser.
        const normalize = (std) => {
          if (shouldDouble) return "double";
          if (std === "single_left" || std === "double") return std;
          return "single_right";
        };
        const oldStandard = section.door_standard;
        const targetStandard = normalize(oldStandard);
        const targetMode = targetStandard === "double" ? "double" : "single";
        const parts = section.door_parts || {};
        const partsOk = Object.entries(parts).every(([key, part]) => key === "full" ? part.standard === undefined : (!part.standard || normalize(part.standard) === part.standard));
        // On garde en mémoire le dernier côté de charnière simple (G/D) choisi, même après
        // un passage en double porte, pour le restituer dès que la porte repasse en simple.
        const targetLastSingle = (targetStandard === "single_left" || targetStandard === "single_right")
          ? targetStandard
          : ((oldStandard === "single_left" || oldStandard === "single_right") ? oldStandard : section.door_last_single_standard);
        if (section.door_standard === targetStandard && section.door_mode === targetMode && partsOk && section.door_last_single_standard === targetLastSingle) return section;
        changed = true;
        const synchronizedParts = Object.fromEntries(Object.entries(parts).map(([key, part]) => {
          if (key === "full") { const { standard: _ignored, ...rest } = part; return [key, rest]; }
          return [key, part.standard ? { ...part, standard: normalize(part.standard) } : part];
        }));
        return { ...section, door_mode: targetMode, door_standard: targetStandard, door_last_single_standard: targetLastSingle, door_parts: synchronizedParts };
      });
      return changed ? relabelSections(next) : previous;
    });
  }, [sections]);


  // Garde les cotes de sous-pente valides lorsque l'artisan modifie le gabarit du caisson.
  useEffect(() => {
    setOptions((prev) => {
    if (!prev.under_slope_enabled && !prev.under_slope_rear_enabled) return prev;
      const maxLow = Math.round(height * 10) - 10;
      const nextLow = Math.max(100, Math.min(maxLow, prev.under_slope_low_height_mm || Math.round(height * 10 * 0.6)));
      const nextBevel = Math.max(100, Math.min(Math.round(width * 10), prev.under_slope_bevel_width_mm || Math.round(Math.min(width * 10, 600))));
      // Backend impose ge=300 pour la hauteur basse arrière (contrairement à l'avant, ge=100) — bornes alignées.
      const nextRearLow = Math.max(300, Math.min(maxLow, prev.under_slope_rear_low_height_mm || Math.round(height * 10 * 0.6)));
      const nextRearBevel = Math.max(100, Math.min(Math.round(depth * 10), prev.under_slope_rear_bevel_depth_mm || Math.round(Math.min(depth * 10, 450))));
      if (nextLow === prev.under_slope_low_height_mm && nextBevel === prev.under_slope_bevel_width_mm && nextRearLow === prev.under_slope_rear_low_height_mm && nextRearBevel === prev.under_slope_rear_bevel_depth_mm) return prev;
      return {
        ...prev,
        under_slope_low_height_mm: nextLow,
        under_slope_bevel_width_mm: nextBevel,
        under_slope_rear_low_height_mm: nextRearLow,
        under_slope_rear_bevel_depth_mm: nextRearBevel,
      };
    });
  }, [height, width, depth]);

  useEffect(() => {
    if (!universe || canUseUnderSlope) return;
    setOptions((prev) => prev.under_slope_enabled ? { ...prev, under_slope_enabled: false } : prev);
  }, [canUseUnderSlope, universe]);

  // Quand la largeur totale change (slider/input) → redistribue proportionnellement les section widths.
  useEffect(() => {
    setSections((prev) => {
      const normalized = normalizeSectionWidths(prev, width, SECTION_MIN_CM);
      return normalized === prev ? prev : relabelSections(normalized);
    });
  }, [width]);

  // Répare immédiatement un ancien brouillon dont les cotes seraient devenues incohérentes.
  useEffect(() => {
    setSections((prev) => {
      const normalized = normalizeSectionWidths(prev, width, SECTION_MIN_CM);
      return normalized === prev ? prev : relabelSections(normalized);
    });
  }, [sections, width]);

  // Ancrage des éléments intérieurs : quand la hauteur globale change, re-contraindre
  // les y_mm de chaque aménagement pour qu'ils restent à l'intérieur du meuble.
  useEffect(() => {
    const maxYMm = Math.round(height * 10) - 20; // marge haute de 20mm
    const minYMm = 20; // marge basse
    setSections((prev) => {
      let changed = false;
      const next = prev.map((sec) => {
        if (!sec.elements || sec.elements.length === 0) return sec;
        const clamped = sec.elements.map((el) => {
          const halfH = (el.height_mm || 150) / 2;
          const lower = minYMm + halfH;
          const upper = maxYMm - halfH;
          const clampedY = Math.max(lower, Math.min(upper, el.y_mm));
          if (clampedY !== el.y_mm) { changed = true; return { ...el, y_mm: clampedY }; }
          return el;
        });
        return clamped === sec.elements ? sec : { ...sec, elements: clamped };
      });
      return changed ? next : prev;
    });
  }, [height]); // eslint-disable-line react-hooks/exhaustive-deps

  // Chantier 3.1 : "Sans façade" (doorsFinishId === "none") ne doit jamais chiffrer de porte,
  // même si des sections ont individuellement has_door=true (configurées avant ce choix) —
  // on neutralise has_door uniquement dans la copie envoyée au pricing, jamais dans l'état
  // `sections` lui-même (qui reste intact pour l'affichage 2D/3D, conformément à la règle
  // "ne pas toucher au schéma/à la géométrie").
  const sectionsForPricing = doorsFinishId === "none" ? sections.map((s) => ({ ...s, has_door: false })) : sections;
  // Le backend ne connaît pas le sentinel "none" : on lui envoie null (= repli sur le caisson,
  // sans incidence puisqu'aucune porte n'est chiffrée de toute façon dans ce cas).
  const pricingDoorsFinishId = doorsFinishId === "none" ? null : (doorsFinishId || null);

  // Résilience : une coupure réseau transitoire (ex. redémarrage backend, redéploiement) ne
  // doit jamais bloquer durablement "Valider le devis" (breakdown resterait null pour
  // toujours). `!e.response` = erreur réseau/transport (pas de réponse HTTP reçue) → on
  // retente automatiquement et silencieusement jusqu'à 2 fois. Une vraie erreur de
  // validation (422, etc. — `e.response` présent) est, elle, affichée immédiatement, sans
  // retentative inutile.
  const fetchPreviewOnce = async (cfg, attempt = 0) => {
    try {
      setLoading(true);
      const b = await previewQuote(cfg);
      setBreakdown(b);
    } catch (e) {
      if (!e?.response && attempt < 2) {
        setTimeout(() => fetchPreviewOnce(cfg, attempt + 1), 1500);
      } else {
        toast.error("Erreur de calcul", { description: formatApiError(e) });
      }
    } finally { setLoading(false); }
  };
  const requestPreview = useMemo(() => debounce(fetchPreviewOnce, 250), []);

  useEffect(() => {
    if (!catalog) return;
    requestPreview({
      universe,
      template_id: templateId,
      total_width: width, height, depth,
      sections: sectionsForPricing,
      carcass_finish_id: carcassFinishId,
      doors_finish_id: pricingDoorsFinishId,
      default_handle_id: defaultHandleId,
      quantity,
      custom_color_requested: customColorRequested,
      options,
    });
  }, [catalog, universe, templateId, width, height, depth, sections, carcassFinishId, doorsFinishId, defaultHandleId, quantity, customColorRequested, options, requestPreview]);

  // Largeur minimale absolue d'une section (200 mm) — permet d'accueillir tiroir/penderie.
  const SECTION_MIN_CM = 20;

  const addSection = () => {
    if (sections.length >= 20) return toast.error("Maximum 20 sections");
    const nextCount = sections.length + 1;
    const each = width / nextCount;
    if (each < SECTION_MIN_CM) {
      const requiredTotal = nextCount * SECTION_MIN_CM;
      toast.error(
        `Impossible d'ajouter une section`,
        { description: `Il faut au minimum ${requiredTotal * 10} mm de largeur totale pour ${nextCount} sections (min. ${SECTION_MIN_CM * 10} mm par section). Agrandissez d'abord la largeur totale.` }
      );
      return;
    }
    const eachRounded = Math.round(each * 10) / 10; // précision 1 mm
    const rest = Math.round((width - eachRounded * (nextCount - 1)) * 10) / 10;
    const resized = sections.map((s) => ({ ...s, width_cm: eachRounded }));
    resized.push({ ...defaultSection(height, rest) });
    setSections(relabelSections(resized));
  };
  const removeSection = (idx) => {
    if (sections.length <= 1) return toast.error("Minimum 1 section");
    const kept = sections.filter((_, i) => i !== idx);
    const each = Math.round((width / kept.length) * 10) / 10;
    const rest = Math.round((width - each * (kept.length - 1)) * 10) / 10;
    const redistributed = kept.map((s, i) => ({
      ...s,
      width_cm: i === kept.length - 1 ? rest : each,
      elements: (s.elements || []).map((element) => ({
        ...element,
        span_sections: Math.min(element.span_sections || 1, kept.length - i),
      })),
    }));
    setSections(relabelSections(redistributed));
  };
  const updateSection = (idx, next) => {
    setSections(relabelSections(sections.map((s, i) => (i === idx ? next : s))));
  };

  /** Changement direct de la largeur totale (saisie clavier) — recale aussi les sections au
   *  prorata pour qu'elles restent toujours cohérentes avec le nouveau total (même logique que
   *  le drag sur le schéma et le garde-fou anti-dépassement 2700 mm). */
  const handleSetWidthCm = useCallback((nextCm) => {
    const minCm = 30;
    const maxCm = 270;
    const clamped = Math.round(Math.max(minCm, Math.min(maxCm, nextCm)) * 10) / 10;
    setWidth(clamped);
    setSections((prev) => relabelSections(normalizeSectionWidths(prev, clamped)));
  }, []);

  /** Drag horizontal d'une séparation SVG → ajuste width_cm des 2 sections adjacentes. */
  const handleSeparatorDrag = useCallback((leftIdx, deltaCm, startWLeft, startWRight) => {
    const MIN_SEC = 30;
    const newLeft = Math.max(MIN_SEC, Math.min(startWLeft + startWRight - MIN_SEC, startWLeft + deltaCm));
    const newRight = startWLeft + startWRight - newLeft;
    setSections((prev) => prev.map((s, i) => {
      if (i === leftIdx) return { ...s, width_cm: Math.round(newLeft * 10) / 10 };
      if (i === leftIdx + 1) return { ...s, width_cm: Math.round(newRight * 10) / 10 };
      return s;
    }));
  }, []);

  /** Drag de la poignée de largeur/hauteur globale directement sur le schéma (bord droit /
   *  bord bas du meuble). Même principe que la séparation de section : le delta cumulé depuis
   *  le début du drag est appliqué à la valeur de départ (idempotent, pas de dérive). */
  const handleResizeTotal = useCallback((axis, deltaCm, startValueCm) => {
    if (axis === "width") {
      const minCm = 30;
      const maxCm = 270;
      const next = Math.round(Math.max(minCm, Math.min(maxCm, startValueCm + deltaCm)));
      setWidth(next);
      setSections((prev) => relabelSections(normalizeSectionWidths(prev, next)));
    } else {
      const minCm = 30;
      const maxCm = 270;
      const next = Math.round(Math.max(minCm, Math.min(maxCm, startValueCm + deltaCm)));
      setHeight(next);
    }
  }, []);


  /** Saisie numérique précise (mm) : redistribue le delta sur la section suivante (ou précédente si dernière). */
  /** Édite width_cm d'une section en préservant la largeur totale.
   *  Règles strictes :
   *   - La section ciblée reste ≥ 200 mm (SECTION_MIN_CM × 10).
   *   - Le "reste" à répartir sur les autres sections ne peut pas rendre l'une d'elles < 200 mm.
   *   - Compensation prise sur la section immédiatement adjacente (idx+1 sinon idx-1).
   *   - Si aucune compensation valide possible → aucun changement + toast.
   */
  const setSectionWidthMm = useCallback((idx, mm) => {
    setSections((prev) => {
      const MIN_MM = SECTION_MIN_CM * 10; // 200 mm
      const totalMm = prev.reduce((s, x) => s + (x.width_cm || 0) * 10, 0);
      // Borne haute absolue : totalMm - MIN_MM × (autres)
      const maxAllowedMm = totalMm - MIN_MM * (prev.length - 1);
      if (maxAllowedMm < MIN_MM) {
        // Layout impossible : total insuffisant pour respecter le min sur toutes les sections.
        return prev;
      }
      const clampedMm = Math.max(MIN_MM, Math.min(maxAllowedMm, Math.round(mm)));
      const clampedCm = clampedMm / 10;
      const compIdx = idx < prev.length - 1 ? idx + 1 : idx - 1;
      if (compIdx < 0 || compIdx >= prev.length) {
        // 1 seule section — width_cm = totalWidth exact
        return prev.map((s, i) => (i === idx ? { ...s, width_cm: clampedCm } : s));
      }
      const currentCm = prev[idx].width_cm || 0;
      const compCurrentCm = prev[compIdx].width_cm || 0;
      const deltaCm = clampedCm - currentCm;
      const newCompCm = compCurrentCm - deltaCm;
      if (newCompCm < SECTION_MIN_CM - 0.001) {
        toast.error("Largeur impossible", {
          description: `La section adjacente ne peut pas être plus petite que ${MIN_MM} mm.`,
        });
        return prev;
      }
      const clampedCompCm = Math.round(newCompCm * 10) / 10;
      return prev.map((s, i) => {
        if (i === idx) return { ...s, width_cm: clampedCm };
        if (i === compIdx) return { ...s, width_cm: clampedCompCm };
        return s;
      });
    });
  }, []);

  /** Décalage X global (cm) du bord gauche d'une section — la zone de sous-pente est toujours
   *  relative à la largeur TOTALE du meuble, jamais à une section isolée. */
  const getSectionLeftCm = (sectionIdx) => sections.slice(0, sectionIdx).reduce((sum, s) => sum + (s.width_cm || 0), 0);

  /** La sous-pente est désormais une option purement tarifaire (+40 %).
   *  Aucune zone d'exclusion géométrique n'est appliquée aux éléments. */
  const isUnderSlopeZone = () => false;

  // Garde-fou désactivé : la sous-pente est purement tarifaire, pas de suppression d'éléments.
  // (Ancienne boucle de suppression retirée pour éviter l'erreur React #185.)

  /** Drag & Drop libre — user relâche un pill sur une section à une hauteur y_mm exacte.
   *  Règles V3 :
   *   - butée basse : un tiroir ne peut descendre en-dessous de la plinthe/pieds (>= 100 mm du bas).
   *   - snapping : si un autre tiroir est à ±60 mm, on l'aimante contre lui (bloc vertical solidaire).
   */
  // Chantier 2.1 : aimantation verticale inter-sections. Si un aménagement (tablette,
  // penderie, tiroir) est posé/déplacé à une hauteur proche d'un élément d'une section
  // immédiatement voisine (gauche/droite), on aligne automatiquement sur cette hauteur —
  // purement visuel, n'affecte jamais la géométrie des sous-pentes/fileurs.
  const CROSS_SECTION_SNAP_MM = 15;
  const snapToNeighborHeight = (sectionIdx, yMm) => {
    const neighborIdxs = [sectionIdx - 1, sectionIdx + 1].filter((i) => i >= 0 && i < sections.length);
    let bestY = null;
    let bestDist = CROSS_SECTION_SNAP_MM;
    for (const ni of neighborIdxs) {
      for (const el of (sections[ni]?.elements || [])) {
        const d = Math.abs(el.y_mm - yMm);
        if (d <= bestDist) { bestDist = d; bestY = el.y_mm; }
      }
    }
    return bestY;
  };

  const handleDropElement = (idx, yMm, kind, xFrac = 0.5) => {
    // Par défaut, chaque élément reste dans sa propre travée. Le passage traversant
    // est une décision volontaire via l'action « Étirer / Fusionner » du panneau section.
    const ownerIdx = idx;
    const current = sections[ownerIdx];
    if (!current) return;
    if (kind === "door") {
      placeDoorOnSection(ownerIdx);
      return;
    }
    if (kind === "section") {
      const currentWidth = current.width_cm || width / Math.max(1, sections.length);
      const leftWidth = Math.round(currentWidth * Math.min(0.7, Math.max(0.3, xFrac)) * 2) / 2;
      const rightWidth = Math.round((currentWidth - leftWidth) * 2) / 2;
      if (leftWidth < 30 || rightWidth < 30) return toast.error("Chaque section doit mesurer au moins 300 mm");
      dismissedHandleSections.current.clear();
      setSections((previous) => relabelSections([...previous.slice(0, ownerIdx), { ...current, width_cm: leftWidth, elements: [] }, { ...current, width_cm: rightWidth, elements: [] }, ...previous.slice(ownerIdx + 1)]));
      setActiveElementTool("sections");
      toast.success("Section posée");
      return;
    }
    let finalYMm = yMm;
    if (kind === "drawer") {
      // Butée basse : min 100 mm (hauteur pieds/plinthe = 100 mm)
      finalYMm = Math.max(100, yMm);
      // Aimantation : cherche un tiroir voisin dans les 60 mm et snap contre lui (façade 150 mm typique)
      const drawerFaceMm = 150;
      const snapRangeMm = 60;
      const otherDrawers = (current.elements || []).filter((e) => e.kind === "drawer");
      for (const d of otherDrawers) {
        const distAbove = Math.abs((d.y_mm + drawerFaceMm) - finalYMm);
        const distBelow = Math.abs((d.y_mm - drawerFaceMm) - finalYMm);
        if (distAbove <= snapRangeMm) { finalYMm = d.y_mm + drawerFaceMm; break; }
        if (distBelow <= snapRangeMm) { finalYMm = d.y_mm - drawerFaceMm; break; }
      }
      // Butée haute : ne peut pas dépasser le plafond
      finalYMm = Math.min(height * 10 - drawerFaceMm, finalYMm);
    }
    const crossSnap = snapToNeighborHeight(ownerIdx, finalYMm);
    if (crossSnap !== null) {
      finalYMm = kind === "drawer" ? Math.min(height * 10 - 150, Math.max(100, crossSnap)) : crossSnap;
    }
    if (isUnderSlopeZone(ownerIdx, finalYMm)) {
      toast.error("Pose impossible sous la sous-pente", { description: "Aucun élément ne peut être posé directement sous la zone de toit incliné." });
      return;
    }
    const newElement = {
      id: `${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      kind,
      y_mm: finalYMm,
      span_sections: 1,
      width_mm: Math.round((current.width_cm || width / Math.max(1, sections.length)) * 10),
      depth_mm: Math.min(650, Math.round(depth * 10)),
      height_mm: kind === "drawer" ? 150 : kind.startsWith("shelf") ? 19 : 30,
    };
    const nextElements = [...(current.elements || []), newElement];
    // Tablette automatique au-dessus du tiroir — ferme l'espace et offre une surface de rangement
    if (kind === "drawer") {
      const TOLERANCE_MM = 40;
      const drawerTop = finalYMm + (newElement.height_mm || 150);
      // Ajouter une tablette au-dessus du nouveau tiroir (sera nettoyée si sandwich)
      const alreadyHasShelfOrDrawer = nextElements.some(
        (e) => ((e.kind === "shelf_fixed" || e.kind === "shelf_mobile") && Math.abs(e.y_mm - drawerTop) < TOLERANCE_MM)
          || (e.kind === "drawer" && e.id !== newElement.id && Math.abs(e.y_mm - drawerTop) < TOLERANCE_MM)
      );
      if (!alreadyHasShelfOrDrawer && drawerTop < height * 10 - 50) {
        nextElements.push({
          id: `shelf_fixed-auto-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          kind: "shelf_fixed",
          y_mm: drawerTop,
          span_sections: newElement.span_sections || 1,
          width_mm: newElement.width_mm,
          depth_mm: newElement.depth_mm,
          height_mm: 19,
        });
      }
      // Nettoyage global : retirer TOUTE tablette prise en sandwich entre deux tiroirs adjacents
      const drawers = nextElements.filter((e) => e.kind === "drawer");
      for (let i = nextElements.length - 1; i >= 0; i--) {
        const shelf = nextElements[i];
        if (shelf.kind !== "shelf_fixed" && shelf.kind !== "shelf_mobile") continue;
        const hasDrawerBelow = drawers.some((d) => Math.abs((d.y_mm + (d.height_mm || 150)) - shelf.y_mm) < TOLERANCE_MM);
        const hasDrawerAbove = drawers.some((d) => Math.abs(d.y_mm - shelf.y_mm) < TOLERANCE_MM);
        if (hasDrawerBelow && hasDrawerAbove) nextElements.splice(i, 1);
      }
    }
    updateSection(ownerIdx, { ...current, elements: nextElements });
    if (kind === "drawer") {
      maybeOpenHandlePopup(ownerIdx);
    }
    // Les tiroirs passent devant les portes : le visuel 2D découpe automatiquement la façade
    // en parties haute/basse autour de leur axe, sans masquer la porte ni ses repères atelier.
    const label = { drawer: "Tiroir", penderie: "Barre penderie", shelf_fixed: "Tablette fixe", shelf_mobile: "Tablette mobile" }[kind] || kind;
    const snapNote = finalYMm !== yMm ? " (aimanté)" : "";
    const isTraversing = newElement.span_sections === 2;
    toast.success(
      isTraversing ? "Tiroir traversant créé" : `${label} placé à H : ${newElement.y_mm} mm — Section ${String.fromCharCode(65 + ownerIdx)}${snapNote}`,
      { description: isTraversing ? `Façade pleine largeur sur les sections ${String.fromCharCode(65 + ownerIdx)} et ${String.fromCharCode(66 + ownerIdx)}.` : undefined },
    );
  };

  // Pose dédiée aux portes : une façade n'a ni hauteur de dépôt ni état transitoire.
  // L'écriture fonctionnelle est le seul chemin utilisé par clic, tap et drop.
  const placeDoorOnSection = (sectionIdx) => {
    const section = sections[sectionIdx];
    if (!section) return;
    if (section.has_door) {
      setActiveMode(null);
      toast.info("Porte déjà posée", { description: "Utilisez le bouton … pour choisir son ouverture." });
      return;
    }
    const isDouble = requiresDoubleDoor(section.width_cm, width / Math.max(1, sections.length));
    // Une façade battante réellement posée remplace la simple préparation pour
    // portes coulissantes. Sans cela, le drop est bien enregistré mais son rendu
    // est volontairement masqué par le mode « coulissant », ce qui donne l'impression
    // que la porte disparaît.
    if (options.prepared_for_coulissant) {
      setOptions((previous) => ({ ...previous, prepared_for_coulissant: false }));
    }
    setSections((previous) => previous.map((current, index) => {
      if (index !== sectionIdx || current.has_door) return current;
      return {
        ...current,
        has_door: true,
        door_standard: requiresDoubleDoor(current.width_cm, width / Math.max(1, previous.length)) ? "double" : (current.door_standard || "single_right"),
      };
    }));
    setActiveMode(null);
    toast.success(isDouble ? "Double porte posée" : "Porte posée", options.prepared_for_coulissant ? { description: "Mode battant activé pour afficher la façade." } : undefined);
    maybeOpenHandlePopup(sectionIdx);
  };

  /** Ouvre le pop-up de choix de poignée après l'ajout d'une porte ou d'un tiroir, sauf si
   * cette section a déjà une poignée dédiée choisie. */
  const maybeOpenHandlePopup = (sectionIdx) => {
    const section = sections[sectionIdx];
    if (section?.handle_id || dismissedHandleSections.current.has(sectionIdx)) return;
    setHandlePopup({ sectionIdx, choice: section?.handle_id || defaultHandleId, doorMirror: !!section?.door_mirror });
  };

  /** Demi-hauteur logique d'un élément (mm), utilisée pour tous les calculs de bornes/collision. */
  const halfSpanMm = (kind, heightMm) => (kind === "drawer" ? (heightMm || 150) / 2 : 15);

  /**
   * Trouve la position Y libre la plus proche de `desiredY` dans une liste d'éléments
   * (hors `excludeId`), en respectant les bornes [0, maxY] ajustées à la demi-hauteur de
   * l'élément déplacé — c'est la règle des "murs" : un élément ne peut jamais dépasser le
   * sol ou le plafond du caisson, même partiellement (son bord, pas seulement son centre).
   * Retourne null si aucune position libre n'existe.
   */
  const findNearestFreeY = (elementsList, kind, heightMm, desiredY, maxY, excludeId = null) => {
    const half = halfSpanMm(kind, heightMm);
    const list = elementsList.filter((e) => e.id !== excludeId);
    const gapWith = (o) => {
      if (kind === "drawer" && o.kind === "drawer") return half + (o.height_mm || 150) / 2;
      return Math.max(half, halfSpanMm(o.kind, o.height_mm));
    };
    const collides = (y) => list.some((o) => Math.abs(o.y_mm - y) < gapWith(o));
    let target = Math.max(half, Math.min(maxY - half, Math.round(desiredY)));
    if (!collides(target)) return target;
    let best = null;
    let bestDist = Infinity;
    for (const o of list) {
      const g = gapWith(o);
      for (const cand of [o.y_mm + g, o.y_mm - g]) {
        const clamped = Math.max(half, Math.min(maxY - half, cand));
        if (clamped !== cand) continue; // dépasserait le sol/plafond — ignoré plutôt que tronqué
        if (collides(clamped)) continue;
        const d = Math.abs(clamped - target);
        if (d < bestDist) { bestDist = d; best = clamped; }
      }
    }
    return best;
  };

  /**
   * Repositionne un élément existant à la hauteur souhaitée (mm), éventuellement dans une
   * autre section (glisser-déposer inter-sections, Chantier 3). `targetSectionIdx` par
   * défaut = `sectionIdx` (déplacement vertical classique dans la même travée).
   * Gestion de collision : min-gap dépendant du kind (drawer = sa propre hauteur, penderie/
   * tablette = 30mm). Si la cible entre en collision, snap au voisin le plus proche encore
   * libre. Si aucune position n'est libre, on refuse le déplacement (toast).
   * Les bornes [0, hauteur caisson] sont appliquées au BORD de l'élément (pas son centre) :
   * un tiroir redimensionné ne peut plus jamais dépasser le sol ou le plafond ("murs").
   *
   * NB : les éléments locked (tablette structurelle) restent déplaçables via le
   * drag SVG dans le meuble — la liste "Éléments placés" ne leur affiche pas
   * de contrôles (input/flèches) donc pas de risque de mauvaise manip.
   */
  const handleMoveElement = (sectionIdx, elId, newYMm, targetSectionIdx = sectionIdx) => {
    const current = sections[sectionIdx];
    if (!current) return;
    const el = (current.elements || []).find((e) => e.id === elId);
    if (!el) return;
    const maxY = Math.round(height * 10);
    const half = halfSpanMm(el.kind, el.height_mm);

    // Glisser-déposer vers une autre section (Chantier 3.2) — même élément, nouvelle travée.
    if (targetSectionIdx !== sectionIdx) {
      const toSection = sections[targetSectionIdx];
      if (!toSection) return;
      if (el.locked) { toast.error("Élément structurel — impossible à déplacer"); return; }
      if ((el.span_sections || 1) > 1) {
        toast.error("Un tiroir traversant ne peut pas changer de section", { description: "Ramenez-le à une section avant de le déplacer." });
        return;
      }
      const toWidthMm = Math.round((toSection.width_cm || width / Math.max(1, sections.length)) * 10);
      const desiredY = Math.max(half, Math.min(maxY - half, Math.round(newYMm)));
      const freeY = findNearestFreeY(toSection.elements || [], el.kind, el.height_mm, desiredY, maxY);
      if (freeY === null) {
        toast.error("Aucune place libre dans cette section", { description: "Essayez une autre section ou retirez un élément existant.", id: `cross-section-${elId}` });
        return;
      }
      const movedElement = { ...el, y_mm: freeY, width_mm: Math.min(el.width_mm || toWidthMm, toWidthMm) };
      setSections((previous) => previous.map((section, index) => {
        if (index === sectionIdx) return { ...section, elements: (section.elements || []).filter((e) => e.id !== elId) };
        if (index === targetSectionIdx) return { ...section, elements: [...(section.elements || []), movedElement] };
        return section;
      }));
      setSelectedPlacedElement({ sectionIdx: targetSectionIdx, elId });
      const label = { drawer: "Tiroir", penderie: "Penderie", shelf_fixed: "Tablette fixe", shelf_mobile: "Tablette mobile" }[el.kind] || el.kind;
      toast.success(`${label} déplacé vers la section ${String.fromCharCode(65 + targetSectionIdx)}`, { id: `cross-section-${elId}` });
      return;
    }

    // Déplacement vertical classique, même section.
    let target = Math.max(half, Math.min(maxY - half, Math.round(newYMm)));
    const crossSnap = snapToNeighborHeight(sectionIdx, target);
    if (crossSnap !== null) target = Math.max(half, Math.min(maxY - half, crossSnap));
    if (isUnderSlopeZone(sectionIdx, target)) {
      toast.error("Déplacement impossible sous la sous-pente", { description: "Aucun élément ne peut rester directement sous la zone de toit incliné." });
      return;
    }
    const freeY = findNearestFreeY(current.elements || [], el.kind, el.height_mm, target, maxY, elId);
    if (freeY === null) {
      toast.info("Bloqué par les éléments voisins, aucune place libre ici", { id: `collision-${elId}`, duration: 1600 });
      return;
    }
    if (freeY !== target) {
      toast.info(`Butée contre un élément voisin ou le caisson — arrêté à ${freeY} mm`, { id: `collision-${elId}`, duration: 1600 });
    }
    updateSection(sectionIdx, {
      ...current,
      elements: (current.elements || []).map((e) => (e.id === elId ? { ...e, y_mm: freeY } : e)),
    });
  };

  /** Duplique un élément posé (tiroir/penderie/tablette) juste à côté de l'original, dans la
   * même section, à la première position libre trouvée (en dessous, sinon au-dessus). */
  const handleDuplicateElement = (sectionIdx, elId) => {
    const current = sections[sectionIdx];
    if (!current) return;
    const el = (current.elements || []).find((e) => e.id === elId);
    if (!el) return;
    if (el.locked) { toast.error("Élément structurel — impossible à dupliquer"); return; }
    if ((el.span_sections || 1) > 1) {
      toast.error("Un tiroir traversant ne peut pas être dupliqué directement", { description: "Ramenez-le à une section avant de le dupliquer." });
      return;
    }
    const maxY = Math.round(height * 10);
    const half = halfSpanMm(el.kind, el.height_mm);
    const gap = 2 * half + 10;
    const freeY = findNearestFreeY(current.elements || [], el.kind, el.height_mm, el.y_mm - gap, maxY)
      ?? findNearestFreeY(current.elements || [], el.kind, el.height_mm, el.y_mm + gap, maxY);
    if (freeY === null || freeY === undefined) {
      toast.error("Aucune place libre pour dupliquer ici", { description: "Déplacez ou supprimez un élément voisin avant de dupliquer." });
      return;
    }
    const newEl = { ...el, id: `${el.kind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, y_mm: freeY };
    updateSection(sectionIdx, { ...current, elements: [...(current.elements || []), newEl] });
    setSelectedPlacedElement({ sectionIdx, elId: newEl.id });
    const label = { drawer: "Tiroir", penderie: "Penderie", shelf_fixed: "Tablette fixe", shelf_mobile: "Tablette mobile" }[el.kind] || el.kind;
    toast.success(`${label} dupliqué`, { description: `Nouvelle copie posée à H : ${freeY} mm.` });
  };

  /**
   * Chantier 2.2 : redimensionne la façade d'un tiroir en tirant son bord haut ou bas.
   * Le bord opposé reste fixe. Peut couvrir tout le caisson (sol au plafond) et se coller
   * parfaitement (écart 0) contre un élément voisin de la même section, mais jamais le
   * chevaucher. `targetEdgeYMm` est la position visée (mm) du bord déplacé.
   */
  const handleResizeElement = (sectionIdx, elId, edge, targetEdgeYMm) => {
    const current = sections[sectionIdx];
    if (!current) return;
    const el = (current.elements || []).find((e) => e.id === elId);
    if (!el || el.kind !== "drawer") return;
    const maxYMm = Math.round(height * 10);
    const curHalf = (el.height_mm || 150) / 2;
    const fixedEdgeYMm = edge === "bottom" ? el.y_mm + curHalf : el.y_mm - curHalf;
    let target = Math.max(0, Math.min(maxYMm, Math.round(targetEdgeYMm)));
    const halfSpanOf = (o) => o.kind === "drawer" ? (o.height_mm || 150) / 2 : (({ penderie: 40, shelf_fixed: 30, shelf_mobile: 30 }[o.kind]) || 30) / 2;
    const others = (current.elements || []).filter((e) => e.id !== elId);
    for (const o of others) {
      const oHalf = halfSpanOf(o);
      if (edge === "bottom") {
        if (o.y_mm < fixedEdgeYMm && (o.y_mm + oHalf) > target) target = o.y_mm + oHalf;
      } else {
        if (o.y_mm > fixedEdgeYMm && (o.y_mm - oHalf) < target) target = o.y_mm - oHalf;
      }
    }
    let newHeight = Math.round(Math.abs(fixedEdgeYMm - target));
    // Façade de tiroir strictement bornée entre 150 et 300 mm (règle atelier impérative) —
    // le drag s'arrête à la butée plutôt que d'être refusé, pour un geste fluide.
    newHeight = Math.max(150, Math.min(300, newHeight));
    target = edge === "bottom" ? fixedEdgeYMm - newHeight : fixedEdgeYMm + newHeight;
    const newCenter = Math.round((fixedEdgeYMm + target) / 2);
    updateSection(sectionIdx, {
      ...current,
      elements: current.elements.map((e) => (e.id === elId ? { ...e, height_mm: newHeight, y_mm: newCenter } : e)),
    });
  };

  const updatePlacedElementDimensions = (sectionIdx, elId, patch) => {
    setSections((previous) => previous.map((section, index) => index !== sectionIdx ? section : { ...section, elements: (section.elements || []).map((element) => element.id === elId ? { ...element, ...patch } : element) }));
  };

  /** Retirer un élément librement placé — bloque la suppression des éléments verrouillés. */
  const handleRemoveElement = (sectionIdx, elId) => {
    const current = sections[sectionIdx];
    if (!current) return;
    const target = (current.elements || []).find((e) => e.id === elId);
    if (target?.locked) {
      toast.error("Élément structurel — impossible à retirer", { description: "Cette tablette assure la rigidité du meuble (règle atelier au-delà de 1210 mm de hauteur)." });
      return;
    }
    updateSection(sectionIdx, {
      ...current,
      elements: (current.elements || []).filter((e) => e.id !== elId),
    });
  };

  // Chantier 8 : le miroir est un accessoire à taille libre (défaut = pleine section) —
  // seul élément dont on permet de préciser directement largeur/hauteur en cm.
  const handleResizeMirror = (sectionIdx, elId, patch) => {
    const current = sections[sectionIdx];
    if (!current) return;
    updateSection(sectionIdx, {
      ...current,
      elements: (current.elements || []).map((e) => (e.id === elId ? { ...e, ...patch } : e)),
    });
  };

  const handleRemoveDoor = (sectionIdx) => {
    const current = sections[sectionIdx];
    if (!current) return;
    updateSection(sectionIdx, { ...current, has_door: false, door_mode: "none", door_parts: {}, door_coverage: "full", door_height_mm: null });
    toast.success(`Porte retirée de la section ${String.fromCharCode(65 + sectionIdx)}`);
  };

  const handleRemoveDoorSegment = (sectionIdx, topMm, segmentHeightMm) => {
    const current = sections[sectionIdx];
    if (!current) return;
    const totalMm = height * 10;
    const aboveMm = Math.max(0, Math.round(topMm));
    const belowMm = Math.max(0, Math.round(totalMm - topMm - segmentHeightMm));
    if (aboveMm < 200 && belowMm < 200) {
      handleRemoveDoor(sectionIdx);
      return;
    }
    const preserveBottom = belowMm >= aboveMm;
    const nextHeight = Math.max(200, preserveBottom ? belowMm : aboveMm);
    updateSection(sectionIdx, {
      ...current,
      door_coverage: preserveBottom ? "bottom" : "top",
      door_height_mm: nextHeight,
    });
    toast.success("Partie de porte retirée", { description: preserveBottom ? "La porte basse est conservée." : "La porte haute est conservée." });
  };

  /** Menu d'options d'ouverture de porte — remplace l'ancien cycle "à l'aveugle" par des choix
   *  explicites : sens d'ouverture (simple) ou côté du vantail maître (double). */
  const [doorMenu, setDoorMenu] = useState(null); // { sectionIdx, x, y } | null
  const [handlePopup, setHandlePopup] = useState(null); // { sectionIdx, choice } | null
  const dismissedHandleSections = useRef(new Set());
  // Chantier 2.5 : fermeture manuelle du bandeau "Sous réserve de validation" (>800mm).
  // Ne modifie jamais la condition d'affichage elle-même, seulement sa fermeture par l'utilisateur.
  const [wideSectionAlertDismissed, setWideSectionAlertDismissed] = useState(false);

  const handleSetDoorOrientation = (sectionIdx, patch) => {
    setSections((previous) => previous.map((sec, index) => {
      if (index !== sectionIdx || !sec.has_door) return sec;
      const standard = patch.door_standard || sec.door_standard;
      const parts = Object.fromEntries(Object.entries(sec.door_parts || {}).map(([key, part]) => {
        if (key === "full") { const { standard: _ignored, ...rest } = part; return [key, rest]; }
        return [key, { ...part, standard }];
      }));
      return {
        ...sec,
        ...patch,
        door_standard: standard,
        door_last_single_standard: (standard === "single_left" || standard === "single_right") ? standard : sec.door_last_single_standard,
        door_parts: parts,
      };
    }));
    setDoorMenu(null);
  };

  // Le côté de charnière (G/D) choisi pour une porte simple reste mémorisé (`door_last_single_standard`)
  // même après un passage en porte double imposé par la largeur, pour être restitué au retour en simple.
  const setFacadeMode = (sectionIdx, mode, requestedStandard = null) => {
    if (mode !== "none" && options.prepared_for_coulissant) setOptions((previous) => ({ ...previous, prepared_for_coulissant: false }));
    const hadDoorBefore = !!sections[sectionIdx]?.has_door;
    setSections((previous) => previous.map((section, index) => {
      if (index !== sectionIdx) return section;
      if (mode === "none") return { ...section, has_door: false, door_mode: "none", door_parts: {} };
      const resolvedMode = mode === "single" && section.width_cm * 10 > 600 ? "double" : mode;
      const standard = resolvedMode === "double" ? "double" : (requestedStandard || section.door_last_single_standard || "single_right");
      const { standard: _ignored, ...fullRest } = section.door_parts?.full || {};
      return {
        ...section,
        has_door: true,
        door_mode: resolvedMode,
        door_standard: standard,
        door_last_single_standard: standard === "double" ? section.door_last_single_standard : standard,
        door_parts: { ...(section.door_parts || {}), full: { ...fullRest, enabled: true } },
      };
    }));
    if (mode !== "none" && !hadDoorBefore) {
      maybeOpenHandlePopup(sectionIdx);
    }
  };

  const setFacadePart = (sectionIdx, part, patch) => {
    setSections((previous) => previous.map((section, index) => {
      if (index !== sectionIdx) return section;
      // La façade pleine n'a qu'une seule source de vérité : `door_standard`.
      if (part === "full" && patch.standard) {
        const { standard, ...rest } = patch;
        const { standard: _ignored, ...fullRest } = section.door_parts?.full || {};
        return {
          ...section,
          door_standard: standard,
          door_last_single_standard: (standard === "single_left" || standard === "single_right") ? standard : section.door_last_single_standard,
          door_parts: { ...(section.door_parts || {}), full: { enabled: true, ...fullRest, ...rest } },
        };
      }
      return {
        ...section,
        door_parts: { ...(section.door_parts || {}), [part]: { enabled: true, standard: section.door_standard || "single_right", ...(section.door_parts || {})[part], ...patch } },
      };
    }));
  };
  const resizeFacadePart = (sectionIdx, part, height_mm) => setFacadePart(sectionIdx, part, { height_mm: Math.round(height_mm) });

  const sectionHasDrawerCut = (sectionIdx) => sections.some((owner, ownerIdx) => (owner.elements || []).some((element) => element.kind === "drawer" && ownerIdx <= sectionIdx && sectionIdx < ownerIdx + (element.span_sections || 1)));

  const removeSectionFromPlan = (sectionIdx) => {
    // Règle des séparations > 800 mm : on autorise désormais la suppression même si la
    // section résultante dépasse 800 mm de large (stabilité atelier). On marque alors la
    // section fusionnée (manual_wide_section) pour empêcher le re-fractionnement automatique
    // de l'annuler aussitôt, et on prévient l'artisan via une alerte persistante obligatoire.
    // Chantier 2.5 fix : le calcul de fusion + sa "largeur" résultante sont faits de façon
    // purement synchrone AVANT setSections (plutôt que mutée dans l'updater puis relue juste
    // après) pour éviter toute dépendance au moment réel d'exécution de l'updater React —
    // ce qui empêchait le bandeau de se réarmer sur une 2e fusion > 800 mm consécutive.
    if (sections.length <= 1) return;
    dismissedHandleSections.current.clear();
    const removed = sections[sectionIdx];
    const rest = sections.filter((_, index) => index !== sectionIdx);
    const hasInvalidWidth = rest.some((section) => !Number.isFinite(section.width_cm) || section.width_cm <= 0);
    let merged;
    if (hasInvalidWidth) {
      merged = relabelSections(equalSections(rest.length, height, width));
    } else {
      const targetIdx = sectionIdx > 0 ? sectionIdx - 1 : 0;
      const transferredWidth = Math.max(0, Number(removed.width_cm) || 0);
      merged = relabelSections(rest.map((section, index) => index === targetIdx ? { ...section, width_cm: Math.max(30, section.width_cm + transferredWidth) } : section));
    }
    let resultingWideMerge = false;
    if (merged.length === 1 && (merged[0].width_cm || 0) * 10 > 800) {
      resultingWideMerge = true;
      merged[0] = { ...merged[0], manual_wide_section: true };
    }
    setSections(merged);
    setActiveElementTool("sections");
    if (resultingWideMerge) {
      setWideSectionAlertDismissed(false);
      toast.warning("Sous réserve de validation", { description: "Séparation supprimée malgré une largeur de caisson > 800 mm : à valider avant fabrication.", duration: 8000 });
    } else {
      toast.success("Section supprimée", { description: "Sa largeur est transférée à la section voisine." });
    }
  };

  const handleToggleTraversingDrawer = (sectionIdx, elId) => {
    if (sectionIdx >= sections.length - 1) return;
    setSections((previous) => previous.map((section, index) => {
      if (index !== sectionIdx) return section;
      return {
        ...section,
        elements: (section.elements || []).map((element) => element.id === elId
          ? { ...element, span_sections: (element.span_sections || 1) > 1 ? 1 : 2 }
          : element),
      };
    }));
    const drawer = (sections[sectionIdx]?.elements || []).find((element) => element.id === elId);
    const enabled = (drawer?.span_sections || 1) === 1;
    toast.success(enabled ? "Tiroir traversant activé" : "Tiroir ramené à une section", {
      description: enabled ? `Façade pleine largeur sur les sections ${String.fromCharCode(65 + sectionIdx)} et ${String.fromCharCode(66 + sectionIdx)}.` : undefined,
    });
  };

  const handleExtendTraversingDrawer = (sectionIdx, elId) => {
    setSections((previous) => previous.map((section, index) => {
      if (index !== sectionIdx) return section;
      const maxSpan = previous.length - sectionIdx;
      const updatedElements = (section.elements || []).map((element) => {
        if (element.id !== elId) return element;
        const newSpan = Math.min(maxSpan, (element.span_sections || 1) + 1);
        return { ...element, span_sections: newSpan };
      });
      // Tablette auto au-dessus du tiroir traversant
      const drawer = updatedElements.find((e) => e.id === elId);
      if (drawer) {
        const shelfYMm = drawer.y_mm + (drawer.height_mm || 150);
        const TOLERANCE_MM = 30;
        const alreadyHasShelf = updatedElements.some(
          (e) => (e.kind === "shelf_fixed" || e.kind === "shelf_mobile") && Math.abs(e.y_mm - shelfYMm) < TOLERANCE_MM
        );
        if (!alreadyHasShelf && shelfYMm < height * 10 - 50) {
          updatedElements.push({
            id: `shelf_fixed-auto-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            kind: "shelf_fixed",
            y_mm: shelfYMm,
            span_sections: drawer.span_sections,
            width_mm: drawer.width_mm,
            depth_mm: drawer.depth_mm,
            height_mm: 19,
          });
        }
      }
      return { ...section, elements: updatedElements };
    }));
  };

  const handleReduceTraversingDrawer = (sectionIdx, elId) => {
    setSections((previous) => previous.map((section, index) => {
      if (index !== sectionIdx) return section;
      return {
        ...section,
        elements: (section.elements || []).map((element) => element.id === elId
          ? { ...element, span_sections: Math.max(1, (element.span_sections || 1) - 1) }
          : element),
      };
    }));
    toast.success("Tiroir réduit d’une section", { description: "Vous pouvez revenir jusqu’à une seule colonne." });
  };

  /**
   * Click & Place — Porte (toggle par section) + tous les éléments Drag'n'Drop en mode tactile.
   * Sur mobile, l'utilisateur tape un pill de la palette (active un mode), puis tape sur une
   * section du meuble pour poser l'élément à la hauteur choisie.
   */
  const handleClickPlace = (idx, yFrac, xFrac = 0) => {
    const current = sections[idx];
    if (!current) return;
    if (activeMode === "section") {
      const currentWidth = current.width_cm || width / Math.max(1, sections.length);
      // Pas de 5 mm : précis pour l'atelier tout en restant stable avec un clic canvas.
      let leftWidth = Math.round(currentWidth * Math.min(0.95, Math.max(0.05, xFrac)) * 2) / 2;
      let rightWidth = Math.round((currentWidth - leftWidth) * 2) / 2;
      // Une zone SVG peut être recouverte par les éléments déjà posés : dans ce cas,
      // la séparation demandée reste possible au centre plutôt que d'être silencieusement perdue.
      if (leftWidth < 30 || rightWidth < 30) {
        leftWidth = Math.round((currentWidth / 2) * 10) / 10;
        rightWidth = Math.round((currentWidth - leftWidth) * 10) / 10;
      }
      if (leftWidth < 30 || rightWidth < 30) {
        toast.error("Position de séparation impossible", { description: "Chaque section doit conserver au moins 300 mm de largeur." });
        return;
      }
      const left = { ...current, width_cm: leftWidth, elements: [] };
      const right = { ...current, width_cm: rightWidth, elements: [] };
      setSections((previous) => relabelSections([...previous.slice(0, idx), left, right, ...previous.slice(idx + 1)]));
      setActiveElementTool("sections");
      toast.success("Section ajoutée", { description: `Montant positionné à ${Math.round(leftWidth * 10)} mm dans la section ${String.fromCharCode(65 + idx)}.` });
      return;
    }
    if (activeMode === "door") {
      placeDoorOnSection(idx);
      return;
    }
    // Placement tactile des éléments Drag'n'Drop (tiroir, penderie, tablettes).
    if (["drawer", "penderie", "shelf_fixed", "shelf_mobile"].includes(activeMode)) {
      const yMm = Math.round(height * 10 * (1 - Math.min(1, Math.max(0, yFrac))));
      handleDropElement(idx, yMm, activeMode, xFrac);
    }
  };

  const compressSketch = (file) => new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const scale = Math.min(1, 1400 / Math.max(image.width, image.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const context = canvas.getContext("2d");
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(objectUrl);
      resolve({ mime_type: "image/jpeg", data_url: canvas.toDataURL("image/jpeg", 0.72) });
    };
    image.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error("Image illisible")); };
    image.src = objectUrl;
  });

  const handleSlopePhoto = async (file) => {
    if (!file) return;
    if (!canUseUnderSlope) {
      toast.error("Option indisponible", { description: "La sous-pente est réservée aux dressings, caissons et bibliothèques." });
      return;
    }
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Format non pris en charge", { description: "Importez un JPEG, PNG ou WEBP." });
      return;
    }
    if (file.size < 1024 || file.size > 5 * 1024 * 1024) {
      toast.error("Image hors limites", { description: "Choisissez une image entre 1 Ko et 5 Mo." });
      return;
    }
    setSlopeAnalyzing(true);
    setSlopeImageName(file.name);
    setSlopeAnalysisNote("");
    setSlopeAnalysisStatus("loading");
    try {
      const encoded = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",")[1]);
        reader.onerror = () => reject(new Error("Lecture de l'image impossible"));
        reader.readAsDataURL(file);
      });
      const sketch = await compressSketch(file).catch(() => ({ mime_type: file.type, data_url: `data:${file.type};base64,${encoded}` }));
      setOptions((prev) => ({
        ...prev,
        customer_structure_sketch: { name: file.name, ...sketch },
      }));
      const analysis = await analyzeSlopeImage({ image_base64: encoded, mime_type: file.type });
      setSlopeAnalysisNote(analysis.note || "Analyse terminée — vérifiez les cotes avant devis.");
      if (!analysis.detected) {
        setSlopeAnalysisStatus("not-detected");
        toast.info("Sous-pente non confirmée", { description: analysis.note || "Ajoutez un croquis coté pour pré-remplir les valeurs." });
        return;
      }
      setOptions((prev) => ({
        ...prev,
        under_slope_enabled: true,
        under_slope_direction: analysis.direction || prev.under_slope_direction || "left",
        under_slope_low_height_mm: analysis.low_height_mm || prev.under_slope_low_height_mm,
        under_slope_bevel_width_mm: analysis.bevel_width_mm || prev.under_slope_bevel_width_mm,
      }));
      setSlopeAnalysisStatus("success");
      toast.success("Paramètres sous-pente pré-remplis", { description: "Vérifiez les cotes avant de générer le devis." });
    } catch (error) {
      setSlopeAnalysisStatus("error");
      toast.error("Analyse de l'image impossible", { description: formatApiError(error) });
    } finally {
      setSlopeAnalyzing(false);
    }
  };

  const currentConfigForSnapshot = () => ({
    universe,
    template_id: templateId,
    total_width: width,
    height, depth,
    sections: sectionsForPricing,
    carcass_finish_id: carcassFinishId,
    doors_finish_id: pricingDoorsFinishId,
    default_handle_id: defaultHandleId,
    quantity,
    custom_color_requested: customColorRequested,
    options,
  });

  const projectDraftPayload = (notify = false) => ({
    id: serverDraftId,
    config: currentConfigForSnapshot(),
    project_name: projectName,
    items: additionalItems.map((item) => ({ client_id: item.id, label: item.label, config: item.config })),
    assembly_item_client_id: linkedAdditionalItemId,
    assembly_module_order: assemblyModuleOrder,
    state: projectName.trim() ? "saved" : "started",
    notify,
  });

  const saveProjectDraft = async () => {
    if (!user || !universe) return;
    if (!projectName.trim()) {
      toast.error("Référence client / Chantier requise", { description: "Renseignez la référence en haut de la configuration avant d’enregistrer." });
      return;
    }
    setSavingProject(true);
    try {
      const saved = await saveQuoteDraft(projectDraftPayload(true));
      if (!serverDraftId) setServerDraftId(saved.id);
      toast.success("Projet enregistré", { description: "Votre configuration est disponible dans le suivi commercial." });
    } catch (error) {
      toast.error("Enregistrement du projet impossible", { description: formatApiError(error) });
    } finally {
      setSavingProject(false);
    }
  };

  useEffect(() => {
    if (!draftLoaded || !user || !universe || !catalog || !projectName.trim()) return;
    if (new URLSearchParams(window.location.search).get("edit") && !editingQuoteId) return;
    const timer = setTimeout(async () => {
      try {
        const saved = await saveQuoteDraft(projectDraftPayload(false));
        if (!serverDraftId) setServerDraftId(saved.id);
      } catch {
        // Le brouillon navigateur continue de protéger le travail en cas de réseau indisponible.
      }
    }, 2500);
    return () => clearTimeout(timer);
  }, [templateId, width, height, depth, sections, options, carcassFinishId, doorsFinishId, defaultHandleId, quantity, customColorRequested, additionalItems, linkedAdditionalItemId, projectName, universe, user, catalog, draftLoaded, serverDraftId]); // eslint-disable-line

  const addCurrentToProject = () => {
    if (!breakdown) return;
    const previousProjectItemId = additionalItems[0]?.id || null;
    const label = `Meuble ${additionalItems.length + 1}`;
    const snapshot = {
      id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      label,
      config: JSON.parse(JSON.stringify(currentConfigForSnapshot())),
      breakdown: JSON.parse(JSON.stringify(breakdown)),
    };
    flushSync(() => {
      setAdditionalItems((prev) => [...prev, snapshot]);
      if (previousProjectItemId) {
        setAssemblyCandidateId(previousProjectItemId);
        setAssemblyDialogOpen(true);
      }
    });
    // Repartir de zéro pour le nouveau meuble
    setWidth(100);
    setHeight(200);
    setDepth(50);
    setSections(relabelSections(equalSections(1, 200, 100)));
    setDoorsFinishId("none");
    setSelectedPlacedElement(null);
    setActiveElementTool(null);
    setOptions((prev) => ({ ...prev, under_slope_enabled: false, under_slope_rear_enabled: false, fileur_left: false, fileur_right: false, fileur_top: false, prepared_for_coulissant: false }));
    toast.success(`${label} ajouté au projet`, { description: "Le canvas est prêt pour configurer un nouveau meuble." });
  };

  const handleAddProjectActivation = (event) => {
    event.preventDefault();
    event.stopPropagation();
    addCurrentToProject();
  };

  const removeCurrentItem = () => {
    if (additionalItems.length === 0) return;
    // Charge le premier meuble de la liste comme nouveau "en cours" (sans sauvegarder le courant)
    const nextItem = additionalItems[0];
    setTemplateId(nextItem.config.template_id);
    setWidth(nextItem.config.total_width);
    setHeight(nextItem.config.height);
    setDepth(nextItem.config.depth);
    setSections(nextItem.config.sections);
    setCarcassFinishId(nextItem.config.carcass_finish_id);
    setDoorsFinishId(nextItem.config.doors_finish_id || nextItem.config.carcass_finish_id);
    setDefaultHandleId(nextItem.config.default_handle_id || "sdb_profil_alu");
    setQuantity(nextItem.config.quantity || 1);
    setCustomColorRequested(nextItem.config.custom_color_requested || false);
    setOptions(nextItem.config.options || {});
    // Retire nextItem de la liste (il est maintenant le meuble courant sur le canvas)
    setAdditionalItems((prev) => prev.filter((it) => it.id !== nextItem.id));
    // Si nextItem était le module lié, dissocier l'assemblage (il est maintenant le meuble principal)
    if (linkedAdditionalItemId === nextItem.id) setLinkedAdditionalItemId(null);
    setDeleteConfirmId(null);
    toast.success("Meuble retiré", { description: "Le meuble suivant a été chargé sur le plan." });
  };

  const removeAdditionalItem = (id) => {
    setAdditionalItems((prev) => prev.filter((it) => it.id !== id));
    if (linkedAdditionalItemId === id) { setLinkedAdditionalItemId(null); setAssemblyModuleOrder("a_left"); }
    setDeleteConfirmId(null);
  };

  const requestDeleteAdditionalItem = (id) => {
    setDeleteConfirmId(id);
  };

  const duplicateAdditionalItem = (item) => {
    const clone = {
      ...item,
      id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      label: `${item.label} (copie)`,
    };
    setAdditionalItems((prev) => [...prev, clone]);
    toast.success("Meuble dupliqué avec succès");
  };

  const editAdditionalItem = (item) => {
    const wasLinked = linkedAdditionalItemId === item.id;
    // Sauvegarde du meuble courant avant swap (même sans breakdown disponible)
    const currentSnapshot = {
      id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      label: `Meuble`,
      config: JSON.parse(JSON.stringify(currentConfigForSnapshot())),
      breakdown: breakdown ? JSON.parse(JSON.stringify(breakdown)) : null,
    };
    
    // Load item config
    setTemplateId(item.config.template_id);
    setWidth(item.config.total_width);
    setHeight(item.config.height);
    setDepth(item.config.depth);
    setSections(item.config.sections);
    setCarcassFinishId(item.config.carcass_finish_id);
    setDoorsFinishId(item.config.doors_finish_id || item.config.carcass_finish_id);
    setDefaultHandleId(item.config.default_handle_id || "sdb_profil_alu");
    setQuantity(item.config.quantity || 1);
    setCustomColorRequested(item.config.custom_color_requested || false);
    setOptions(item.config.options || {});
    
    // Swap : retire l'item édité, ajoute le snapshot courant
    setAdditionalItems((prev) => [...prev.filter((it) => it.id !== item.id), currentSnapshot]);
    // Si l'item édité était le module assemblé, on pointe le lien vers le snapshot
    // (il représente le meuble courant mis en attente — l'utilisateur pourra re-lier après édition)
    if (wasLinked) {
      setLinkedAdditionalItemId(currentSnapshot.id);
      toast.info(`Édition : Module assemblé`, { description: "Le meuble courant a été mis en attente. Le lien d'assemblage sera conservé — re-liez si besoin après édition." });
    } else {
      setLinkedAdditionalItemId((prev) => prev);
      toast.info(`Édition en cours`, { description: "Le meuble courant a été mis en attente. Modifiez, puis cliquez « Ajouter ce meuble au projet »." });
    }
  };

  const updateAdditionalLabel = (id, label) => {
    setAdditionalItems((prev) => prev.map((it) => (it.id === id ? { ...it, label } : it)));
  };

  // Détection : le meuble "courant" est un doublon exact du dernier meuble ajouté à la liste
  // (cas où le pro clique "Ajouter ce meuble au projet" sur son dernier meuble sans le modifier
  // ensuite — le canvas continue d'afficher la même config). Sans cette détection, ce même
  // meuble était compté et facturé deux fois (une fois comme "Meuble N" figé, une fois comme
  // meuble courant implicite).
  const isCurrentDuplicateOfLastAdded = additionalItems.length > 0 &&
    JSON.stringify(currentConfigForSnapshot()) === JSON.stringify(additionalItems[additionalItems.length - 1].config);

  const totalMeublesCount = additionalItems.length + (isCurrentDuplicateOfLastAdded ? 0 : 1);
  const linkedAdditionalItem = additionalItems.find((item) => item.id === linkedAdditionalItemId) || null;
  const assemblyChoices = isCurrentDuplicateOfLastAdded ? additionalItems.slice(0, -1) : additionalItems;

  const templateVariantForConfig = (config) => {
    if (config.options?.prepared_for_coulissant) return "coulissant";
    const itemTemplate = catalog?.templates?.find((entry) => entry.id === config.template_id);
    return itemTemplate?.variant || "battant";
  };

  const sdbDrawersForConfig = (config) => catalog?.templates?.find((entry) => entry.id === config.template_id)?.sdb_drawers || 0;

  const submitQuote = async () => {
    // B2B : si connecté, seul le nom du projet est requis. Sinon fallback formulaire client complet.
    if (user) {
      if (!projectName.trim()) {
        toast.error("Merci d'indiquer un nom de projet");
        return;
      }
    } else {
      if (!client.contact_name || !client.email) {
        toast.error("Nom et email du client requis");
        return;
      }
    }
    setSubmitting(true);
    try {
      // Si le meuble courant duplique le dernier ajouté, on l'utilise comme config primaire
      // et on retire ce doublon de la liste des meubles additionnels (évite le double comptage).
      const primaryConfig = isCurrentDuplicateOfLastAdded
        ? additionalItems[additionalItems.length - 1].config
        : {
            universe,
            template_id: templateId, total_width: width, height, depth, sections: sectionsForPricing,
            carcass_finish_id: carcassFinishId, doors_finish_id: pricingDoorsFinishId,
            default_handle_id: defaultHandleId, quantity,
            custom_color_requested: customColorRequested,
            options,
          };
      const extraItems = isCurrentDuplicateOfLastAdded ? additionalItems.slice(0, -1) : additionalItems;
      const assemblyItemClientId = linkedAdditionalItemId && extraItems.some((item) => item.id === linkedAdditionalItemId)
        ? linkedAdditionalItemId
        : null;
      const quote = await createQuote({
        config: primaryConfig,
        project_name: projectName.trim(),
        client,
        items: extraItems.map((it) => ({ client_id: it.id, label: it.label, config: it.config })),
        assembly_item_client_id: assemblyItemClientId,
        assembly_module_order: assemblyModuleOrder,
        apply_installation_fee: applyInstallationFee,
        apply_delivery: applyDelivery && deliveryEstimate && (deliveryEstimate.tier === "50km" || deliveryEstimate.tier === "100km"),
        delivery_postal_code: deliveryPostalCode.trim() || null,
        draft_id: serverDraftId,
        edit_quote_id: editingQuoteId,
      });
      toast.success(editingQuoteId ? "Devis mis à jour" : "Devis créé", { description: `Référence ${quote.reference}` });
      setModalOpen(false);
      // Génère le PDF avec capture du rendu 3D masqué (cf. bloc hors-écran domId="cabinet-3d-main"/"cabinet-3d-item-{idx}")
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      generateQuotePDF(quote, {
        canvasElement: document.querySelector("#cabinet-3d-main canvas"),
        itemCanvasElements: extraItems.map((_, idx) => document.querySelector(`#cabinet-3d-item-${idx} canvas`)),
        catalog,
        user,
      });
      localStorage.removeItem("aih-config-draft");
      if (embed) {
        window.parent?.postMessage({ type: "aih-quote-created", quote: { id: quote.id, reference: quote.reference, total: quote.breakdown.total_ht } }, "*");
      } else {
        navigate(`/devis/${quote.id}`);
      }
    } catch (e) {
      toast.error("Impossible de créer le devis", { description: formatApiError(e) });
    } finally {
      setSubmitting(false);
    }
  };

  if (!catalog) {
    return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  const finishesUnie = catalog.finishes.filter((f) => f.category === "unie");
  const finishesBois = catalog.finishes.filter((f) => f.category === "bois");
  const finishesSpecial = catalog.finishes.filter((f) => f.category === "special");

  // Nuancier filtré selon univers (règle V3 : SdB restreint à 4 décors)
  const finishesForUniverse = isSdb
    ? catalog.finishes.filter((f) => SDB_FINISH_IDS.includes(f.id))
    : catalog.finishes;
  const finishesUnieFiltered = isSdb ? finishesForUniverse.filter((f) => f.category === "unie") : finishesUnie;
  const finishesBoisFiltered = isSdb ? finishesForUniverse.filter((f) => f.category === "bois") : finishesBois;
  const finishesSpecialFiltered = isSdb ? finishesForUniverse.filter((f) => f.category === "special") : finishesSpecial;

  // ============ SÉLECTION DE L'UNIVERS (4 blocs autonomes) ============
  if (!universe) {
    return (
      <main data-testid="universe-picker" className="min-h-screen">
        <div className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 py-12 lg:py-20">
          <div className="text-center mb-12">
            <p className="eyebrow mb-3">Bienvenue</p>
            <h1 className="text-4xl sm:text-5xl mb-4">Quel univers souhaitez-vous configurer&nbsp;?</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto text-sm">
              Quatre configurateurs autonomes — dimensions et options adaptées à chaque univers.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { id: "dressing", label: "Dressing & Caissons", icon: Layers, desc: "Modulable au mm, drag & drop libre.", cta: "Dressing sur mesure" },
              { id: "salle_de_bain", label: "Salle de bain", icon: Home, desc: "Configurations dédiées, prof. 470 mm.", cta: "Meuble SdB" },
              { id: "cuisine", label: "Cuisine", icon: Layers, desc: "Caissons cuisine, prof. 560 mm.", cta: "Caisson cuisine" },
              { id: "autres", label: "Autres", icon: Layers, desc: "Bibliothèques, rangements de bureau.", cta: "Autre structure" },
            ].map((u) => {
              const Icon = u.icon;
              return (
                <button
                  key={u.id}
                  data-testid={`universe-${u.id}`}
                  onClick={() => {
                    chooseUniverse(u.id);
                    if (u.id === "salle_de_bain") {
                      setCategoryId("salle_de_bain");
                      setTemplateId("sdb-2tiroirs");
                      setCarcassFinishId("blanc_premium");
                      setDoorsFinishId("blanc_premium");
                      setDefaultHandleId("sdb_profil_alu");
                      setOptions((o) => ({ ...o, plinth: false, feet_visible: true, sdb_foot_ellipse_height: o.sdb_foot_ellipse_height || 100 }));
                    } else if (u.id === "cuisine") {
                      setCategoryId("cuisine");
                      setTemplateId("template-battant");
                      setDepth(56);
                    } else if (u.id === "autres") {
                      setCategoryId("autres");
                      setTemplateId("template-open");
                    } else {
                      setCategoryId("dressing");
                      setTemplateId("template-battant");
                    }
                  }}
                  className="group text-left rounded-2xl border-2 border-border bg-card p-6 hover:border-primary hover:shadow-xl transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <Icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                  <h2 className="font-heading text-xl mb-2">{u.label}</h2>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-4">{u.desc}</p>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary">
                    {u.cta} <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="configurator-page" className="min-h-screen">
      <div className="w-full min-w-0 px-4 sm:px-6 lg:px-10 2xl:px-16 py-6 lg:py-8">
        {!embed && user && (
          <WelcomeCard user={user} quotes={recentQuotes} />
        )}
        <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="eyebrow mb-2">Configurateur · {isSdb ? "Salle de Bain" : universe === "cuisine" ? "Cuisine" : universe === "autres" ? "Autres structures" : "Dressing & Caissons"}</p>
            <h1 className="text-3xl sm:text-4xl">Composez votre meuble</h1>
          </div>
          <button
            data-testid="change-universe"
            onClick={resetUniverse}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary text-primary-foreground font-heading text-sm font-bold border-2 border-primary hover:bg-accent hover:border-accent shadow-md transition-all animate-pulse-slow"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Changer d&apos;univers
          </button>
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground border-y border-border py-3">
          <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-primary" /> Fabriqué en France</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-primary" /> Devis instantané</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-primary" /> Tarification atelier réelle</span>
        </div>

        {/* Mobile : parcours vertical sans couche superposée. Desktop : visualiseur sticky à côté des contrôles. */}
        <div
          className="flex flex-col gap-6"
          style={{ alignItems: "flex-start", position: "relative" }}
        >
          {/* PLAN DOMINANT : les commandes essentielles sont regroupées dans la légende au-dessus. */}
          <div
            data-testid="configurator-left"
            className="order-1 w-full min-w-0 bg-background/95 pb-3 mb-4 border-b border-border"
          >
            {/* Salle de Bain : le produit n'est pas une question de "portes" mais un choix
                entre un meuble bas 1/2/3 tiroirs ou une colonne fixe à 2 portes (fiche produit). */}
            {isSdb && (
              <section data-testid="sdb-template-picker" className="paper mb-3 p-3 sm:p-4 border-t-4 border-t-primary">
                <p className="eyebrow text-[10px] mb-2">1. Type de meuble</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {catalog.templates.filter((t) => t.variant === "sdb" || t.variant === "sdb_column").map((t) => {
                    const active = templateId === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        data-testid={`sdb-template-${t.id}`}
                        onClick={() => setTemplateId(t.id)}
                        className={`text-left rounded-lg border p-3 transition-colors ${active ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-primary/50 bg-background"}`}
                      >
                        <span className="block text-sm font-bold">{t.name.replace("SDB — ", "")}</span>
                        <span className="block text-[10px] text-muted-foreground mt-0.5 leading-snug">{t.description}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}
            {isSdb && templateId !== "sdb-colonne-2portes" && (
              <section data-testid="sdb-panel" className="paper mb-3 p-3 sm:p-4 border-t-4 border-t-primary space-y-4">
                <p className="eyebrow text-[10px] mb-1">1b. Options Salle de Bain</p>

                <div>
                  <p className="text-[11px] font-bold mb-2">Finition supérieure</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: "sans_vasque", label: "Sans vasque", hint: "Tiroirs seuls, pas de plan supérieur." },
                      { id: "avec_dessus", label: "Avec dessus", hint: "Plan de toilette plat, même finition que le caisson." },
                      { id: "avec_vasque", label: "Avec vasque", hint: "Vasque résine encastrée, moulée dans le plan." },
                    ].map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        data-testid={`sdb-top-${r.id}`}
                        onClick={() => setOptions({ ...options, sdb_top_finish: r.id })}
                        className={`px-3 py-2.5 rounded-md text-left border transition-colors ${(options.sdb_top_finish || "sans_vasque") === r.id ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}
                      >
                        <span className="block text-xs font-medium">{r.label}</span>
                        <span className={`block text-[10px] mt-0.5 leading-snug ${(options.sdb_top_finish || "sans_vasque") === r.id ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                          {r.hint}
                        </span>
                      </button>
                    ))}
                  </div>
                  {options.sdb_top_finish === "avec_vasque" && (
                    <>
                      <p data-testid="sdb-vasque-resine-note" className="text-[10px] text-muted-foreground mt-1.5 flex items-start gap-1.5">
                        <Info className="w-3 h-3 mt-0.5 shrink-0" />
                        La vasque résine est encastrée dans le meuble : le plan supérieur est nécessairement en résine (moulage intégré), quelle que soit la finition du caisson choisie.
                      </p>
                      <div className="mt-2.5">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid="sdb-double-vasque"
                            checked={!!options.sdb_double_vasque}
                            disabled={width * 10 < 1200}
                            onChange={(e) => setOptions({ ...options, sdb_double_vasque: e.target.checked })}
                            className="w-4 h-4 accent-primary"
                          />
                          <span className="text-[11px] font-bold">Double vasque <span className="font-normal text-muted-foreground">(option gratuite, à partir de 1200 mm)</span></span>
                        </label>
                        {width * 10 < 1200 && !options.sdb_double_vasque && (
                          <p className="text-[10px] text-muted-foreground mt-1">Disponible dès que la largeur atteint 1200 mm (actuellement {Math.round(width * 10)} mm).</p>
                        )}
                      </div>
                      {!options.sdb_double_vasque && (
                        <div className="mt-2.5">
                          <p className="text-[11px] font-bold mb-1.5">Position de la vasque <span className="font-normal text-muted-foreground">(option gratuite)</span></p>
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              { id: "gauche", label: "Gauche" },
                              { id: "centre", label: "Centre" },
                              { id: "droite", label: "Droite" },
                            ].map((p) => (
                              <button
                                key={p.id}
                                type="button"
                                data-testid={`sdb-vasque-position-${p.id}`}
                                onClick={() => setOptions({ ...options, sdb_vasque_position: p.id })}
                                className={`px-3 py-2 rounded-md text-xs font-medium border transition-colors ${(options.sdb_vasque_position || "centre") === p.id ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}
                              >
                                {p.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="sdb-credence"
                      checked={(options.sdb_credence_sides || []).length > 0}
                      onChange={(e) => setOptions({ ...options, sdb_credence_sides: e.target.checked ? ["fond"] : [] })}
                      className="w-4 h-4 accent-primary"
                    />
                    <span>Crédence H 100 mm</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="sdb-credence-soudee"
                      checked={(options.sdb_credence_soudee_sides || []).length > 0}
                      onChange={(e) => setOptions({ ...options, sdb_credence_soudee_sides: e.target.checked ? ["fond"] : [] })}
                      className="w-4 h-4 accent-primary"
                    />
                    <span>Crédence soudée</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="sdb-porte-serviette"
                      checked={(options.sdb_porte_serviette_qty || 0) > 0}
                      onChange={(e) => setOptions({ ...options, sdb_porte_serviette_qty: e.target.checked ? 1 : 0 })}
                      className="w-4 h-4 accent-primary"
                    />
                    <span>Porte-serviette intégré</span>
                  </label>
                </div>

                {(options.sdb_credence_sides || []).length > 0 && (
                  <div className="mt-2.5">
                    <p className="text-[11px] font-bold mb-1.5">Côtés de la crédence H 100 mm <span className="font-normal text-muted-foreground">(plusieurs côtés possibles)</span></p>
                    <div className="grid grid-cols-3 gap-2 max-w-[300px]">
                      {[
                        { id: "gauche", label: "Gauche" },
                        { id: "fond", label: "Fond" },
                        { id: "droite", label: "Droite" },
                      ].map((p) => {
                        const active = (options.sdb_credence_sides || []).includes(p.id);
                        return (
                          <button
                            key={p.id}
                            type="button"
                            data-testid={`sdb-credence-side-${p.id}`}
                            onClick={() => setOptions((previous) => {
                              const current = previous.sdb_credence_sides || [];
                              const next = active ? current.filter((s) => s !== p.id) : [...current, p.id];
                              return { ...previous, sdb_credence_sides: next.length > 0 ? next : ["fond"] };
                            })}
                            className={`px-3 py-2 rounded-md text-xs font-medium border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}
                          >
                            {p.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(options.sdb_credence_soudee_sides || []).length > 0 && (
                  <div className="mt-2.5">
                    <p className="text-[11px] font-bold mb-1.5">Côtés de la crédence soudée <span className="font-normal text-muted-foreground">(plusieurs côtés possibles)</span></p>
                    <div className="grid grid-cols-3 gap-2 max-w-[300px]">
                      {[
                        { id: "gauche", label: "Gauche" },
                        { id: "fond", label: "Fond" },
                        { id: "droite", label: "Droite" },
                      ].map((p) => {
                        const active = (options.sdb_credence_soudee_sides || []).includes(p.id);
                        return (
                          <button
                            key={p.id}
                            type="button"
                            data-testid={`sdb-credence-soudee-side-${p.id}`}
                            onClick={() => setOptions((previous) => {
                              const current = previous.sdb_credence_soudee_sides || [];
                              const next = active ? current.filter((s) => s !== p.id) : [...current, p.id];
                              return { ...previous, sdb_credence_soudee_sides: next.length > 0 ? next : ["fond"] };
                            })}
                            className={`px-3 py-2 rounded-md text-xs font-medium border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}
                          >
                            {p.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(options.sdb_porte_serviette_qty || 0) > 0 && (
                  <div className="mt-2.5">
                    <p className="text-[11px] font-bold mb-1.5">Côté du porte-serviette <span className="font-normal text-muted-foreground">(option gratuite)</span></p>
                    <div className="grid grid-cols-3 gap-2 max-w-[260px]">
                      {[
                        { id: "gauche", label: "Gauche" },
                        { id: "droite", label: "Droite" },
                        { id: "both", label: "Les deux" },
                      ].map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          data-testid={`sdb-porte-serviette-side-${p.id}`}
                          onClick={() => setOptions({ ...options, sdb_porte_serviette_side: p.id })}
                          className={`px-3 py-2 rounded-md text-xs font-medium border transition-colors ${(options.sdb_porte_serviette_side || "droite") === p.id ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-[11px] font-bold mb-2">Pieds elliptiques (paire)</p>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { h: null, label: "Aucun" },
                      { h: 100, label: "100 mm" },
                      { h: 140, label: "140 mm" },
                      { h: 200, label: "200 mm" },
                    ].map((r) => (
                      <button
                        key={String(r.h)}
                        type="button"
                        data-testid={`sdb-foot-${r.h || 'none'}`}
                        onClick={() => setOptions((prev) => ({ ...prev, sdb_foot_ellipse_height: r.h }))}
                        className={`px-2 py-1.5 rounded-md text-[10px] font-medium border transition-colors ${(options.sdb_foot_ellipse_height || null) === r.h ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>

              </section>
            )}
            {isSdb && templateId === "sdb-colonne-2portes" && (
              <section data-testid="sdb-panel" className="paper mb-3 p-3 sm:p-4 border-t-4 border-t-primary">
                <p className="text-xs text-muted-foreground">Colonne SdB standard 300×1400×200 mm, sans options supplémentaires.</p>
              </section>
            )}
            <section data-testid="schema-control-legend" className="paper mb-3 p-3 sm:p-4 border-t-4 border-t-primary">
              <div className="grid gap-3 xl:grid-cols-[3fr_2fr_2fr_2fr] xl:items-end">
                <div>
                  <Label htmlFor="project-reference" className="text-xs font-heading">1. Référence client / Chantier</Label>
                  <Input id="project-reference" data-testid="project-reference-input" value={projectName} onChange={(event) => setProjectName(event.target.value)} placeholder="Ex. DUPONT — Dressing chambre" className="mt-1 h-10 bg-background" />
                  {!projectName.trim() && <p data-testid="project-reference-required" className="mt-1 text-[10px] text-muted-foreground">Optionnel ici — vous le renseignerez juste avant de créer le devis.</p>}
                </div>
                <SchemaDimensionInput label="L" valueCm={width} minMm={isSdb ? (templateId === "sdb-colonne-2portes" ? 300 : SDB_MIN_WIDTH * 10) : 300} maxMm={isSdb ? (templateId === "sdb-colonne-2portes" ? 300 : SDB_MAX_WIDTH * 10) : 2700} onChangeCm={handleSetWidthCm} testId="schema-width" disabled={isSdb && templateId === "sdb-colonne-2portes"} />
                <div>{options.feet_visible !== false && <span data-testid="schema-height-feet-note" className="block text-[9px] text-muted-foreground mb-0.5 leading-tight text-center">+100mm si pieds</span>}<SchemaDimensionInput label="H" valueCm={height} minMm={300} maxMm={2700} onChangeCm={setHeight} testId="schema-height" disabled={isSdb} note="mm sans pieds" /></div>
                <SchemaDimensionInput label="P" valueCm={depth} minMm={300} maxMm={750} onChangeCm={setDepth} testId="schema-depth" disabled={isSdb} />
              </div>
              {!isSdb && (
                <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2 border-t border-border pt-3">
                  <label className="flex flex-wrap items-center justify-center gap-1 rounded-md border border-border bg-background px-2 py-2 text-[10px] text-center leading-tight cursor-pointer min-h-[46px]"><input data-testid="schema-back-panel" type="checkbox" checked={options.has_back_panel} onChange={(e) => setOptions({ ...options, has_back_panel: e.target.checked })} className="accent-primary" /> Fond</label>
                  <label className="flex flex-wrap items-center justify-center gap-1 rounded-md border border-border bg-background px-2 py-2 text-[10px] text-center leading-tight cursor-pointer min-h-[46px]"><input data-testid="schema-feet" type="checkbox" checked={options.feet_visible !== false} onChange={(e) => setOptions({ ...options, feet_visible: e.target.checked })} className="accent-primary" /> Pieds</label>
                  <label className="flex flex-wrap items-center justify-center gap-1 rounded-md border border-border bg-background px-2 py-2 text-[10px] text-center leading-tight cursor-pointer min-h-[46px]">
                    <input data-testid="schema-plinth" type="checkbox" checked={!!options.plinth} onChange={(e) => setOptions({ ...options, plinth: e.target.checked })} className="accent-primary" /> Plinthe
                    {options.plinth && (
                      <select
                        data-testid="schema-plinth-height"
                        value={options.plinth_height_mm || 80}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setOptions({ ...options, plinth_height_mm: Number(e.target.value) })}
                        className="h-7 rounded border border-border bg-card px-1.5 text-[11px] font-medium cursor-pointer"
                        title="Hauteur de la plinthe"
                      >
                        <option value={80}>80mm</option>
                        <option value={100}>100mm</option>
                      </select>
                    )}
                  </label>
                  <label className="flex flex-wrap items-center justify-center gap-1 rounded-md border border-border bg-background px-2 py-2 text-[10px] text-center leading-tight cursor-pointer min-h-[46px]"><input data-testid="schema-fileur-left" type="checkbox" checked={!!options.fileur_left} onChange={(e) => setOptions({ ...options, fileur_left: e.target.checked })} className="accent-primary" /> Fileur G{options.fileur_left && <FileurMmField testId="schema-fileur-left-mm" value={options.fileur_left_width} onChange={(cm) => setOptions({ ...options, fileur_left_width: cm })} />} {options.fileur_left && "mm"}</label>
                  <label className="flex flex-wrap items-center justify-center gap-1 rounded-md border border-border bg-background px-2 py-2 text-[10px] text-center leading-tight cursor-pointer min-h-[46px]"><input data-testid="schema-fileur-right" type="checkbox" checked={!!options.fileur_right} onChange={(e) => setOptions({ ...options, fileur_right: e.target.checked })} className="accent-primary" /> Fileur D{options.fileur_right && <FileurMmField testId="schema-fileur-right-mm" value={options.fileur_right_width} onChange={(cm) => setOptions({ ...options, fileur_right_width: cm })} />} {options.fileur_right && "mm"}</label>
                  <label className="flex flex-wrap items-center justify-center gap-1 rounded-md border border-border bg-background px-2 py-2 text-[10px] text-center leading-tight cursor-pointer min-h-[46px]"><input data-testid="schema-fileur-top" type="checkbox" checked={!!options.fileur_top} onChange={(e) => setOptions({ ...options, fileur_top: e.target.checked })} className="accent-primary" /> Fileur H{options.fileur_top && <FileurMmField testId="schema-fileur-top-mm" value={options.fileur_top_width} onChange={(cm) => setOptions({ ...options, fileur_top_width: cm })} />} {options.fileur_top && "mm"}</label>
                  {/* Chantier 3.2 : côtés gauche/droit du meuble "identique à la façade" —
                      même principe (case à cocher) que les fileurs ci-dessus. Information
                      de fabrication uniquement, n'affecte pas le schéma 2D. */}
                  <label className="flex flex-wrap items-center justify-center gap-1 rounded-md border border-border bg-background px-2 py-2 text-[10px] text-center leading-tight cursor-pointer min-h-[46px]"><input data-testid="schema-side-left-facade" type="checkbox" checked={!!options.side_left_facade} onChange={(e) => setOptions({ ...options, side_left_facade: e.target.checked })} className="accent-primary" /> Côté Gauche : Décor Façade</label>
                  <label className="flex flex-wrap items-center justify-center gap-1 rounded-md border border-border bg-background px-2 py-2 text-[10px] text-center leading-tight cursor-pointer min-h-[46px]"><input data-testid="schema-side-right-facade" type="checkbox" checked={!!options.side_right_facade} onChange={(e) => setOptions({ ...options, side_right_facade: e.target.checked })} className="accent-primary" /> Côté Droit : Décor Façade</label>
                  {/* Préparation caisson pour portes coulissantes (non fournies) — remis en avant dans
                      la barre compacte du schéma, auparavant uniquement dans le panneau détaillé masqué. */}
                  <label className={`flex flex-wrap items-center justify-center gap-1 rounded-md border px-2 py-2 text-[10px] text-center leading-tight cursor-pointer min-h-[46px] ${options.prepared_for_coulissant ? "border-primary bg-primary/5 text-primary font-medium" : "border-border bg-background"}`} title="Réservation +100mm profondeur extérieure — portes et rails coulissants non fournis">
                    <input data-testid="schema-prepared-coulissant" type="checkbox" checked={!!options.prepared_for_coulissant} onChange={(e) => setOptions({ ...options, prepared_for_coulissant: e.target.checked })} className="accent-primary" /> Préparation pour portes coulissantes (Portes non fournies)
                  </label>
                  {canUseUnderSlope && (
                    <button
                      type="button"
                      data-testid="schema-under-slope"
                      onClick={() => {
                        const isActive = options.under_slope_enabled || options.under_slope_rear_enabled || activeElementTool === "slope";
                        if (isActive) {
                          // Décocher parent → décocher tous les enfants + fermer panneau
                          setActiveElementTool(null);
                          setOptions((prev) => ({ ...prev, under_slope_enabled: false, under_slope_rear_enabled: false }));
                        } else {
                          setActiveElementTool("slope");
                        }
                      }}
                      className={`flex flex-wrap items-center justify-center gap-1 rounded-md border px-2 py-2 text-[10px] text-center leading-tight cursor-pointer min-h-[46px] transition-colors ${(options.under_slope_enabled || options.under_slope_rear_enabled) ? "border-primary bg-primary/5 text-primary font-medium" : activeElementTool === "slope" ? "border-primary/60 bg-primary/5" : "border-border bg-background hover:border-primary/40"}`}
                      title="Cliquez pour configurer les sous-pentes latérale et arrière"
                    >
                      <Mountain className="w-3.5 h-3.5" />
                      Sous-pente {(options.under_slope_enabled || options.under_slope_rear_enabled) ? "(+40 %)" : ""}
                    </button>
                  )}
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
                <label className="flex items-center gap-1.5"><span className="text-sm font-bold">{isSdb ? "Finition (Côtés)" : "Finition (Caissons)"}</span>{carcassMatchesFacade && hasDifferentiatedFacade ? (
                  <span data-testid="schema-carcass-finish-synced" className="h-8 min-w-28 flex items-center rounded-md border border-border bg-muted/50 px-2 text-xs text-muted-foreground">{carcassFinish?.name || "—"}</span>
                ) : (
                  <select data-testid="schema-carcass-finish" value={carcassFinishId || "blanc_premium"} onChange={(e) => { setCarcassFinishId(e.target.value); setCustomColorRequested(false); }} className="h-8 min-w-28 rounded-md border border-border bg-background px-2 text-xs">{isSdb ? catalog.finishes.filter((finish) => ["blanc_premium", "gris_graphite", "chene_davos", "beton_chicago"].includes(finish.id)).map((finish) => <option key={finish.id} value={finish.id}>{finish.name}</option>) : <><option value="blanc_premium">Blanc</option><option value="melamine_gris">Gris</option></>}</select>
                )}</label>
                {!isSdb && (
                  <label className={`inline-flex items-center gap-1.5 text-[11px] ${hasDifferentiatedFacade ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`} title={hasDifferentiatedFacade ? "" : "Choisissez d'abord une façade différenciée"}>
                    <input data-testid="schema-carcass-matches-facade" type="checkbox" checked={carcassMatchesFacade} disabled={!hasDifferentiatedFacade} onChange={(e) => setCarcassMatchesFacade(e.target.checked)} className="accent-primary" />
                    Identique à la façade
                  </label>
                )}
                <label className="flex items-center gap-1.5"><span className="text-sm font-bold">Finition (Façades)</span><select data-testid="schema-doors-finish" value={doorsFinishId} onChange={(e) => setDoorsFinishId(e.target.value)} className="h-8 min-w-36 rounded-md border border-border bg-background px-2 text-xs">{!isSdb && (<><option value="none">Sans façade</option><option value="">Identique au caisson</option></>)}{catalog.finishes.filter((finish) => ["blanc_premium", "gris_graphite", "chene_davos", "beton_chicago"].includes(finish.id)).map((finish) => <option key={finish.id} value={finish.id}>{finish.name}</option>)}</select></label>
                <HandlePicker value={defaultHandleId} onChange={setDefaultHandleId} handles={isSdb ? catalog.sdb_handles : catalog.handles} />
                <span className="ml-auto text-[10px] text-muted-foreground">Sections : {sections.length} · posez les éléments directement sur le plan</span>
              </div>
            </section>
            {/* Bandeau instruction Drag & Drop — visible uniquement Dressing, MASQUÉ sur mobile pour gagner de la place */}
            {!isSdb && (
              <div
                data-testid="dnd-banner"
                className="hidden lg:flex mb-3 rounded-xl border-2 border-primary/40 bg-primary/5 px-4 py-2.5 items-center gap-2.5"
              >
                <MousePointer2 className="w-5 h-5 text-primary shrink-0" />
                <p className="text-sm sm:text-base font-heading font-bold text-primary leading-tight">
                  Glissez vos éléments directement dans le meuble pour l&apos;aménager
                </p>
              </div>
            )}

            {canUseUnderSlope && activeElementTool === "slope" && (
              <section data-testid="under-slope-panel" className="paper mb-3 border-t-4 border-t-primary p-3 sm:p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="eyebrow text-[10px]">Réglage du volume</p>
                    <h2 className="font-heading text-lg">Sous-pentes</h2>
                  </div>
                  <button type="button" data-testid="close-under-slope-panel" onClick={() => setActiveElementTool(null)} className="rounded-md border border-border p-2 text-muted-foreground transition-colors hover:border-primary hover:text-primary" aria-label="Fermer les réglages sous-pente"><X className="h-4 w-4" /></button>
                </div>
                <div className="mt-4 grid gap-4 xl:grid-cols-2">
                  <div className={`rounded-lg border p-3 ${options.under_slope_enabled ? "border-primary bg-primary/5" : "border-border"}`}>
                    <ToggleRow
                      testId="under-slope-toggle"
                      label="Sous-pente latérale"
                      checked={!!options.under_slope_enabled}
                      onChange={(enabled) => setOptions((prev) => ({
                        ...prev,
                        under_slope_enabled: enabled,
                        under_slope_low_height_mm: Math.max(100, Math.min(Math.round(height * 10) - 10, prev.under_slope_low_height_mm || Math.round(height * 10 * 0.6))),
                        under_slope_bevel_width_mm: Math.max(100, Math.min(Math.round(width * 10), prev.under_slope_bevel_width_mm || Math.round(Math.min(width * 10, 600)))),
                      }))}
                      hint="Découpe le toit sur la largeur du meuble."
                    />
                    {options.under_slope_enabled && (
                      <div className="mt-3 space-y-3">
                        <div className="grid grid-cols-2 gap-2" data-testid="under-slope-direction-control">
                          {[{ id: "left", label: "Pente à gauche" }, { id: "right", label: "Pente à droite" }].map((side) => <button key={side.id} type="button" data-testid={`under-slope-direction-${side.id}`} onClick={() => setOptions((prev) => ({ ...prev, under_slope_direction: side.id }))} className={`rounded-md border px-3 py-2 text-xs font-medium transition-colors ${options.under_slope_direction === side.id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:border-primary"}`}>{side.label}</button>)}
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <SlopeMetricControl label="Hauteur basse" value={options.under_slope_low_height_mm} min={100} max={Math.round(height * 10) - 10} testId="under-slope-low-height" onChange={(value) => setOptions((prev) => ({ ...prev, under_slope_low_height_mm: value }))} />
                          <SlopeMetricControl label="Largeur du biseau" value={options.under_slope_bevel_width_mm} min={100} max={Math.round(width * 10)} testId="under-slope-bevel-width" onChange={(value) => setOptions((prev) => ({ ...prev, under_slope_bevel_width_mm: value }))} />
                        </div>
                      </div>
                    )}
                  </div>
                  <div data-testid="under-slope-rear-panel" className={`rounded-lg border p-3 ${options.under_slope_rear_enabled ? "border-primary bg-primary/5" : "border-border"}`}>
                    <ToggleRow
                      testId="under-slope-rear-toggle"
                      label="Sous-pente arrière"
                      checked={!!options.under_slope_rear_enabled}
                      onChange={(enabled) => setOptions((prev) => ({
                        ...prev,
                        under_slope_rear_enabled: enabled,
                        under_slope_rear_low_height_mm: Math.max(300, Math.min(Math.round(height * 10) - 10, prev.under_slope_rear_low_height_mm || Math.round(height * 10 * 0.6))),
                        under_slope_rear_bevel_depth_mm: Math.max(100, Math.min(Math.round(depth * 10), prev.under_slope_rear_bevel_depth_mm || Math.round(Math.min(depth * 10, 450)))),
                      }))}
                      hint="Découpe le toit en profondeur vers le fond ; visible en vue Côté et 3D."
                    />
                    {options.under_slope_rear_enabled && (
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        <SlopeMetricControl label="Hauteur basse arrière" value={options.under_slope_rear_low_height_mm} min={300} max={Math.round(height * 10) - 10} testId="under-slope-rear-low-height" onChange={(value) => setOptions((prev) => ({ ...prev, under_slope_rear_low_height_mm: value }))} />
                        <SlopeMetricControl label="Profondeur du biseau" value={options.under_slope_rear_bevel_depth_mm} min={100} max={Math.round(depth * 10)} testId="under-slope-rear-bevel-depth" onChange={(value) => setOptions((prev) => ({ ...prev, under_slope_rear_bevel_depth_mm: value }))} />
                      </div>
                    )}
                  </div>
                </div>
              </section>
            )}

            <div className="flex flex-col gap-3 lg:grid lg:grid-cols-[96px_minmax(0,1fr)] lg:items-start">
              {/* Palette Drag & Drop — masquée dans le tunnel SdB.
                  Mobile : barre horizontale compacte au-dessus du visuel.
                  Desktop (lg+) : colonne verticale à gauche. */}
              {!isSdb && (
              <>
              <div
                className="paper p-2 flex flex-row flex-wrap items-center gap-1.5 overflow-visible sm:flex-nowrap sm:overflow-x-auto lg:h-[clamp(600px,calc(100vh-11rem),900px)] lg:flex-col lg:flex-nowrap lg:items-stretch lg:overflow-y-auto lg:overflow-x-hidden"
                data-testid="drag-palette"
              >
                <div className="flex flex-row items-center gap-1 pr-2 border-r border-border/60 shrink-0 lg:flex-col lg:pr-0 lg:pb-2 lg:border-r-0 lg:border-b">
                  <Layers className="w-4 h-4 text-primary" />
                  <span className="text-[10px] font-medium uppercase tracking-wider lg:writing-mode-vertical">Éléments</span>
                </div>
                <PlaceModeBtn mode="section" active={activeMode} onSet={setActiveMode} label="Section" testId="add-section" vertical />
                <DragPill kind="drawer" label="Tiroir" testId="drag-drawer" vertical activeMode={activeMode} onSetActiveMode={setActiveMode} />
                <DragPill kind="penderie" label="Penderie" testId="drag-penderie" vertical activeMode={activeMode} onSetActiveMode={setActiveMode} />
                <DragPill kind="shelf_fixed" label="Tab. fixe" testId="drag-shelf-fixed" vertical activeMode={activeMode} onSetActiveMode={setActiveMode} />
                <DragPill kind="shelf_mobile" label="Tab. mobile" testId="drag-shelf-mobile" vertical activeMode={activeMode} onSetActiveMode={setActiveMode} />
                <div className="h-7 border-l border-border mx-1 lg:h-px lg:w-full lg:border-l-0 lg:border-t" />
              </div>

              </>
              )}

              <div className="paper p-3 sm:p-4 lg:p-6 h-[42vh] min-h-[340px] lg:h-[clamp(600px,calc(100vh-11rem),900px)] lg:col-start-2 flex flex-col relative overflow-hidden">
              {sections.some((sec) => sec.manual_wide_section && (sec.width_cm || 0) * 10 > 800) && !wideSectionAlertDismissed && (
                <div data-testid="wide-section-alert-banner" className="absolute top-14 sm:top-16 left-3 right-3 sm:left-4 sm:right-4 z-20 flex items-start gap-2 rounded-lg border-2 border-amber-500 bg-amber-50 px-3 py-2 text-amber-900 shadow-sm">
                  <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0" />
                  <span className="text-xs font-bold leading-snug flex-1">Sous réserve de validation — séparation retirée au-delà de 800 mm de largeur, à valider avant fabrication.</span>
                  <button
                    type="button"
                    data-testid="wide-section-alert-dismiss"
                    aria-label="Fermer l'alerte"
                    onClick={() => setWideSectionAlertDismissed(true)}
                    className="shrink-0 rounded-full p-0.5 text-amber-900/70 hover:text-amber-900 hover:bg-amber-200/60 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <div className="absolute top-3 right-3 sm:top-4 sm:right-4 flex max-w-[calc(100%-1.5rem)] flex-wrap justify-end items-center gap-2 z-10">
                <div className="flex rounded-full border border-border bg-background overflow-hidden" data-testid="view-mode-toggle">
                  <button
                    data-testid="view-2d"
                    onClick={() => { setViewMode("2d"); setTwoDView("front"); }}
                    className={`px-3 py-1.5 text-[11px] font-medium transition-colors ${viewMode === "2d" && twoDView === "front" ? "bg-primary text-primary-foreground" : "hover:bg-background/60"}`}
                  >Façade</button>
                  <button data-testid="view-side" onClick={() => { setViewMode("2d"); setTwoDView("side"); }} className={`px-3 py-1.5 text-[11px] font-medium transition-colors ${viewMode === "2d" && twoDView === "side" ? "bg-primary text-primary-foreground" : "hover:bg-background/60"}`}>Côté</button>
                  <button
                    data-testid="view-3d"
                    onClick={() => setViewMode("3d")}
                    className={`px-3 py-1.5 text-[11px] font-medium transition-colors ${viewMode === "3d" ? "bg-primary text-primary-foreground" : "hover:bg-background/60"}`}
                  >3D</button>
                </div>
                <button
                  data-testid="toggle-doors"
                  onClick={() => setShowDoors(!showDoors)}
                  className={`rounded-full border px-3 py-1.5 text-[11px] font-medium transition-colors ${showDoors ? "bg-background border-border hover:border-primary" : "bg-primary/10 border-primary/40 text-primary"}`}
                  title={showDoors ? "Masquer les portes" : "Afficher les portes"}
                  aria-label={showDoors ? "Masquer les portes" : "Afficher les portes"}
                  aria-pressed={showDoors}
                >{showDoors ? "Masquer les portes" : "Afficher les portes"}</button>
                {/* Raccourci visuel : assembler ce meuble avec un autre du projet, directement
                    depuis le schéma (plus besoin de descendre jusqu'au panneau "Plusieurs meubles"). */}
                {additionalItems.length > 0 && (
                  <button
                    type="button"
                    data-testid="schema-link-assembly"
                    disabled={isCurrentDuplicateOfLastAdded}
                    onClick={() => setLinkedAdditionalItemId((current) => current ? null : additionalItems[additionalItems.length - 1].id)}
                    className={`rounded-full border px-3 py-1.5 text-[11px] font-medium transition-colors inline-flex items-center gap-1 disabled:opacity-40 ${linkedAdditionalItemId ? "bg-primary border-primary text-primary-foreground" : "bg-background border-border hover:border-primary"}`}
                    title={isCurrentDuplicateOfLastAdded ? "Modifiez d'abord le meuble courant pour créer deux modules distincts" : linkedAdditionalItemId ? `Assemblé avec « ${linkedAdditionalItem?.label} » — cliquer pour dissocier` : additionalItems.length > 1 ? `Assembler avec « ${additionalItems[additionalItems.length - 1].label} » (choix précis dans « Plusieurs meubles » ci-dessous)` : `Assembler avec « ${additionalItems[0].label} »`}
                  >
                    {linkedAdditionalItemId ? <Unlink className="w-3 h-3" /> : <Link2 className="w-3 h-3" />} {linkedAdditionalItemId ? "Assemblé" : "Assembler"}
                  </button>
                )}
              </div>
              {/* Chantier 3.3 : légende compacte d'actions pour l'élément sélectionné sur le
                  schéma — alternative à la croix isolée (bug de clic sur le bord), en plus de
                  la croix désormais centrée sur la façade elle-même. */}
              {selectedElementInfo && !isSdb && (
                <div
                  data-testid="element-actions-legend"
                  className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 z-20 w-[118px] rounded-xl border border-border bg-background/95 backdrop-blur-sm shadow-lg p-2.5 flex flex-col gap-1.5"
                >
                  <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground truncate">
                    {{ drawer: "Tiroir", penderie: "Penderie", shelf_fixed: "Tablette fixe", shelf_mobile: "Tablette mobile" }[selectedElementInfo.el.kind] || selectedElementInfo.el.kind}
                  </p>
                  <p className="text-[11px] font-semibold">H : {Math.round(selectedElementInfo.el.y_mm)} mm</p>
                  {!selectedElementInfo.el.locked ? (
                    <>
                      <button
                        type="button"
                        data-testid="legend-duplicate-element"
                        onClick={() => handleDuplicateElement(selectedElementInfo.sectionIdx, selectedElementInfo.el.id)}
                        className="flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 text-primary px-2 py-1 text-[11px] font-medium hover:bg-primary/10 transition-colors"
                      >
                        <Copy className="w-3 h-3" /> Dupliquer
                      </button>
                      <button
                        type="button"
                        data-testid="legend-remove-element"
                        onClick={() => { handleRemoveElement(selectedElementInfo.sectionIdx, selectedElementInfo.el.id); setSelectedPlacedElement(null); }}
                        className="flex items-center gap-1.5 rounded-lg border border-destructive/30 bg-destructive/5 text-destructive px-2 py-1 text-[11px] font-medium hover:bg-destructive/10 transition-colors"
                      >
                        <Trash2 className="w-3 h-3" /> Supprimer
                      </button>
                    </>
                  ) : (
                    <p className="text-[10px] text-muted-foreground flex items-center gap-1"><Lock className="w-3 h-3" /> Structurel</p>
                  )}
                  <button
                    type="button"
                    data-testid="legend-close"
                    onClick={() => setSelectedPlacedElement(null)}
                    className="text-[10px] text-muted-foreground hover:text-foreground self-end"
                  >Fermer</button>
                </div>
              )}
              <div className="flex min-h-0 w-full flex-1 items-center justify-center">
                {viewMode === "2d" ? (
                  <CabinetVisualizer
                    totalWidth={width}
                    height={height}
                    depth={depth}
                    sections={sections}
                    carcassColor={carcassFinish?.swatch || "#B8935A"}
                    doorsColor={doorsFinish?.swatch || null}
                    doorsDifferent={!!doorsFinishId && doorsFinishId !== "none" && doorsFinishId !== carcassFinishId}
                    separations={breakdown?.separations ?? 0}
                    showDoors={showDoors}
                    handles={isSdb ? catalog.sdb_handles : catalog.handles}
                    defaultHandleId={defaultHandleId}
                    activeMode={activeMode}
                    onSectionClick={(idx, yFrac, xFrac) => {
                      handleClickPlace(idx, yFrac, xFrac);
                      if (activeMode !== "door") setActiveMode(null);
                    }}
                    onPlaceDoor={placeDoorOnSection}
                    // SdB : la façade est imposée par le produit (tiroirs seuls, ou colonne
                    // 2 portes fixe) — aucun choix de porte à exposer au client.
                    onFacadeMode={isSdb ? null : setFacadeMode}
                    onFacadePart={isSdb ? null : setFacadePart}
                    onResizeDoorPart={isSdb ? null : resizeFacadePart}
                    onDropElement={handleDropElement}
                    onMoveElement={handleMoveElement}
                    onResizeElement={isSdb ? null : handleResizeElement}
                    onEditSectionWidth={isSdb ? null : setSectionWidthMm}
                    onRemoveElement={handleRemoveElement}
                    onDuplicateElement={isSdb ? null : handleDuplicateElement}
                    onElementSelect={setSelectedPlacedElement}
                    onRemoveDoor={isSdb ? null : handleRemoveDoor}
                    onRemoveDoorSegment={isSdb ? null : handleRemoveDoorSegment}
                    onOpenDoorMenu={isSdb ? null : (sectionIdx, x, y) => setDoorMenu({ sectionIdx, x, y })}
                    onRemoveSection={removeSectionFromPlan}
                    onSectionSelect={() => setActiveElementTool("sections")}
                    onExtendDrawer={handleExtendTraversingDrawer}
                    onReduceDrawer={handleReduceTraversingDrawer}
                    onSeparatorDrag={handleSeparatorDrag}
                    onResizeTotal={handleResizeTotal}
                    options={options}
                    templateVariant={templateVariant}
                    sdbDrawers={template?.sdb_drawers || 0}
                    twoDView={twoDView}
                  />
                ) : (
                  <CabinetVisualizer3D
                    totalWidth={width}
                    height={height}
                    depth={depth}
                    sections={sections}
                    carcassColor={carcassFinish?.swatch || "#B8935A"}
                    doorsColor={doorsFinish?.swatch || null}
                    doorsFinishId={doorsFinishId && doorsFinishId !== "none" ? doorsFinishId : null}
                    doorsDifferent={!!doorsFinishId && doorsFinishId !== "none" && doorsFinishId !== carcassFinishId}
                    showDoors={showDoors}
                    templateVariant={templateVariant}
                    options={options}
                    sdbDrawers={template?.sdb_drawers || 0}
                    handleSvgType={selectedHandle?.svg_type || null}
                  />
                )}
              </div>
              <div className="pt-2 lg:pt-4 border-t border-border flex items-end justify-between gap-2">
                <div className="min-w-0">
                  <p className="eyebrow text-[10px] truncate">{structureLabel(template?.name, sections.some((s) => s.has_door))} · {category?.name}</p>
                  <p className="font-heading text-sm lg:text-lg mt-0.5 truncate">
                    {sections.length} section{sections.length > 1 ? "s" : ""} · {width * 10}×{height * 10}×{depth * 10} mm
                  </p>
                  <p className="hidden lg:block text-xs text-muted-foreground">
                    Caisson : {carcassFinish?.name}{doorsFinishId && doorsFinishId !== "none" && doorsFinishId !== carcassFinishId ? ` · Portes : ${doorsFinish?.name}` : ""}
                  </p>
                </div>
                <div data-testid="price-hidden-notice" className="text-right shrink-0">
                  <p className="eyebrow text-[10px]">Tarif professionnel</p>
                  <p className="font-heading text-sm lg:text-base mt-0.5">Visible sur devis</p>
                </div>
              </div>
            </div>
            </div>

          {!isSdb && false && <FacadeConfigurator
            sections={sections}
            hasDrawerCut={sectionHasDrawerCut}
            onModeChange={setFacadeMode}
            onPartChange={setFacadePart}
            onRemoveDoor={handleRemoveDoor}
          />}

          </div>

          <section data-testid="quote-summary-band" className="order-2 paper border-t-4 border-t-primary p-4 sm:p-5">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
              <div>
                <p className="eyebrow text-[10px]">Récapitulatif du devis</p>
                <div className="flex items-center gap-2.5">
                  <div data-testid="main-item-badge" className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center font-heading text-xs shrink-0">1</div>
                  <h2 className="font-heading text-xl">{projectName || "Référence chantier à renseigner"}</h2>
                </div>
                <p className="mt-1 text-sm text-muted-foreground pl-[42px]">{Math.round(width * 10)} × {Math.round(height * 10)} × {Math.round(depth * 10)} mm · {sections.length} section{sections.length > 1 ? "s" : ""} · {sections.reduce((sum, section) => sum + (section.elements || []).length, 0)} élément{sections.reduce((sum, section) => sum + (section.elements || []).length, 0) > 1 ? "s" : ""} · {sections.filter((section) => section.has_door).length} porte{sections.filter((section) => section.has_door).length > 1 ? "s" : ""}</p>
              </div>
              <div className="flex items-center gap-3"><span className="text-xs text-muted-foreground">Prix détaillé après création du devis</span><Button data-testid="summary-create-quote" onClick={() => setModalOpen(true)} disabled={loading || !breakdown} className="btn-primary">Valider le devis <ArrowRight className="w-4 h-4" /></Button></div>
            </div>
          </section>

          {/* Multi-meubles : remis dans le parcours visible (auparavant piégé dans le panneau
              "configurator-right" masqué — fonctionnalité invisible pour l'utilisateur). */}
          <section className="order-3 paper p-6 border-l-4 border-l-accent" data-testid="multi-furniture-panel">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
              <h2 className="font-heading text-xl shrink-0">Plusieurs meubles ?</h2>
              <Button
                data-testid="add-current-to-project"
                variant="outline"
                size="sm"
                className="w-full sm:w-auto h-10 sm:h-8 justify-center touch-manipulation"
                type="button"
                onClick={handleAddProjectActivation}
                disabled={!breakdown}
              >
                <Plus className="w-3.5 h-3.5" /> Ajouter ce meuble au projet
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Configurez plusieurs meubles pour le même chantier (ex: dressing gauche + droite, meuble SDB + colonne). Le devis PDF cumule tous les meubles.
            </p>
            {linkedAdditionalItem && (
              <div data-testid="assembly-link-summary" className="mb-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5">
                <p className="text-xs font-medium text-primary inline-flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Ensemble assemblé sur chantier</p>
                <p className="mt-1 text-[11px] text-muted-foreground">Module A (meuble courant) + Module B ({linkedAdditionalItem.label}). Le devis conservera deux caissons distincts.</p>
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="assembly-swap-order"
                    onClick={() => setAssemblyModuleOrder((o) => o === "a_left" ? "b_left" : "a_left")}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-[11px] font-medium text-foreground hover:border-primary hover:bg-primary/5 transition-colors"
                  >
                    <ArrowLeftRight className="w-3 h-3" />
                    Inverser G / D
                  </button>
                  <span className="text-[10px] text-muted-foreground">
                    {assemblyModuleOrder === "a_left" ? "Module A à gauche · Module B à droite" : "Module B à gauche · Module A à droite"}
                  </span>
                </div>
                <div className="mt-2 max-w-xs mx-auto">
                  <AssemblyOverview
                    mainConfig={{ total_width: width, height, sections, carcass_finish_id: carcassFinishId }}
                    moduleBConfig={linkedAdditionalItem.config}
                    assembly={{
                      continuous_plinth_mm: (options.plinth && linkedAdditionalItem.config.options?.plinth) ? Math.round((width + linkedAdditionalItem.config.total_width) * 10) : null,
                      module_order: assemblyModuleOrder,
                    }}
                    catalog={catalog}
                    mode="technical"
                  />
                </div>
                <div className="mt-3" data-testid="assembly-3d-preview">
                  <p className="text-[10px] text-muted-foreground mb-1.5 font-medium">Assemblage 3D</p>
                  <AssemblyVisualizer3D
                    mainConfig={{ total_width: width, height, depth, sections, carcass_finish_id: carcassFinishId, doors_finish_id: doorsFinishId, template_variant: templateVariant, options }}
                    moduleBConfig={linkedAdditionalItem.config}
                    assembly={{
                      continuous_plinth_mm: (options.plinth && linkedAdditionalItem.config.options?.plinth) ? Math.round((width + linkedAdditionalItem.config.total_width) * 10) : null,
                      module_order: assemblyModuleOrder,
                    }}
                    catalog={catalog}
                  />
                </div>
              </div>
            )}
            {isCurrentDuplicateOfLastAdded && (
              <p data-testid="duplicate-current-warning" className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2">
                Le meuble actuel est identique au dernier meuble déjà ajouté au projet — il n'est donc pas compté une deuxième fois. Modifiez une dimension ou une option sur le canvas pour créer un meuble réellement différent avant de l'ajouter à nouveau.
              </p>
            )}
            {assemblyChoices.length === 0 ? (
              <p className="text-[11px] text-muted-foreground italic bg-background rounded-lg p-3">
                Aucun meuble additionnel — le devis actuel contient uniquement le meuble ci-dessus (Meuble 1).
              </p>
            ) : (
              <div className="space-y-2" data-testid="additional-items-list">
                {assemblyChoices.map((it, i) => {
                  const isLinked = linkedAdditionalItemId === it.id;
                  return (
                  <div
                    key={it.id}
                    data-testid={`additional-item-${it.id}`}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${isLinked ? "bg-primary/5 border-primary/40" : "bg-background border-border"}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-heading text-xs shrink-0 ${isLinked ? "bg-primary text-primary-foreground" : "bg-accent/20 text-foreground"}`}>
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p data-testid={`additional-label-${it.id}`} className="text-sm font-medium truncate">Meuble {i + 1}</p>
                        {isLinked && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-primary bg-primary/10 rounded-full px-1.5 py-0.5 shrink-0">
                            <Link2 className="w-2.5 h-2.5" /> Module assemblé
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {it.config.total_width * 10}×{it.config.height * 10}×{it.config.depth * 10} mm · {it.config.sections.length} section{it.config.sections.length > 1 ? "s" : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        data-testid={`toggle-assembly-link-${it.id}`}
                        disabled={isCurrentDuplicateOfLastAdded}
                        onClick={() => setLinkedAdditionalItemId((current) => current === it.id ? null : it.id)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors disabled:opacity-40 ${isLinked ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-primary hover:bg-primary/10"}`}
                        title={isCurrentDuplicateOfLastAdded ? "Modifiez d’abord le meuble courant pour créer deux modules distincts" : linkedAdditionalItemId === it.id ? "Dissocier ces modules" : "Lier au meuble courant (assemblage)"}
                      >
                        {isLinked ? <Unlink className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        data-testid={`edit-additional-${it.id}`}
                        onClick={() => editAdditionalItem(it)}
                        className="w-8 h-8 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 flex items-center justify-center transition-colors"
                        title="Éditer ce meuble (remplacera le meuble en cours)"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`duplicate-additional-${it.id}`}
                        onClick={() => duplicateAdditionalItem(it)}
                        className="w-8 h-8 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 flex items-center justify-center transition-colors"
                        title="Dupliquer ce meuble"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`remove-additional-${it.id}`}
                        onClick={() => requestDeleteAdditionalItem(it.id)}
                        className="w-8 h-8 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 flex items-center justify-center transition-colors"
                        title="Retirer ce meuble du projet"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  );
                })}
                {/* Meuble {N} = le canvas affiché ci-dessus (en cours d'édition), toujours le dernier
                    numéro puisque c'est le dernier modifié chronologiquement. Sans cette carte, dès
                    qu'un "Meuble 1" est ajouté, le meuble courant n'apparaît plus nulle part dans la
                    liste et donne l'impression d'avoir disparu — on le rend donc toujours visible ici. */}
                <div data-testid="current-item-as-meuble-1" className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/30">
                  <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-heading text-xs shrink-0">{assemblyChoices.length + 1}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">Meuble {assemblyChoices.length + 1} <span className="text-[10px] font-normal text-muted-foreground">· en cours d'édition ci-dessus</span></p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {Math.round(width * 10)}×{Math.round(height * 10)}×{Math.round(depth * 10)} mm · {sections.length} section{sections.length > 1 ? "s" : ""}
                    </p>
                  </div>
                  <button
                    data-testid="remove-current-item"
                    onClick={() => setDeleteConfirmId("__current__")}
                    className="w-8 h-8 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 flex items-center justify-center transition-colors shrink-0"
                    title="Retirer ce meuble du projet"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-border/60">
                  <span className="text-xs text-muted-foreground">Total projet ({totalMeublesCount} meuble{totalMeublesCount > 1 ? "s" : ""})</span>
                  <span data-testid="total-project-price-hidden" className="text-xs font-medium">Calculé sur devis</span>
                </div>
              </div>
            )}

            {/* Dialog confirmation de suppression d'un meuble */}
            <Dialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
              <DialogContent data-testid="delete-furniture-confirm-dialog" className="max-w-sm">
                <DialogHeader>
                  <DialogTitle>Retirer ce meuble ?</DialogTitle>
                  <DialogDescription>
                    {deleteConfirmId === "__current__"
                      ? "Le meuble en cours sera retiré du projet. Le meuble suivant de la liste sera chargé sur le plan."
                      : deleteConfirmId && linkedAdditionalItemId === deleteConfirmId
                        ? "Ce meuble est lié à l'assemblage. Le retirer dissociera l'ensemble assemblé."
                        : "Cette action supprime définitivement ce meuble du projet en cours."}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2">
                  <Button variant="outline" size="sm" onClick={() => setDeleteConfirmId(null)}>Annuler</Button>
                  <Button
                    data-testid="confirm-delete-furniture"
                    variant="destructive"
                    size="sm"
                    onClick={() => deleteConfirmId === "__current__" ? removeCurrentItem() : removeAdditionalItem(deleteConfirmId)}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Retirer
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </section>

          {/* Réglages avancés : conservés hors du parcours principal. */}
          <div className="hidden" data-testid="configurator-right">
            {/* Catégorie retirée : l'univers de départ tient lieu de catégorie (règle V3) */}

            {/* Les dressings démarrent sans structure/porte : les éléments sont posés directement dans le plan. */}
            {isSdb && <section className="paper p-6">
              <h2 className="font-heading text-xl mb-4">1. Type de meuble SdB</h2>
              <div className={`grid ${isSdb ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2"} gap-2`} data-testid="templates-gallery">
                {catalog.templates.filter((t) => isSdb ? (t.variant === "sdb" || t.variant === "sdb_column") : (t.variant !== "sdb" && t.variant !== "sdb_column")).map((t) => {
                  const active = templateId === t.id;
                  const variant = t.variant || "battant";
                  return (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`template-${t.id}`}
                      onClick={() => setTemplateId(t.id)}
                      className={`group text-left rounded-xl border p-4 transition-all ${
                        active ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/40 bg-background"
                      }`}
                    >
                      {/* Icône simple selon variant */}
                      <div className="h-16 flex items-center justify-center mb-2 bg-background/50 rounded-lg" style={{ background: (carcassFinish?.swatch || "#B8935A") + "22" }}>
                        {variant === "open" && (
                          <svg viewBox="0 0 40 40" className="w-10 h-10">
                            <rect x="4" y="6" width="32" height="28" fill="none" stroke={carcassFinish?.swatch || "#B8935A"} strokeWidth="2" />
                            <line x1="4" y1="18" x2="36" y2="18" stroke={carcassFinish?.swatch || "#B8935A"} strokeWidth="1.5" />
                            <line x1="4" y1="26" x2="36" y2="26" stroke={carcassFinish?.swatch || "#B8935A"} strokeWidth="1.5" strokeDasharray="3 2" />
                          </svg>
                        )}
                        {variant === "battant" && (
                          <svg viewBox="0 0 40 40" className="w-10 h-10">
                            <rect x="4" y="6" width="14" height="28" fill={carcassFinish?.swatch || "#B8935A"} opacity="0.75" />
                            <rect x="22" y="6" width="14" height="28" fill={carcassFinish?.swatch || "#B8935A"} opacity="0.75" />
                            <circle cx="15" cy="20" r="1" fill="#111" />
                            <circle cx="25" cy="20" r="1" fill="#111" />
                          </svg>
                        )}
                        {variant === "coulissant" && (
                          <svg viewBox="0 0 40 40" className="w-10 h-10">
                            <rect x="4" y="8" width="22" height="24" fill={carcassFinish?.swatch || "#B8935A"} opacity="0.6" />
                            <rect x="14" y="8" width="22" height="24" fill={carcassFinish?.swatch || "#B8935A"} opacity="0.85" />
                            <line x1="2" y1="6" x2="38" y2="6" stroke="#666" strokeWidth="1.4" />
                            <line x1="2" y1="34" x2="38" y2="34" stroke="#666" strokeWidth="1.4" />
                          </svg>
                        )}
                      </div>
                      <p className="text-sm font-medium leading-tight">{t.name}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{t.description}</p>
                    </button>
                  );
                })}
              </div>
            </section>}

            {/* 2. Dimensions */}
            <section className={`paper p-6 ${!isSdb ? "hidden" : ""}`}>
              <h2 className="font-heading text-xl mb-4">1. Dimensions</h2>
              {isSdb ? (
                /* Salle de Bain — dimensions imposées par la fiche produit.
                   Seule la largeur est modulable (sauf Colonne 2 portes qui est 100% figée). */
                <div className="space-y-4" data-testid="sdb-dimensions-locked">
                  <div className="rounded-lg border border-primary/30 bg-primary/5 p-3.5 flex items-start gap-2.5 text-[12px] leading-snug">
                    <Lock className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <p className="text-primary">
                      Les dimensions techniques sont <b>imposées par la fiche produit</b>{" "}
                      {templateId === "sdb-colonne-2portes"
                        ? "(Colonne 2 portes : L 300 × H 1400 × P 200 mm)"
                        : "de ce modèle SdB. Seule la largeur est modulable."}
                    </p>
                  </div>
                  {/* Largeur — modulable sauf pour la Colonne */}
                  {templateId === "sdb-colonne-2portes" ? (
                    <LockedDimRow label="Largeur totale" value={30} testId="width-locked" />
                  ) : (
                    <DimensionControl label="Largeur totale" valueCm={width} minMm={(typeof SDB_MIN_WIDTH !== "undefined" ? SDB_MIN_WIDTH : 55) * 10} maxMm={(typeof SDB_MAX_WIDTH !== "undefined" ? SDB_MAX_WIDTH : 120) * 10} onChangeCm={setWidth} testId="width" />
                  )}
                  {/* Hauteur & Profondeur — verrouillées, affichées en info-cards */}
                  <div className="grid grid-cols-2 gap-3">
                    <LockedDimRow label="Hauteur" value={height} testId="height-locked" />
                    <LockedDimRow label="Profondeur" value={depth} testId="depth-locked" />
                  </div>
                </div>
              ) : (
                <div className="space-y-5">
                  <DimensionControl label="Largeur totale" valueCm={width} minMm={300} maxMm={2700} onChangeCm={handleSetWidthCm} testId="width" />
                  <DimensionControl label="Hauteur" valueCm={height} minMm={300} maxMm={2700} onChangeCm={setHeight} testId="height" />
                  <DimensionControl label="Profondeur" valueCm={depth} minMm={200} maxMm={750} onChangeCm={setDepth} testId="depth" />
                </div>
              )}
            </section>

            {/* 3. Options de pose (Lot 15) */}
            <section className="paper p-6" data-testid="options-panel">
              <h2 className="font-heading text-xl mb-1">2. Caisson & pose</h2>
              <p className="text-xs text-muted-foreground mb-4">Fond, plinthe, fileurs, pieds — visibles sur le dessin 2D/3D.</p>

              <div className="space-y-4">
                <div className="flex flex-col gap-4">
                  {canUseUnderSlope && activeElementTool !== "slope" && <div data-testid="under-slope-panel-legacy" className={`rounded-lg border-2 p-4 transition-colors ${options.under_slope_enabled || options.under_slope_rear_enabled ? "border-primary bg-primary/5" : "border-border bg-background"}`}>
                    <ToggleRow
                      testId="under-slope-toggle"
                      label="Sous-pente / Angle coupé"
                      checked={!!options.under_slope_enabled}
                      onChange={(enabled) => setOptions((prev) => ({
                        ...prev,
                        under_slope_enabled: enabled,
                        under_slope_low_height_mm: Math.max(100, Math.min(Math.round(height * 10) - 10, prev.under_slope_low_height_mm || Math.round(height * 10 * 0.6))),
                        under_slope_bevel_width_mm: Math.max(100, Math.min(Math.round(width * 10), prev.under_slope_bevel_width_mm || Math.round(Math.min(width * 10, 600)))),
                      }))}
                      hint="Découpe et dessus inclinés, adaptés automatiquement au rendu 2D/3D."
                    />
                    {options.under_slope_enabled && (
                      <div className="mt-4 space-y-4">
                        <div>
                          <p className="text-[11px] eyebrow mb-2">Côté de la pente</p>
                          <div className="grid grid-cols-2 gap-2" data-testid="under-slope-direction-control">
                            {[{ id: "left", label: "Gauche" }, { id: "right", label: "Droite" }].map((side) => (
                              <button
                                key={side.id}
                                type="button"
                                data-testid={`under-slope-direction-${side.id}`}
                                onClick={() => setOptions((prev) => ({ ...prev, under_slope_direction: side.id }))}
                                className={`px-3 py-2 rounded-md border text-xs font-medium transition-colors ${options.under_slope_direction === side.id ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}
                              >
                                Pente à {side.label.toLowerCase()}
                              </button>
                            ))}
                          </div>
                        </div>
                        <SlopeMetricControl
                          label="Hauteur basse"
                          value={options.under_slope_low_height_mm}
                          min={100}
                          max={Math.round(height * 10) - 10}
                          testId="under-slope-low-height"
                          onChange={(value) => setOptions((prev) => ({ ...prev, under_slope_low_height_mm: value }))}
                        />
                        <SlopeMetricControl
                          label="Largeur du biseau"
                          value={options.under_slope_bevel_width_mm}
                          min={100}
                          max={Math.round(width * 10)}
                          testId="under-slope-bevel-width"
                          onChange={(value) => setOptions((prev) => ({ ...prev, under_slope_bevel_width_mm: value }))}
                        />
                        {breakdown?.under_slope_surcharge > 0 && (
                          <div data-testid="under-slope-surcharge-line" className="rounded-md border border-primary/30 bg-background px-3 py-2 text-xs font-medium">
                            Majoration sous-pente appliquée sur le devis
                          </div>
                        )}
                        <div className="pt-3 border-t border-primary/20">
                          <input
                            ref={slopePhotoInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            data-testid="under-slope-photo-input"
                            className="sr-only"
                            onChange={(event) => {
                              handleSlopePhoto(event.target.files?.[0]);
                              event.target.value = "";
                            }}
                          />
                          <button
                            type="button"
                            data-testid="under-slope-photo-upload-button"
                            onClick={() => slopePhotoInputRef.current?.click()}
                            disabled={slopeAnalyzing}
                            className="w-full min-h-10 rounded-md border border-dashed border-primary/50 bg-background px-3 py-2 text-xs font-medium text-primary hover:bg-primary/5 disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2"
                          >
                            {slopeAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Info className="w-4 h-4" />}
                            {slopeAnalyzing ? "Analyse du croquis…" : "Importer une photo / un croquis"}
                          </button>
                          <p data-testid="under-slope-photo-formats" className="mt-1.5 text-[10px] text-muted-foreground">JPEG, PNG ou WEBP · 5 Mo max · image utilisée uniquement pour cette analyse.</p>
                          {options.customer_structure_sketch?.data_url && <div data-testid="customer-structure-sketch" className="mt-3 flex items-center gap-3 border-t border-border pt-3"><img src={options.customer_structure_sketch.data_url} alt="Croquis de structure client" className="h-14 w-20 rounded border border-border object-cover" /><div className="min-w-0"><p className="text-xs font-medium">Croquis client joint</p><p className="truncate text-[10px] text-muted-foreground">{options.customer_structure_sketch.name}</p></div></div>}
                          {slopeImageName && <p data-testid="under-slope-photo-name" className="mt-1 text-[10px] text-muted-foreground truncate">{slopeImageName}</p>}
                          {slopeAnalysisStatus !== "idle" && (
                            <p data-testid="under-slope-analysis-status" data-status={slopeAnalysisStatus} className="mt-2 text-[11px] font-medium text-primary">
                              {slopeAnalysisStatus === "loading" && "Analyse du croquis en cours…"}
                              {slopeAnalysisStatus === "success" && "Analyse terminée — paramètres à vérifier."}
                              {slopeAnalysisStatus === "not-detected" && "Sous-pente non confirmée — complétez les cotes manuellement."}
                              {slopeAnalysisStatus === "error" && "Analyse indisponible — vos réglages manuels restent conservés."}
                            </p>
                          )}
                          {slopeAnalysisNote && <p data-testid="under-slope-analysis-note" className="mt-2 rounded-md bg-background px-2.5 py-2 text-[11px] text-muted-foreground">{slopeAnalysisNote}</p>}
                        </div>
                      </div>
                    )}
                    <div className="mt-4 border-t border-primary/20 pt-4" data-testid="under-slope-rear-panel">
                      <ToggleRow
                        testId="under-slope-rear-toggle"
                        label="Sous-pente arrière (profondeur)"
                        checked={!!options.under_slope_rear_enabled}
                        onChange={(enabled) => setOptions((prev) => ({
                          ...prev,
                          under_slope_rear_enabled: enabled,
                          under_slope_rear_low_height_mm: Math.max(300, Math.min(Math.round(height * 10) - 10, prev.under_slope_rear_low_height_mm || Math.round(height * 10 * 0.6))),
                          under_slope_rear_bevel_depth_mm: Math.max(100, Math.min(Math.round(depth * 10), prev.under_slope_rear_bevel_depth_mm || Math.round(Math.min(depth * 10, 450)))),
                        }))}
                        hint="Le toit descend vers le fond du meuble ; visible en vue Côté et en 3D."
                      />
                      {options.under_slope_rear_enabled && (
                        <div className="mt-4 grid gap-4 sm:grid-cols-2">
                          <SlopeMetricControl
                            label="Hauteur basse arrière"
                            value={options.under_slope_rear_low_height_mm}
                            min={300}
                            max={Math.round(height * 10) - 10}
                            testId="under-slope-rear-low-height"
                            onChange={(value) => setOptions((prev) => ({ ...prev, under_slope_rear_low_height_mm: value }))}
                          />
                          <SlopeMetricControl
                            label="Profondeur du biseau arrière"
                            value={options.under_slope_rear_bevel_depth_mm}
                            min={100}
                            max={Math.round(depth * 10)}
                            testId="under-slope-rear-bevel-depth"
                            onChange={(value) => setOptions((prev) => ({ ...prev, under_slope_rear_bevel_depth_mm: value }))}
                          />
                        </div>
                      )}
                    </div>
                  </div>}
                  <ToggleRow
                    testId="opt-back-panel"
                    label="Fond de meuble"
                    checked={options.has_back_panel}
                    onChange={(v) => setOptions({ ...options, has_back_panel: v })}
                    hint={options.has_back_panel ? "Panneau de fond fermé" : "Sans fond"}
                  />
                  {!isSdb && (
                  <div>
                    <p className="text-[11px] eyebrow mb-2">Support du meuble (bas)</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {[
                        {
                          id: "feet",
                          label: "Avec pieds",
                          hint: "Meuble autoportant, posé directement au sol. Pieds visibles ou masqués par une plinthe (au choix ci-dessous).",
                        },
                        {
                          id: "no_feet",
                          label: "Sans pieds",
                          hint: "Caisson posé sur un autre meuble, suspendu au mur, ou touchant le sol sans pieds apparents.",
                        },
                      ].map((r) => {
                        const isSansPied = options.plinth === false && options.feet_visible === false;
                        const isActive = r.id === "feet" ? !isSansPied : isSansPied;
                        return (
                          <button
                            key={r.id}
                            type="button"
                            data-testid={`opt-support-${r.id}`}
                            onClick={() => {
                              if (r.id === "feet") setOptions({ ...options, feet_visible: true, plinth: true });
                              else setOptions({ ...options, plinth: false, feet_visible: false });
                            }}
                            className={`px-3 py-2.5 rounded-md text-left border transition-colors ${isActive ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}
                          >
                            <span className="block text-xs font-medium">{r.label}</span>
                            <span className={`block text-[10px] mt-0.5 leading-snug ${isActive ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                              {r.hint}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {!(options.plinth === false && options.feet_visible === false) && (
                      <label className="flex items-start gap-2 mt-2.5 px-3 py-2 rounded-md border border-border bg-background cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid="opt-support-add-plinth"
                          checked={options.plinth !== false}
                          onChange={(e) => setOptions({ ...options, plinth: e.target.checked, feet_visible: true })}
                          className="mt-0.5 w-4 h-4 accent-primary"
                        />
                        <span className="text-xs">
                          <span className="font-medium">Ajouter une plinthe</span>
                          <span className="block text-[10px] text-muted-foreground mt-0.5">
                            Masque les pieds avec un bandeau bas. Décochez pour laisser les pieds apparents.
                          </span>
                        </span>
                      </label>
                    )}
                  </div>
                  )}

                  {!isSdb && (
                    <div>
                      <p className="text-[11px] eyebrow mb-2">Gamme des tiroirs (Merivobox)</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {[
                          { id: "M", label: "Merivobox M", hint: "Côté bas · façade minimale 130 mm." },
                          { id: "L", label: "Merivobox L", hint: "Côté haut · façade jusqu’à 300 mm." },
                        ].map((r) => {
                          const isActive = (options.drawer_gamme || "M") === r.id || (r.id === "L" && options.drawer_gamme === "E");
                          return (
                            <button
                              key={r.id}
                              type="button"
                              data-testid={`opt-drawer-gamme-${r.id}`}
                              onClick={() => setOptions({ ...options, drawer_gamme: r.id })}
                              className={`px-3 py-2.5 rounded-md text-left border transition-colors ${isActive ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}
                            >
                              <span className="block text-xs font-medium">{r.label}</span>
                              <span className={`block text-[10px] mt-0.5 leading-snug ${isActive ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                                {r.hint}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1.5">
                        S'applique à tous les tiroirs du meuble. Tarifs par taille (largeur × profondeur) gérés dans le Back-Office.
                      </p>
                    </div>
                  )}
                </div>

                {/* Lot 15 iter3 : préparation caisson pour portes coulissantes (NON fournies) — non applicable en Salle de Bain (pas de portes) */}
                {!isSdb && (
                  <div className={`rounded-lg border-2 p-3 transition-all ${options.prepared_for_coulissant ? "border-primary bg-primary/5" : "border-border/70 bg-background"}`}>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="opt-prepared-coulissant"
                        checked={!!options.prepared_for_coulissant}
                        onChange={(e) => setOptions({ ...options, prepared_for_coulissant: e.target.checked })}
                        className="mt-0.5 w-4 h-4 accent-primary"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium">Préparer le caisson pour portes coulissantes</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                          Portes et rails <b>non fournis</b>. Réservation automatique de <b>+100 mm sur la profondeur extérieure</b> (sans réduire l'utile intérieur). Les cotes de vantaux à commander seront affichées.
                        </p>
                      </div>
                    </label>
                  </div>
                )}

                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Fileurs (habillage 150 mm par défaut)</p>
                  <div className="grid grid-cols-3 gap-2">
                    <FileurToggle testId="opt-fileur-left"  label="Gauche" checked={options.fileur_left}  width={options.fileur_left_width}
                      onCheck={(v) => setOptions({ ...options, fileur_left: v })}
                      onWidth={(w) => setOptions({ ...options, fileur_left_width: w })}
                    />
                    <FileurToggle testId="opt-fileur-right" label="Droite" checked={options.fileur_right} width={options.fileur_right_width}
                      onCheck={(v) => setOptions({ ...options, fileur_right: v })}
                      onWidth={(w) => setOptions({ ...options, fileur_right_width: w })}
                    />
                    <FileurToggle testId="opt-fileur-top" label="Haut" checked={options.fileur_top} width={options.fileur_top_width}
                      onCheck={(v) => setOptions({ ...options, fileur_top: v })}
                      onWidth={(w) => setOptions({ ...options, fileur_top_width: w })}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* 4. Sections */}
            {!isSdb && (
            <section className="paper p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl">3. Sections & éléments</h2>
                <Button
                  data-testid="legacy-add-section"
                  variant="outline"
                  size="sm"
                  onClick={addSection}
                  disabled={sections.length >= 20}
                >
                  <Plus className="w-3.5 h-3.5" /> Ajouter
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mb-4">
                Chaque section est indépendante : type, poignée, tablettes, penderie.
              </p>

              {/* Saisie numérique précise des largeurs (mm) — la somme reste = largeur totale */}
              {sections.length > 1 && (
                <div className="mb-5 p-4 rounded-lg bg-background border border-border" data-testid="section-widths-editor">
                  <div className="flex items-center justify-between mb-3">
                    <p className="eyebrow text-[10px]">Largeurs des sections (mm)</p>
                    <button
                      type="button"
                      data-testid="distribute-equal"
                      onClick={() => {
                        const n = sections.length;
                        const each = Math.round((width / n) * 10) / 10;
                        if (each < SECTION_MIN_CM) {
                          toast.error("Impossible de répartir", {
                            description: `Chaque section serait inférieure à ${SECTION_MIN_CM * 10} mm. Agrandissez la largeur totale ou supprimez des sections.`,
                          });
                          return;
                        }
                        const rest = Math.round((width - each * (n - 1)) * 10) / 10;
                        setSections((prev) => prev.map((s, i) => ({ ...s, width_cm: i === n - 1 ? rest : each })));
                      }}
                      className="text-[10px] px-2.5 py-1 rounded-full border border-border hover:border-primary hover:bg-primary/5 transition-colors inline-flex items-center gap-1"
                      title="Répartir la largeur totale équitablement entre toutes les sections"
                    >
                      ≡ Répartir également
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    {sections.map((s, i) => (
                      <SectionWidthInput
                        key={`sw-${s.id}`}
                        valueMm={Math.round((s.width_cm || 0) * 10)}
                        testId={`section-width-mm-${i}`}
                        label={String.fromCharCode(65 + i)}
                        onCommit={(mm) => setSectionWidthMm(i, mm)}
                        onDelete={sections.length > 1 ? () => removeSection(i) : null}
                      />
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">
                    Somme = {Math.round(sections.reduce((a, s) => a + (s.width_cm || 0) * 10, 0))} mm sur {width * 10} mm — modifier une section ajuste automatiquement la suivante.
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <AnimatePresence>
                  {sections.map((s, i) => (
                    <SectionEditor
                      key={s.id}
                      section={s}
                      index={i}
                      breakdown={breakdown}
                      handles={isSdb ? catalog.sdb_handles : catalog.handles}
                      fixedShelfRequired={fixedShelfRequired}
                      onChange={(next) => updateSection(i, next)}
                      onRemove={() => removeSection(i)}
                      onRemoveElement={handleRemoveElement}
                      onMoveElement={handleMoveElement}
                      onToggleTraversingDrawer={handleToggleTraversingDrawer}
                      canRemove={sections.length > 1}
                      canSpanDrawer={i < sections.length - 1}
                      heightCm={height}
                      sectionWidthCm={s.width_cm || 0}
                    />
                  ))}
                </AnimatePresence>
              </div>
            </section>
            )}

            {/* Couleur Caisson (ou "Couleur Côté" pour la Salle de Bain : pas de caisson fermé,
                seuls les côtés du meuble sont visibles — pas de portes, tiroirs + plan du dessus). */}
            <section className="paper p-6 border-l-4 border-l-primary">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg border-2 border-primary flex items-center justify-center shadow-sm" style={{ background: carcassFinish?.swatch || "#ccc" }}>
                    <span className="text-[9px] font-bold text-white mix-blend-difference">{isSdb ? "CÔT" : "CAI"}</span>
                  </div>
                  <div>
                    <p className="eyebrow text-[10px]">{isSdb ? "Étape 5 · Côtés visibles du meuble" : "Étape 5 · Structure complète (côtés, fond, intérieur)"}</p>
                    <h2 className="font-heading text-xl">{isSdb ? "5. Couleur Côté" : "5. Couleur Caisson"}</h2>
                  </div>
                </div>
                {/* Chantier 3.2 : le caisson peut reprendre automatiquement la couleur de la
                    façade dès qu'une façade différenciée réelle est choisie à l'étape 6. */}
                <label className={`text-xs flex items-center gap-2 ${hasDifferentiatedFacade ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`} title={hasDifferentiatedFacade ? "" : "Choisissez d'abord « Différencier » à l'étape 6 pour activer cette option"}>
                  <input
                    type="checkbox"
                    data-testid="toggle-carcass-matches-facade"
                    checked={carcassMatchesFacade}
                    disabled={!hasDifferentiatedFacade}
                    onChange={(e) => setCarcassMatchesFacade(e.target.checked)}
                    className="w-3.5 h-3.5 accent-primary"
                  />
                  Identique à la façade
                </label>
              </div>
              {carcassMatchesFacade && hasDifferentiatedFacade ? (
                <p className="text-xs text-muted-foreground" data-testid="carcass-matches-facade-hint">
                  Le caisson reprend automatiquement la couleur de la façade ({doorsFinish?.name}). Décochez pour choisir une couleur de caisson indépendante.
                </p>
              ) : (
                <ColorPicker
                  catUnie={finishesUnieFiltered}
                  catBois={finishesBoisFiltered}
                  value={carcassFinishId}
                  onChange={setCarcassFinishId}
                  prefix="caisson"
                />
              )}
            </section>

            {/* Couleur Portes */}
            <section className={`paper p-6 border-l-4 ${doorsFinishId && doorsFinishId !== "none" ? "border-l-accent" : "border-l-border"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center shadow-sm ${doorsFinishId && doorsFinishId !== "none" ? "border-accent" : "border-dashed border-border"}`}
                       style={{ background: doorsFinishId && doorsFinishId !== "none" ? (doorsFinish?.swatch || "#ccc") : "transparent" }}>
                    <span className="text-[9px] font-bold text-white mix-blend-difference">POR</span>
                  </div>
                  <div>
                    <p className="eyebrow text-[10px]">Étape 6 · Portes, tiroirs et façades</p>
                    <h2 className="font-heading text-xl">6. Couleur Façades</h2>
                  </div>
                </div>
                {/* Chantier 3.1 : 3 états clairs — "Sans façade" (choix par défaut, aucune porte
                    chiffrée), "Identique au caisson" (porte réelle, même couleur) et
                    "Différencier" (couleur distincte, +30/40%). */}
                {/* Chantier 3.1 : 3 états clairs — "Sans façade" (choix par défaut, aucune porte
                    chiffrée), "Identique au caisson" (porte réelle, même couleur) et
                    "Différencier" (couleur distincte, +30/40%). Non applicable en Salle de Bain :
                    la façade (face de tiroir) y est toujours une couleur concrète et indépendante. */}
                {!isSdb && (
                <div className="flex items-center gap-1 rounded-lg border border-border bg-muted/40 p-1" role="group" aria-label="Choix de façade">
                  <button
                    type="button"
                    data-testid="facade-mode-none"
                    onClick={() => setDoorsFinishId("none")}
                    className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${doorsFinishId === "none" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-background"}`}
                  >Sans façade</button>
                  <button
                    type="button"
                    data-testid="facade-mode-same"
                    onClick={() => setDoorsFinishId("")}
                    className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${doorsFinishId === "" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-background"}`}
                  >Identique au caisson</button>
                  <button
                    type="button"
                    data-testid="facade-mode-different"
                    onClick={() => setDoorsFinishId(doorsFinishId && doorsFinishId !== "none" ? doorsFinishId : "chene_davos")}
                    className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${doorsFinishId && doorsFinishId !== "none" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-background"}`}
                  >Différencier</button>
                </div>
                )}
              </div>
              {isSdb ? (
                <ColorPicker
                  catUnie={finishesUnieFiltered}
                  catBois={finishesBoisFiltered}
                  catSpecial={finishesSpecialFiltered}
                  value={doorsFinishId}
                  onChange={setDoorsFinishId}
                  prefix="portes"
                />
              ) : doorsFinishId === "none" ? (
                <p className="text-xs text-muted-foreground" data-testid="facade-none-hint">
                  Aucune façade n'est chiffrée pour l'instant — le meuble est quoté caisson nu (portes/tiroirs non inclus).
                  Choisissez « Identique au caisson » ou « Différencier » dès que la façade est décidée.
                </p>
              ) : doorsFinishId ? (
                <>
                  <p className="text-xs text-muted-foreground mb-3">
                    +30% du sous-total pour une finition façades différente du caisson (Miroir = +40%).
                    <br />
                    <b>Vous modifiez ci-dessous la couleur des façades (portes et tiroirs).</b>
                  </p>
                  <ColorPicker
                    catUnie={finishesUnieFiltered}
                    catBois={finishesBoisFiltered}
                    catSpecial={finishesSpecialFiltered}
                    value={doorsFinishId}
                    onChange={setDoorsFinishId}
                    prefix="portes"
                  />
                </>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Portes identiques au caisson ({carcassFinish?.name}). Cliquez « Différencier » pour choisir une autre finition.
                </p>
              )}
              {!isSdb && (
              <div className="mt-4 pt-4 border-t border-border" data-testid="custom-color-block">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    data-testid="custom-color-toggle"
                    checked={customColorRequested}
                    onChange={(e) => {
                      setCustomColorRequested(e.target.checked);
                      if (e.target.checked) setContactOpen(true);
                    }}
                    className="mt-0.5 w-4 h-4 rounded border-border accent-primary cursor-pointer"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      Couleur ou décor non présent ? <span className="text-accent">(+20% sur le meuble)</span>
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Cochez pour appliquer la plus-value « teinte hors nuancier » — un formulaire s'ouvre pour préciser votre demande.
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid="open-color-request"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setContactOpen(true); }}
                    className="text-xs text-primary hover:underline inline-flex items-center gap-1 shrink-0 mt-0.5"
                  >
                    <Mail className="w-3 h-3" /> Demander
                  </button>
                </label>
                {customColorRequested && breakdown?.custom_color_surcharge > 0 && (
                  <div className="mt-3 rounded-lg bg-accent/10 border border-accent/30 px-3 py-2" data-testid="custom-color-surcharge-line">
                    <span className="text-xs text-accent-foreground">Plus-value teinte hors nuancier (+20%)</span>
                  </div>
                )}
              </div>
              )}
            </section>

            {/* Multi-meubles : liste des meubles ajoutés au projet + bouton ajouter */}
            {/* Quantity + CTA */}
            <section className="paper p-6 border-l-4 border-l-accent">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl">7. Récapitulatif</h2>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Quantité</Label>
                  <button
                    data-testid="quantity-decr"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:bg-background"
                  ><Minus className="w-3 h-3" /></button>
                  <span data-testid="quantity-value" className="w-6 text-center tabular-nums">{quantity}</span>
                  <button
                    data-testid="quantity-incr"
                    onClick={() => setQuantity(Math.min(50, quantity + 1))}
                    className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:bg-background"
                  ><Plus className="w-3 h-3" /></button>
                </div>
              </div>

              {breakdown && <BreakdownRows breakdown={breakdown} />}

              {/* Vantaux coulissants à commander séparément (non fournis atelier) */}
              {breakdown?.sliding_doors_specs && (
                <div
                  data-testid="sliding-doors-specs"
                  className="mt-5 p-4 rounded-xl border-2 border-primary/40 bg-primary/5"
                >
                  <p className="font-heading text-sm mb-1 text-primary">
                    📐 Dimensions des portes coulissantes à commander <span className="text-[10px] font-normal opacity-70">(non fournies)</span>
                  </p>
                  <p className="text-[11px] text-muted-foreground mb-3 leading-snug">
                    Le meuble est prévu pour recevoir des vantaux coulissants sur rail — le pro les commande à part chez son fournisseur habituel (Sogal, Delinia, Kazed…).
                  </p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-background rounded-lg p-3 border border-border">
                      <p className="eyebrow text-[9px]">Vantaux</p>
                      <p className="font-heading text-2xl mt-1 tabular-nums" data-testid="sliding-count">{breakdown.sliding_doors_specs.count}</p>
                      <p className="text-[9px] text-muted-foreground">à recouvrement 20 mm</p>
                    </div>
                    <div className="bg-background rounded-lg p-3 border border-border">
                      <p className="eyebrow text-[9px]">Hauteur</p>
                      <p className="font-heading text-2xl mt-1 tabular-nums" data-testid="sliding-vantail-h">{breakdown.sliding_doors_specs.vantail_height_mm}</p>
                      <p className="text-[9px] text-muted-foreground">mm · passage rails 40 mm</p>
                    </div>
                    <div className="bg-background rounded-lg p-3 border border-border">
                      <p className="eyebrow text-[9px]">Largeur unitaire</p>
                      <p className="font-heading text-2xl mt-1 tabular-nums" data-testid="sliding-vantail-w">{breakdown.sliding_doors_specs.vantail_width_mm}</p>
                      <p className="text-[9px] text-muted-foreground">mm</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 pt-5 border-t border-border flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="eyebrow text-[10px]">Tarif professionnel</p>
                  <p data-testid="total-price-hidden" className="font-heading text-xl mt-1">Détail après création du devis</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Le prix détaillé est présenté sur le devis et son PDF.</p>
                </div>
                <div className="flex items-center gap-2">
                  {user && <Button data-testid="save-project-draft" variant="outline" onClick={saveProjectDraft} disabled={savingProject || !breakdown}>
                    {savingProject ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
                  </Button>}
                  <Button data-testid="open-quote-dialog" onClick={() => setModalOpen(true)} disabled={loading || !breakdown} className="btn-primary">
                    Créer le devis <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Rendu 3D masqué (hors-écran) : capturé en PNG au clic "Créer le devis" pour le
          visuel du PDF auto-généré à la création (cf. handleSubmit → generateQuotePDF).
          Même approche que QuoteDetail.jsx (domId="cabinet-3d-main"/"cabinet-3d-item-{idx}"). */}
      <div aria-hidden="true" style={{ position: "fixed", left: "-99999px", top: 0, width: "900px", height: "700px", opacity: 0, pointerEvents: "none" }}>
        <div id="cabinet-3d-main" style={{ width: "900px", height: "700px" }}>
          <CabinetVisualizer3D
            totalWidth={width}
            height={height}
            depth={depth + (options?.prepared_for_coulissant ? 10 : 0)}
            sections={sections}
            carcassColor={carcassFinish?.swatch || "#B8935A"}
            doorsColor={doorsFinish?.swatch || null}
            doorsFinishId={doorsFinishId && doorsFinishId !== "none" ? doorsFinishId : null}
            doorsDifferent={!!doorsFinishId && doorsFinishId !== "none" && doorsFinishId !== carcassFinishId}
            showDoors={showDoors}
            templateVariant={templateVariant}
            options={options}
            sdbDrawers={template?.sdb_drawers || 0}
            handleSvgType={selectedHandle?.svg_type || null}
            printMode
          />
        </div>
        {additionalItems.map((item, idx) => {
          const itemCarcass = catalog.finishes.find((finish) => finish.id === item.config.carcass_finish_id);
          const itemDoors = catalog.finishes.find((finish) => finish.id === (item.config.doors_finish_id || item.config.carcass_finish_id));
          const itemHandles = isSdb ? catalog.sdb_handles : catalog.handles;
          const itemHandle = itemHandles.find((h) => h.id === item.config.default_handle_id) || itemHandles.find((h) => h.id === "sdb_profil_alu");
          return (
            <div key={item.id} id={`cabinet-3d-item-${idx}`} style={{ width: "900px", height: "700px" }}>
              <CabinetVisualizer3D
                totalWidth={item.config.total_width}
                height={item.config.height}
                depth={item.config.depth + (item.config.options?.prepared_for_coulissant ? 10 : 0)}
                sections={item.config.sections}
                carcassColor={itemCarcass?.swatch || "#B8935A"}
                doorsColor={itemDoors?.swatch || null}
                doorsFinishId={item.config.doors_finish_id}
                doorsDifferent={!!item.config.doors_finish_id && item.config.doors_finish_id !== "none" && item.config.doors_finish_id !== item.config.carcass_finish_id}
                showDoors
                handleSvgType={itemHandle?.svg_type || null}
                options={item.config.options || null}
                templateVariant={templateVariantForConfig(item.config)}
                sdbDrawers={sdbDrawersForConfig(item.config)}
                printMode
              />
            </div>
          );
        })}
      </div>

      {/* Menu d'options d'ouverture de porte — ouvert via l'icône "..." sur chaque porte posée */}
      {doorMenu && (() => {
        const section = sections[doorMenu.sectionIdx];
        if (!section) return null;
        const forcedDouble = requiresDoubleDoor(section.width_cm);
        const isDouble = section.door_standard === "double" || forcedDouble;
        const menuLeft = Math.min(doorMenu.x, (typeof window !== "undefined" ? window.innerWidth : 1200) - 220);
        return (
          <>
            <div className="fixed inset-0 z-40" data-testid="door-menu-backdrop" onClick={() => setDoorMenu(null)} />
            <div data-testid="door-orientation-menu" className="fixed z-50 paper rounded-lg border border-border shadow-xl p-2 min-w-[210px]" style={{ left: menuLeft, top: doorMenu.y + 12 }}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-1 pb-1">Portes</p>
              {!forcedDouble && (
                <div className="flex gap-1 px-1 pb-1">
                  <button type="button" data-testid="door-menu-single-left" onClick={() => handleSetDoorOrientation(doorMenu.sectionIdx, { door_standard: "single_left" })} className={`flex-1 h-8 rounded text-xs border ${!isDouble && section.door_standard === "single_left" ? "border-primary bg-primary/10 font-semibold" : "border-border"}`}>Ouverture gauche</button>
                  <button type="button" data-testid="door-menu-single-right" onClick={() => handleSetDoorOrientation(doorMenu.sectionIdx, { door_standard: "single_right" })} className={`flex-1 h-8 rounded text-xs border ${!isDouble && section.door_standard === "single_right" ? "border-primary bg-primary/10 font-semibold" : "border-border"}`}>Ouverture droite</button>
                </div>
              )}
              <div className="flex gap-1 px-1 pb-1">
                <button type="button" data-testid="door-menu-double" disabled={forcedDouble} onClick={() => handleSetDoorOrientation(doorMenu.sectionIdx, { door_standard: "double" })} className={`flex-1 h-8 rounded text-xs border ${isDouble ? "border-primary bg-primary/10 font-semibold" : "border-border"} disabled:opacity-70`}>Porte double{forcedDouble ? " (> 600 mm)" : ""}</button>
              </div>
              {isDouble && (
                <>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-1 pt-1 pb-1 border-t border-border mt-1">Vantail maître</p>
                  <div className="flex gap-1 px-1">
                    <button type="button" data-testid="door-menu-master-left" onClick={() => handleSetDoorOrientation(doorMenu.sectionIdx, { door_standard: "double", door_double_master: "left" })} className={`flex-1 h-8 rounded text-xs border ${(section.door_double_master || "left") === "left" ? "border-primary bg-primary/10 font-semibold" : "border-border"}`}>Gauche</button>
                    <button type="button" data-testid="door-menu-master-right" onClick={() => handleSetDoorOrientation(doorMenu.sectionIdx, { door_standard: "double", door_double_master: "right" })} className={`flex-1 h-8 rounded text-xs border ${section.door_double_master === "right" ? "border-primary bg-primary/10 font-semibold" : "border-border"}`}>Droite</button>
                  </div>
                </>
              )}
            </div>
          </>
        );
      })()}

      {/* Pop-up obligatoire : choix du modèle de poignée à l'ajout d'une porte ou d'un tiroir */}
      <Dialog open={!!handlePopup} onOpenChange={(open) => !open && setHandlePopup(null)}>
        <DialogContent className="max-w-lg bg-card border-border" data-testid="handle-choice-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Choisissez le modèle de poignée</DialogTitle>
            <DialogDescription>
              Section {handlePopup ? String.fromCharCode(65 + handlePopup.sectionIdx) : ""} — ce modèle sera appliqué aux portes et tiroirs de cette section (supplément tarifaire éventuel inclus dans le devis).
            </DialogDescription>
          </DialogHeader>
          {handlePopup && (
            <HandlePicker
              value={handlePopup.choice}
              onChange={(val) => setHandlePopup({ ...handlePopup, choice: val })}
              handles={isSdb ? catalog.sdb_handles : catalog.handles}
            />
          )}
          {/* Option porte miroir */}
          {handlePopup && sections[handlePopup.sectionIdx]?.has_door && (
            <label className="flex items-center gap-2 pt-2 border-t border-border cursor-pointer" data-testid="door-mirror-option">
              <input
                type="checkbox"
                checked={!!handlePopup.doorMirror}
                onChange={(e) => setHandlePopup({ ...handlePopup, doorMirror: e.target.checked })}
                className="accent-primary w-4 h-4"
                data-testid="door-mirror-checkbox"
              />
              <span className="text-sm font-medium">Porte avec miroir</span>
              <span className="text-xs text-muted-foreground ml-auto">(supplément tarifaire inclus au devis)</span>
            </label>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              type="button"
              data-testid="handle-choice-skip-btn"
              className="text-xs h-9"
              onClick={() => {
                dismissedHandleSections.current.add(handlePopup.sectionIdx);
                updateSection(handlePopup.sectionIdx, { ...sections[handlePopup.sectionIdx], door_mirror: !!handlePopup.doorMirror });
                setHandlePopup(null);
              }}
            >
              Garder la poignée par défaut
            </Button>
            <Button
              type="button"
              data-testid="handle-choice-confirm-btn"
              className="btn-primary text-xs h-9"
              onClick={() => {
                updateSection(handlePopup.sectionIdx, { ...sections[handlePopup.sectionIdx], handle_id: handlePopup.choice, door_mirror: !!handlePopup.doorMirror });
                setHandlePopup(null);
                toast.success("Poignée appliquée à la section");
              }}
            >
              Valider cette poignée
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Modale de bienvenue (mini-tuto 3 étapes) — 1re visite du configurateur */}
      <Dialog open={welcomeOpen} onOpenChange={(open) => (open ? setWelcomeOpen(true) : dismissWelcome())}>
        <DialogContent className="max-w-lg bg-card border-border" data-testid="welcome-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <Hand className="w-6 h-6 text-primary" />
              Bienvenue sur votre configurateur
            </DialogTitle>
            <DialogDescription className="text-[13px]">
              Trois étapes suffisent pour composer un meuble sur-mesure et générer son devis HT.
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 my-2">
            <li className="flex gap-3 items-start p-3 rounded-lg bg-primary/5 border border-primary/20">
              <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-heading font-bold shrink-0">1</span>
              <p className="text-sm leading-snug pt-0.5">
                Définissez vos <b>dimensions globales</b> et vos <b>options de pose</b>.
              </p>
            </li>
            <li className="flex gap-3 items-start p-3 rounded-lg bg-primary/5 border border-primary/20">
              <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-heading font-bold shrink-0">2</span>
              <p className="text-sm leading-snug pt-0.5">
                Glissez-déposez vos aménagements (<b>tiroirs, penderies, étagères</b>) depuis la palette de gauche directement dans le meuble.
              </p>
            </li>
            <li className="flex gap-3 items-start p-3 rounded-lg bg-primary/5 border border-primary/20">
              <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-heading font-bold shrink-0">3</span>
              <p className="text-sm leading-snug pt-0.5">
                Si vous utilisez des portes, cliquez sur l&apos;icône{" "}
                <Eye className="inline w-4 h-4 -mt-0.5" /> pour les masquer et aménager l&apos;intérieur librement.
              </p>
            </li>
          </ol>
          <DialogFooter>
            <Button
              data-testid="welcome-cta"
              onClick={dismissWelcome}
              className="w-full sm:w-auto h-11 px-6 text-base font-heading"
            >
              C&apos;est parti !
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={assemblyDialogOpen} onOpenChange={setAssemblyDialogOpen}>
        <DialogContent className="max-w-lg bg-card border-border" data-testid="assembly-choice-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Assembler les caissons&nbsp;?</DialogTitle>
            <DialogDescription>{"Souhaitez-vous assembler ce nouveau meuble avec le(s) précédent(s) en un ensemble unique (avec plinthe continue) ?"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {assemblyChoices.map((item) => (
              <button
                key={item.id}
                type="button"
                data-testid={`assembly-candidate-${item.id}`}
                onClick={() => setAssemblyCandidateId(item.id)}
                className={`w-full text-left border px-3 py-3 rounded-lg transition-colors ${assemblyCandidateId === item.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
              >
                <span className="block text-sm font-medium">{item.label || "Meuble additionnel"}</span>
                <span className="block mt-1 text-xs text-muted-foreground">{item.config.total_width * 10} × {item.config.height * 10} × {item.config.depth * 10} mm</span>
              </button>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" data-testid="assembly-choice-no" onClick={() => setAssemblyDialogOpen(false)}>Non, garder les caissons séparés</Button>
            <Button data-testid="assembly-choice-yes" className="btn-primary" disabled={!assemblyCandidateId} onClick={() => {
              setLinkedAdditionalItemId(assemblyCandidateId);
              setAssemblyDialogOpen(false);
              toast.success("Caissons jumelés", { description: "Le devis inclura les vues assemblée et atelier." });
            }}>
              <Link2 className="w-4 h-4" /> Oui, assembler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client / Project dialog */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg bg-card border-border" data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {user ? "Nom du projet" : "Coordonnées client"}
            </DialogTitle>
            <DialogDescription>
              {user
                ? <>Le devis sera référencé <b className="text-foreground">[{(user.company || user.contact_name || "").toUpperCase()}] - [PROJET]</b>. Le détail commercial apparaît après sa création{additionalItems.length > 0 ? <> ({totalMeublesCount} meuble{totalMeublesCount > 1 ? "s" : ""})</> : null}.</>
                : <>Nous générons un PDF référencé. Le détail commercial apparaît après sa création.</>
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom du projet {user ? "*" : ""}</Label>
              <Input
                data-testid="project-name"
                placeholder="Ex : Dressing M. Durand, SDB Villa Cannes…"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                autoFocus
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Ce nom apparaîtra dans la référence du devis et dans les emails.
              </p>
            </div>
            {!user && (
              <>
                <div>
                  <Label>Société</Label>
                  <Input data-testid="client-company" value={client.company} onChange={(e) => setClient({ ...client, company: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Contact *</Label>
                    <Input data-testid="client-name" value={client.contact_name} onChange={(e) => setClient({ ...client, contact_name: e.target.value })} />
                  </div>
                  <div>
                    <Label>Téléphone</Label>
                    <Input data-testid="client-phone" value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input data-testid="client-email" type="email" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea data-testid="client-notes" rows={3} value={client.notes} onChange={(e) => setClient({ ...client, notes: e.target.value })} />
                </div>
              </>
            )}
            {user && (
              <div>
                <Label>Notes (optionnel)</Label>
                <Textarea
                  data-testid="client-notes"
                  rows={2}
                  placeholder="Détails techniques, contraintes de pose, échéance…"
                  value={client.notes}
                  onChange={(e) => setClient({ ...client, notes: e.target.value })}
                />
              </div>
            )}
            {user && (
              (user.installation_price_ht || 0) > 0 ? (
                <label className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-secondary/20 cursor-pointer">
                  <span className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      data-testid="apply-installation-fee-checkbox"
                      checked={applyInstallationFee}
                      onChange={(e) => setApplyInstallationFee(e.target.checked)}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className="text-sm">Ajouter mon forfait de pose</span>
                  </span>
                  <span data-testid="installation-price-hidden" className="text-xs font-bold text-primary">+ {formatEuro(user.installation_price_ht)} HT</span>
                </label>
              ) : (
                <div data-testid="installation-fee-not-configured" className="flex items-center justify-between gap-3 p-3 rounded-lg border border-dashed border-border bg-secondary/10">
                  <span className="text-sm text-muted-foreground">Forfait de pose non configuré</span>
                  <Link to="/mon-compte" className="text-xs font-medium text-primary underline underline-offset-2">
                    Régler mon tarif
                  </Link>
                </div>
              )
            )}
            <div className="space-y-2 p-3 rounded-lg border border-border bg-secondary/20">
              <Label htmlFor="delivery-postal-code" className="text-sm">Livraison — code postal du client</Label>
              <Input
                id="delivery-postal-code"
                data-testid="delivery-postal-code-input"
                placeholder="Ex : 59300"
                inputMode="numeric"
                maxLength={5}
                value={deliveryPostalCode}
                onChange={(e) => setDeliveryPostalCode(e.target.value.replace(/[^0-9]/g, ""))}
                className="h-9"
              />
              {deliveryChecking && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="delivery-checking">
                  <Loader2 className="w-3 h-3 animate-spin" /> Calcul de la distance…
                </p>
              )}
              {!deliveryChecking && deliveryEstimate && deliveryEstimate.tier === "unavailable" && (
                <p className="text-xs text-destructive" data-testid="delivery-unavailable">
                  Livraison non disponible à cette distance (&gt; 100 km de l'atelier).
                </p>
              )}
              {!deliveryChecking && deliveryEstimate && (deliveryEstimate.tier === "50km" || deliveryEstimate.tier === "100km") && (
                <label className="flex items-center justify-between gap-3 cursor-pointer" data-testid="delivery-available">
                  <span className="flex items-center gap-2 text-xs text-muted-foreground">
                    <input
                      type="checkbox"
                      data-testid="apply-delivery-checkbox"
                      checked={applyDelivery}
                      onChange={(e) => setApplyDelivery(e.target.checked)}
                      className="w-4 h-4 accent-primary"
                    />
                    Ajouter la livraison ({deliveryEstimate.city}, {deliveryEstimate.distance_km} km)
                  </span>
                  <span data-testid="delivery-price-hidden" className="text-xs font-medium">Détaillé sur devis</span>
                </label>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} data-testid="cancel-quote">Annuler</Button>
            <Button className="btn-primary" onClick={submitQuote} disabled={submitting} data-testid="submit-quote">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Créer le devis
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Color request dialog */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="max-w-md bg-card border-border" data-testid="color-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Demander une couleur <span className="text-accent text-lg">(+20%)</span></DialogTitle>
            <DialogDescription>
              Placage spécial, laque RAL, décor précis ? Décrivez votre besoin — notre atelier revient sous 48 h. La plus-value de <b className="text-accent">+20% sur le total HT du meuble</b> est automatiquement appliquée dès validation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Couleur / référence *</Label>
              <Input
                data-testid="color-name"
                placeholder="Ex : RAL 7016, Chêne fumé, Fenix Nero..."
                value={colorForm.name}
                onChange={(e) => setColorForm({ ...colorForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Type de finition</Label>
              <Input
                data-testid="color-finish-type"
                placeholder="Laque mate, mélaminé, placage bois..."
                value={colorForm.finish_type}
                onChange={(e) => setColorForm({ ...colorForm, finish_type: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Votre nom</Label>
                <Input
                  data-testid="color-contact-name"
                  value={colorForm.contact_name}
                  onChange={(e) => setColorForm({ ...colorForm, contact_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  data-testid="color-contact-email"
                  type="email"
                  value={colorForm.contact_email}
                  onChange={(e) => setColorForm({ ...colorForm, contact_email: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Précisions</Label>
              <Textarea
                data-testid="color-notes"
                rows={3}
                placeholder="Contexte projet, quantité, échéance..."
                value={colorForm.notes}
                onChange={(e) => setColorForm({ ...colorForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactOpen(false)} data-testid="cancel-color-request">Annuler</Button>
            <Button
              className="btn-primary"
              data-testid="submit-color-request"
              disabled={colorSending}
              onClick={async () => {
                if (!colorForm.name.trim() || !colorForm.contact_email.trim()) {
                  toast.error("Merci d'indiquer la couleur et un email");
                  return;
                }
                setColorSending(true);
                try {
                  await createColorRequest(colorForm);
                  toast.success("Demande envoyée à l'atelier", { description: "La plus-value de +20% reste appliquée au devis tant que la case est cochée." });
                  setCustomColorRequested(true);
                  setColorForm({ name: "", finish_type: "", contact_name: "", contact_email: "", notes: "" });
                  setContactOpen(false);
                } catch (e) {
                  toast.error("Envoi impossible", { description: formatApiError(e) });
                } finally {
                  setColorSending(false);
                }
              }}
            >
              {colorSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              Envoyer la demande
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

/** Row de dimension avec boutons -10/-1/+1/+10 (pas de saisie clavier — Lot 16). */
/**
 * Ligne dimension verrouillée pour SdB : affiche la valeur en lecture seule,
 * avec pastille de verrouillage. Cohérent visuellement avec SliderRow non-locked.
 */
function LockedDimRow({ label, value, testId }) {
  const mm = Math.round(value * 10);
  return (
    <div data-testid={testId} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-secondary/30 px-4 py-3">
      <div className="flex items-center gap-2 min-w-0">
        <Lock className="w-3.5 h-3.5 text-primary shrink-0" />
        <Label className="text-sm truncate">{label}</Label>
      </div>
      <div className="text-right">
        <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Fixé</p>
        <p className="font-heading text-lg tabular-nums leading-none">{mm} <span className="text-sm text-muted-foreground">mm</span></p>
      </div>
    </div>
  );
}

/** Accélération au maintien sur boutons +/- :
 *  0-300ms → 1 step, puis de plus en plus rapide (intervalle passe de 200ms → 40ms). */
function useHoldRepeat(callback) {
  const cbRef = useRef(callback);
  cbRef.current = callback;
  const timer = useRef(null);
  const start = (step) => {
    cbRef.current(step);
    let interval = 200;
    const tick = () => {
      cbRef.current(step);
      interval = Math.max(40, interval - 18);
      timer.current = setTimeout(tick, interval);
    };
    timer.current = setTimeout(tick, 350);
  };
  const stop = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  useEffect(() => () => stop(), []);
  return { start, stop };
}


function DimensionControl({ label, valueCm, minMm, maxMm, onChangeCm, testId }) {
  const valueMm = Math.round(valueCm * 10);
  const [draft, setDraft] = useState(String(valueMm));
  useEffect(() => { setDraft(String(valueMm)); }, [valueMm]);
  const commit = (rawMm) => {
    const numeric = Number(rawMm);
    if (!Number.isFinite(numeric)) { setDraft(String(valueMm)); return; }
    const clamped = Math.max(minMm, Math.min(maxMm, Math.round(numeric)));
    onChangeCm(clamped / 10);
    setDraft(String(clamped));
  };
  const hold = useHoldRepeat((step) => commit(valueMm + step));
  return (
    <div data-testid={`${testId}-dimension-control`} className="rounded-lg border border-border bg-background p-3">
      <div className="flex items-center justify-between gap-3 mb-2"><Label htmlFor={`${testId}-input`} className="text-sm">{label}</Label><span className="text-[10px] text-muted-foreground">{minMm}–{maxMm} mm</span></div>
      <div className="flex items-center gap-2">
        <button type="button" data-testid={`${testId}-minus-5`} onPointerDown={() => hold.start(-5)} onPointerUp={hold.stop} onPointerLeave={hold.stop} disabled={valueMm <= minMm} className="h-10 min-w-12 rounded-md border border-border bg-card text-xs font-bold hover:border-primary disabled:opacity-40 select-none">−5</button>
        <Input
          id={`${testId}-input`}
          data-testid={`${testId}-input`}
          type="number"
          inputMode="numeric"
          min={minMm}
          max={maxMm}
          step="1"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => commit(draft)}
          onKeyDown={(event) => { if (event.key === "Enter") { commit(draft); event.target.blur(); } }}
          className="h-10 text-center font-heading tabular-nums"
          aria-label={`${label} en millimètres`}
        />
        <span className="text-xs font-medium text-muted-foreground">mm</span>
        <button type="button" data-testid={`${testId}-plus-5`} onPointerDown={() => hold.start(5)} onPointerUp={hold.stop} onPointerLeave={hold.stop} disabled={valueMm >= maxMm} className="h-10 min-w-12 rounded-md border border-border bg-card text-xs font-bold hover:border-primary disabled:opacity-40 select-none">+5</button>
      </div>
    </div>
  );
}

function SchemaDimensionInput({ label, valueCm, minMm, maxMm, onChangeCm, testId, disabled = false, note = null }) {
  const mm = Math.round(valueCm * 10);
  const [draft, setDraft] = useState(String(mm));
  const [error, setError] = useState(null);
  useEffect(() => { setDraft(String(mm)); }, [mm]);
  useEffect(() => { if (!error) return; const t = setTimeout(() => setError(null), 2500); return () => clearTimeout(t); }, [error]);
  const commit = (raw) => {
    if (disabled) return;
    const next = Number(raw);
    if (!Number.isFinite(next)) { setDraft(String(mm)); return; }
    const clamped = Math.max(minMm, Math.min(maxMm, Math.round(next)));
    if (next < minMm) setError(`Minimum ${minMm} mm`);
    else if (next > maxMm) setError(`Maximum ${maxMm} mm`);
    else setError(null);
    onChangeCm(clamped / 10);
    setDraft(String(clamped));
  };
  const holdSchema = useHoldRepeat((step) => commit(mm + step));
  return <div data-testid={`${testId}-control`} className={`w-full h-[72px] rounded-md border px-2 py-1.5 transition-colors flex flex-col justify-between ${disabled ? "opacity-60 border-border bg-background" : error ? "border-red-500 bg-red-50" : "border-border bg-background"}`}><span className="block text-[10px] font-bold text-muted-foreground truncate">{label} ({note || "mm"}){disabled && " · fixe"}</span><div className="flex items-center gap-1"><button type="button" data-testid={`${testId}-minus-5`} disabled={disabled} onPointerDown={() => holdSchema.start(-5)} onPointerUp={holdSchema.stop} onPointerLeave={holdSchema.stop} className="h-7 w-7 flex-shrink-0 rounded border border-border text-xs hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed select-none">−</button><Input data-testid={`${testId}-input`} type="number" inputMode="numeric" disabled={disabled} value={draft} min={minMm} max={maxMm} onChange={(event) => setDraft(event.target.value)} onBlur={() => commit(draft)} onKeyDown={(event) => { if (event.key === "Enter") { commit(draft); event.target.blur(); } }} className={`h-7 w-full min-w-0 px-1 text-center text-sm font-semibold disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${error ? "border-red-500 focus-visible:ring-red-500" : ""}`} /><button type="button" data-testid={`${testId}-plus-5`} disabled={disabled} onPointerDown={() => holdSchema.start(5)} onPointerUp={holdSchema.stop} onPointerLeave={holdSchema.stop} className="h-7 w-7 flex-shrink-0 rounded border border-border text-xs hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed select-none">+</button></div>{error && <span data-testid={`${testId}-error`} role="alert" className="block text-[9px] font-bold text-red-600 leading-tight truncate">{error}</span>}</div>;
}

function ElementToolButton({ icon: Icon, label, testId, active, onClick }) {
  return <button type="button" data-testid={testId} onClick={onClick} className={`h-12 min-w-14 px-2 rounded-md border flex flex-col items-center justify-center gap-0.5 transition-colors ${active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:border-primary/60"}`}><Icon className="w-4 h-4" /><span className="text-[9px] font-medium whitespace-nowrap">{label}</span></button>;
}

function FacadeConfigurator({ sections, hasDrawerCut, onModeChange, onPartChange, onRemoveDoor }) {
  return <section data-testid="facade-configurator" className="paper order-1 p-3 sm:p-4">
    <div className="mb-3"><p className="eyebrow text-[10px]">Façades</p><h2 className="font-heading text-lg">Portes par section</h2></div>
    <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.max(1, sections.length)}, minmax(0, 1fr))` }}>
      {sections.map((section, idx) => {
        const cut = hasDrawerCut(idx);
        const mode = section.door_mode || (section.has_door ? (section.door_standard === "double" ? "double" : "single") : "none");
        const part = (key) => section.door_parts?.[key] || { enabled: true, standard: section.door_standard || "single_right" };
        return <div key={section.id || idx} data-testid={`facade-selector-${idx}`} className="min-w-0 border border-border bg-background p-3 rounded-md">
          <p className="text-xs font-bold">Section {String.fromCharCode(65 + idx)}</p>
          <select data-testid={`facade-mode-${idx}`} value={mode} onChange={(event) => onModeChange(idx, event.target.value)} className="mt-2 h-9 w-full rounded border border-border bg-card px-2 text-xs">
            <option value="none">Sans porte</option><option value="single">1 porte battante</option><option value="double">2 portes (double porte)</option>
          </select>
          {mode !== "none" && !cut && <div className="mt-2 flex gap-1"><button data-testid={`facade-open-left-${idx}`} onClick={() => onPartChange(idx, "full", { standard: "single_left" })} className="flex-1 rounded border border-border py-1 text-[10px]">Gauche</button><button data-testid={`facade-open-right-${idx}`} onClick={() => onPartChange(idx, "full", { standard: mode === "double" ? "double" : "single_right" })} className="flex-1 rounded border border-border py-1 text-[10px]">Droite</button></div>}
          {mode !== "none" && cut && <div className="mt-3 space-y-2"><p className="text-[10px] text-muted-foreground">Façades séparées par le tiroir</p>{["top", "bottom"].map((key) => <div key={key} className="flex items-center gap-1"><button data-testid={`facade-part-${key}-${idx}`} onClick={() => onPartChange(idx, key, { enabled: !part(key).enabled })} className={`flex-1 rounded border py-1 text-[10px] ${part(key).enabled ? "border-primary bg-primary/10" : "border-border"}`}>{key === "top" ? "Porte haute" : "Porte basse"}</button><button aria-label={`Sens ${key}`} data-testid={`facade-part-direction-${key}-${idx}`} onClick={() => onPartChange(idx, key, { standard: part(key).standard === "single_left" ? (mode === "double" ? "double" : "single_right") : "single_left" })} className="h-7 w-7 rounded border border-border text-xs">↔</button></div>)}</div>}
          {mode !== "none" && <button data-testid={`facade-remove-${idx}`} onClick={() => onRemoveDoor(idx)} className="mt-2 text-[10px] text-destructive hover:underline">Retirer toutes les portes</button>}
        </div>;
      })}
    </div>
  </section>;
}

function ElementMmInput({ label, value, min, max, onChange, testId }) {
  const [draft, setDraft] = useState(String(Math.round(value)));
  useEffect(() => { setDraft(String(Math.round(value))); }, [value]);
  const commit = (raw) => {
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) { setDraft(String(Math.round(value))); return; }
    const clamped = Math.max(min, Math.min(max, Math.round(numeric)));
    onChange(clamped);
    setDraft(String(clamped));
  };
  return <label className="text-[10px] text-muted-foreground">{label}<input data-testid={testId} type="number" min={min} max={max} step="1" value={draft} onChange={(event) => setDraft(event.target.value)} onBlur={() => commit(draft)} onKeyDown={(event) => { if (event.key === "Enter") { commit(draft); event.target.blur(); } }} className="mt-1 block h-8 w-20 rounded border border-border bg-background px-2 text-xs text-foreground" /><span className="ml-1">mm</span></label>;
}

function SliderRow({ label, value, min, max, onChange, testId }) {  const clamp = (v) => Math.min(max, Math.max(min, v));
  const bump = (delta) => onChange(clamp(value + delta));
  const mm = Math.round(value * 10);
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <Label className="text-sm">{label}</Label>
        <div className="flex items-center gap-1.5" data-testid={`${testId}-controls`}>
          <button type="button" data-testid={`${testId}-minus-10`} onClick={() => bump(-10)} disabled={value <= min}
            className="w-8 h-8 rounded-lg border border-border bg-background hover:border-primary hover:bg-primary/5 disabled:opacity-40 text-[10px] font-bold transition">−100</button>
          <button type="button" data-testid={`${testId}-minus-1`} onClick={() => bump(-1)} disabled={value <= min}
            className="w-8 h-8 rounded-lg border border-border bg-background hover:border-primary hover:bg-primary/5 disabled:opacity-40 text-sm font-bold transition">−10</button>
          <div data-testid={`${testId}-value`} className="min-w-[54px] h-8 flex items-center justify-center rounded-lg bg-primary/10 border border-primary/30 text-sm font-heading tabular-nums">
            {mm}
          </div>
          <button type="button" data-testid={`${testId}-plus-1`} onClick={() => bump(1)} disabled={value >= max}
            className="w-8 h-8 rounded-lg border border-border bg-background hover:border-primary hover:bg-primary/5 disabled:opacity-40 text-sm font-bold transition">+10</button>
          <button type="button" data-testid={`${testId}-plus-10`} onClick={() => bump(10)} disabled={value >= max}
            className="w-8 h-8 rounded-lg border border-border bg-background hover:border-primary hover:bg-primary/5 disabled:opacity-40 text-[10px] font-bold transition">+100</button>
          <span className="text-xs text-muted-foreground ml-1">mm</span>
        </div>
      </div>
      <Slider
        data-testid={`${testId}-slider`}
        value={[value]}
        min={min}
        max={max}
        step={1}
        onValueChange={(v) => onChange(v[0])}
        className="slider-accent"
      />
      <div className="flex justify-between text-[10px] text-muted-foreground mt-1 uppercase tracking-wider">
        <span>{Math.round(min * 10)} mm</span>
        <span>{Math.round(max * 10)} mm</span>
      </div>
    </div>
  );
}

function ColorPicker({ catUnie, catBois, catSpecial, value, onChange, prefix }) {
  const groups = [
    { title: "Couleurs unies", items: catUnie },
    { title: "Textures bois", items: catBois },
  ];
  if (catSpecial && catSpecial.length > 0) {
    groups.push({ title: "Spécial", items: catSpecial });
  }

  return (
    <div className="space-y-3">
      {groups.map((group) => (
        <div key={group.title}>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">{group.title}</p>
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
            {group.items.map((f) => {
              const active = value === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  data-testid={`${prefix}-color-${f.id}`}
                  onClick={() => onChange(f.id)}
                  className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-all ${
                    active ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/40 bg-background"
                  }`}
                  title={f.name}
                >
                  <span className="w-7 h-7 rounded-full border border-border" style={{ background: f.swatch }} />
                  <span className="text-[9px] leading-tight text-center line-clamp-2">{f.name.split(" ").slice(-1)[0]}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function BreakdownRows({ breakdown }) {
  return (
    <div className="space-y-1.5 text-sm">
      {/* Détails techniques par section — descriptif seul, aucun prix (règle B2B / marque blanche) */}
      {breakdown.sections.map((sb) => {
        const parts = [];
        if (sb.has_door) parts.push("porte");
        if (sb.drawers > 0) parts.push(`${sb.drawers}× tiroir`);
        if (sb.has_penderie_bar) parts.push("penderie");
        if (parts.length === 0) parts.push("ouvert");
        return (
          <div key={sb.id} className="flex justify-between py-1 border-b border-border/60 text-muted-foreground">
            <span>Section {sb.id} — {parts.join(" · ")}</span>
          </div>
        );
      })}
      {breakdown.custom_color_surcharge > 0 && <p className="py-1 text-xs text-accent">Couleur/décor hors nuancier (+20%) · chiffré sur devis</p>}
      {breakdown.under_slope_surcharge > 0 && <p data-testid="basket-under-slope-surcharge" className="py-1 text-xs text-primary">Majoration sous-pente (+20%) · chiffrée sur devis</p>}
    </div>
  );
}

function SlopeMetricControl({ label, value, min, max, testId, onChange }) {
  const safeValue = Math.max(min, Math.min(max, value || min));
  const update = (delta) => onChange(Math.max(min, Math.min(max, safeValue + delta)));
  return (
    <div data-testid={`${testId}-control`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <Label className="text-xs">{label}</Label>
        <div data-testid={`${testId}-value`} className="font-heading text-sm tabular-nums text-primary">{safeValue} mm</div>
      </div>
      <div className="flex items-center gap-2">
        <button type="button" data-testid={`${testId}-minus`} onClick={() => update(-10)} disabled={safeValue <= min} className="w-8 h-8 rounded-md border border-border bg-background hover:border-primary disabled:opacity-40 transition-colors">−</button>
        <Slider data-testid={`${testId}-slider`} value={[safeValue]} min={min} max={max} step={10} onValueChange={(next) => onChange(next[0])} className="flex-1 slider-accent" />
        <button type="button" data-testid={`${testId}-plus`} onClick={() => update(10)} disabled={safeValue >= max} className="w-8 h-8 rounded-md border border-border bg-background hover:border-primary disabled:opacity-40 transition-colors">+</button>
      </div>
      <div className="flex justify-between mt-1 text-[10px] text-muted-foreground"><span>{min} mm</span><span>{max} mm</span></div>
    </div>
  );
}

// (Duplicate legacy BreakdownRows fragment removed — Lot 15 clean-up)

function PlaceModeBtn({ mode, active, onSet, label, testId, vertical = false }) {
  const isActive = active === mode;
  const base = vertical
    ? "h-12 min-w-14 px-2 flex flex-col items-center justify-center gap-0.5 rounded-md border transition-all text-center"
    : "text-xs px-3 py-1.5 rounded-full font-medium border transition-all inline-flex items-center gap-2";
  return (
    <button
      type="button"
      data-testid={testId}
      draggable
      onDragStart={(event) => { event.dataTransfer.setData("application/x-atelier-element", mode); onSet(mode); }}
      onClick={() => onSet(isActive ? null : mode)}
      className={`${base} ${
        isActive
          ? "bg-primary text-primary-foreground border-primary shadow-sm"
          : "bg-background text-foreground border-border hover:border-primary/60 hover:bg-primary/5"
      }`}
      title={mode === "door" ? "Glisser ou cliquer sur une section pour poser une façade entière" : `Cliquer puis déposer sur une section — ${label}`}
    >
      <MiniIcon kind={mode} size={vertical ? 22 : 18} color={isActive ? "#FFF" : "#0465C1"} />
      <span className={vertical ? "text-[9px] leading-tight font-medium" : ""}>{label}</span>
    </button>
  );
}

/** Carte de bienvenue avec accès rapide aux derniers devis (pour pros connectés). */
function WelcomeCard({ user, quotes }) {
  const firstName = (user.contact_name || user.email || "").split(" ")[0] || "Pro";
  const shortName = firstName.charAt(0).toUpperCase() + firstName.slice(1);
  return (
    <div
      data-testid="welcome-card"
      className="mb-6 paper p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 border-l-4 border-l-primary"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center font-heading text-lg shrink-0">
          {shortName.slice(0, 2)}
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Bienvenue</p>
          <p className="font-heading text-lg leading-tight truncate">{shortName}</p>
          <p className="text-xs text-muted-foreground truncate">
            {user.company || user.email}
          </p>
        </div>
      </div>
      {quotes.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
          {quotes.map((q) => (
            <Link
              key={q.id}
              to={`/devis/${q.id}`}
              data-testid={`welcome-recent-${q.id}`}
              className="text-[10px] px-2.5 py-1.5 rounded-full bg-background border border-border hover:border-primary/60 transition-colors truncate max-w-[180px]"
              title={q.reference}
            >
              {q.reference}
            </Link>
          ))}
        </div>
      )}
      <Link
        to="/devis"
        data-testid="welcome-my-quotes"
        className="text-xs px-3.5 py-2 rounded-full bg-primary text-primary-foreground inline-flex items-center gap-1.5 hover:opacity-90 shrink-0"
      >
        Mes devis <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

/** Largeur de section en mm — boutons +/− uniquement (pas de saisie clavier — Lot 16). */
function SectionWidthInput({ valueMm, onCommit, testId, label, onDelete = null }) {
  // Le bump utilise valueMm depuis la prop mais reste OK car il est appelé
  // synchrone au clic (React re-render entre chaque clic). Si les clics sont
  // rapides, la prop est mise à jour à chaque render — cf. commentaire iter_23.
  const bump = (delta) => onCommit(Math.max(200, valueMm + delta));
  const btnBase =
    "h-9 min-w-[38px] px-2 rounded-md border border-border bg-background hover:border-primary hover:bg-primary/5 active:scale-95 transition font-bold tabular-nums leading-none flex items-center justify-center";
  return (
    <div
      className="rounded-lg border border-border bg-background p-3 flex flex-col gap-2.5"
      data-testid={`${testId}-card`}
    >
      {/* Ligne 1 : Titre "Section X" à gauche + poubelle à droite */}
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-heading text-sm shrink-0">
          {label}
        </div>
        <p className="font-heading text-sm flex-1">Section {label}</p>
        {onDelete && (
          <button
            type="button"
            data-testid={`${testId}-delete`}
            onClick={onDelete}
            className="w-8 h-8 rounded-md border border-border text-muted-foreground hover:border-destructive hover:text-destructive hover:bg-destructive/5 active:scale-95 transition flex items-center justify-center shrink-0"
            title={`Supprimer la Section ${label}`}
            aria-label={`Supprimer la Section ${label}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      {/* Ligne 2 : contrôles -10 | -1 | valeur | +1 | +10 avec vrais gaps */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid={`${testId}-minus-10`}
          onClick={() => bump(-10)}
          className={`${btnBase} text-[11px]`}
        >
          −10
        </button>
        <button
          type="button"
          data-testid={`${testId}-minus-1`}
          onClick={() => bump(-1)}
          className={`${btnBase} text-lg`}
        >
          −
        </button>
        <div
          data-testid={testId}
          className="flex-1 min-w-[70px] h-9 flex items-center justify-center rounded-md bg-primary/10 border border-primary/30 text-sm font-heading tabular-nums"
        >
          {valueMm}
          <span className="text-[10px] text-muted-foreground ml-1">mm</span>
        </div>
        <button
          type="button"
          data-testid={`${testId}-plus-1`}
          onClick={() => bump(1)}
          className={`${btnBase} text-lg`}
        >
          +
        </button>
        <button
          type="button"
          data-testid={`${testId}-plus-10`}
          onClick={() => bump(10)}
          className={`${btnBase} text-[11px]`}
        >
          +10
        </button>
      </div>
    </div>
  );
}

/** Options ToggleRow (fond, plinthe) */
function ToggleRow({ testId, label, checked, onChange, hint }) {
  return (
    <label className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-background hover:border-primary/50 cursor-pointer transition-colors">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        {hint && <p className="text-[10px] text-muted-foreground truncate">{hint}</p>}
      </div>
      <input
        type="checkbox"
        data-testid={testId}
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-primary shrink-0"
      />
    </label>
  );
}

/** Fileur toggle avec largeur mm — verrouillage clavier : uniquement boutons +/- */
/** Saisie directe d'un fileur en mm (stockage interne en cm, 150 mm max). */
function FileurMmField({ testId, value, onChange }) {
  const mm = Math.round((Number(value) || 0) * 10);
  const [draft, setDraft] = useState(String(mm));
  useEffect(() => { setDraft(String(mm)); }, [mm]);
  const commit = () => {
    const numeric = Number(draft);
    const clamped = Number.isFinite(numeric) ? Math.max(10, Math.min(150, Math.round(numeric))) : mm;
    setDraft(String(clamped));
    if (clamped !== mm) onChange(clamped / 10);
  };
  return <input data-testid={testId} type="number" inputMode="numeric" min="10" max="150" step="1" value={draft} onClick={(e) => e.stopPropagation()} onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") { commit(); e.target.blur(); } }} className="h-6 w-14 rounded border border-border bg-card px-1 text-center text-[10px] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />;
}

/** Saisie directe d'une dimension du miroir en cm (bornée à la section/au caisson),
 * sans clamp à chaque frappe — l'utilisateur peut taper librement, le clamp n'intervient
 * qu'à la validation (blur / Entrée). */
function MirrorSizeField({ testId, valueMm, minCm = 5, maxCm, onCommit }) {
  const cm = Math.round((valueMm || 0) / 10);
  const [draft, setDraft] = useState(String(cm));
  useEffect(() => { setDraft(String(cm)); }, [cm]);
  const commit = () => {
    const numeric = Number(draft);
    const clamped = Number.isFinite(numeric) && numeric > 0 ? Math.max(minCm, Math.min(maxCm, Math.round(numeric))) : cm;
    setDraft(String(clamped));
    if (clamped !== cm) onCommit(clamped * 10);
  };
  return (
    <input
      type="number"
      data-testid={testId}
      min={minCm}
      max={maxCm}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { commit(); e.target.blur(); } }}
      className="mt-0.5 w-full h-6 rounded border border-border bg-card px-1.5 text-[11px]"
    />
  );
}

function HandlePhoto({ image, alt, size = "sm" }) {
  const dims = size === "lg" ? "w-14 h-11" : "w-9 h-7";
  if (!image) return <span className={`${dims} rounded-[3px] border border-border bg-muted/40 shrink-0`} />;
  return (
    <img
      src={image}
      alt={alt}
      className={`${dims} rounded-[3px] border border-border object-cover shrink-0 bg-[#F2EEE7]`}
      loading="lazy"
    />
  );
}

// Prévisualisation agrandie de la poignée actuellement sélectionnée — affichée sous le
// sélecteur compact pour que l'artisan puisse juger du rendu/matière sans dépendre de
// la petite vignette du menu déroulant (demande utilisateur explicite).
function HandlePreviewCard({ handle }) {
  if (!handle) return null;
  return (
    <div data-testid="handle-preview-card" className="flex items-center gap-3 rounded-lg border border-border bg-secondary/10 p-2 pr-3">
      {handle.image ? (
        <img
          data-testid="handle-preview-image"
          src={handle.image}
          alt={handle.name}
          className="w-20 h-16 rounded-md border border-border object-cover shrink-0 bg-[#F2EEE7]"
        />
      ) : (
        <span className="w-20 h-16 rounded-md border border-dashed border-border bg-muted/40 shrink-0" />
      )}
      <div className="min-w-0 text-xs leading-snug">
        <p className="font-heading text-sm truncate">{handle.name}</p>
        <p className="text-muted-foreground">{handle.finish}{handle.dimensions ? ` · ${handle.dimensions}` : ""}</p>
        <p className="font-semibold text-foreground mt-0.5" data-testid="handle-preview-price">
          {handle.price > 0 ? `+${handle.price.toFixed(2)} € HT / unité` : "Inclus"}
          {handle.reference ? <span className="text-muted-foreground font-normal"> · Réf. {handle.reference}</span> : null}
        </p>
      </div>
    </div>
  );
}

function HandlePicker({ value, onChange, handles }) {
  const grouped = useMemo(() => {
    const groups = [];
    const byGamme = {};
    for (const h of handles) {
      if (!byGamme[h.gamme]) { byGamme[h.gamme] = []; groups.push(h.gamme); }
      byGamme[h.gamme].push(h);
    }
    return groups.map((gamme) => [gamme, byGamme[gamme]]);
  }, [handles]);
  const selected = handles.find((h) => h.id === value) || handles[0];
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="flex items-center gap-1.5">
        <span className="text-sm font-bold">Poignée</span>
        <Select value={selected?.id} onValueChange={onChange}>
          <SelectTrigger data-testid="schema-default-handle" className="h-9 min-w-[210px] text-xs">
            <SelectValue>
              <span className="flex items-center gap-2">
                <HandlePhoto image={selected?.image} alt={selected?.name} />
                <span className="truncate">{selected?.name}</span>
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {grouped.map(([gamme, items]) => (
              <SelectGroup key={gamme}>
                <SelectLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">{gamme}</SelectLabel>
                {items.map((h) => (
                  <SelectItem key={h.id} value={h.id} data-testid={`handle-option-${h.id}`}>
                    <span className="flex items-center gap-2">
                      <HandlePhoto image={h.image} alt={h.name} size="lg" />
                      <span className="truncate">{h.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
      </label>
      <HandlePreviewCard handle={selected} />
    </div>
  );
}

function FileurToggle({ testId, label, checked, width, onCheck, onWidth }) {
  const widthMm = Math.round(width * 10);
  const bump = (delta) => {
    const next = Math.min(150, Math.max(10, widthMm + delta));
    onWidth(next / 10);
  };
  return (
    <div className={`rounded-lg border p-3 transition-colors ${checked ? "border-primary bg-primary/5" : "border-border bg-background"}`}>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          data-testid={testId}
          checked={!!checked}
          onChange={(e) => onCheck(e.target.checked)}
          className="w-4 h-4 accent-primary"
        />
        <span className="text-xs font-medium">{label}</span>
      </label>
      {checked && (
        <div className="mt-2 flex items-center gap-1">
          <button
            type="button"
            data-testid={`${testId}-width-minus`}
            onClick={() => bump(-10)}
            disabled={widthMm <= 10}
            className="w-6 h-7 rounded border border-border bg-background hover:border-primary hover:bg-primary/5 disabled:opacity-40 text-xs font-bold transition"
          >
            −
          </button>
          <div
            data-testid={`${testId}-width`}
            className="w-14 h-7 flex items-center justify-center rounded bg-primary/10 border border-primary/30 text-xs tabular-nums font-heading"
          >
            {widthMm}
          </div>
          <button
            type="button"
            data-testid={`${testId}-width-plus`}
            onClick={() => bump(10)}
            disabled={widthMm >= 150}
            className="w-6 h-7 rounded border border-border bg-background hover:border-primary hover:bg-primary/5 disabled:opacity-40 text-xs font-bold transition"
          >
            +
          </button>
          <span className="text-[9px] text-muted-foreground">mm</span>
        </div>
      )}
    </div>
  );
}

function DragPill({ kind, label, testId, vertical = false, activeMode = null, onSetActiveMode = null }) {
  const isActive = activeMode === kind;
  return (
    <div
      draggable
      data-testid={testId}
      onDragStart={(e) => {
        e.dataTransfer.setData("application/x-atelier-element", kind);
        e.dataTransfer.effectAllowed = "copy";
        // Sur desktop, on n'active pas le mode click-to-place (le drag prend le relais).
        if (onSetActiveMode) onSetActiveMode(null);
      }}
      onClick={() => {
        // Fallback tactile / accessibilité clavier : active/désactive le mode "click-to-place".
        if (!onSetActiveMode) return;
        if (isActive) {
          onSetActiveMode(null);
        } else {
          onSetActiveMode(kind);
          toast.info(`Tapez dans le meuble pour poser un ${label.toLowerCase()}`, {
            description: "Astuce : la position verticale du clic définit la hauteur.",
            duration: 3500,
          });
        }
      }}
      role={onSetActiveMode ? "button" : undefined}
      className={`${vertical
        ? "h-12 min-w-14 px-2 flex flex-col items-center justify-center gap-0.5 rounded-md border"
        : "text-xs px-3 py-1.5 rounded-full font-medium border inline-flex items-center gap-2"} ${
        isActive
          ? "bg-primary text-primary-foreground border-primary shadow-sm"
          : "bg-background text-foreground border-border hover:border-primary/60 hover:bg-primary/5"
      } cursor-grab active:cursor-grabbing select-none transition-colors text-center`}
      title={`Glisser dans une section pour placer un ${label.toLowerCase()} — ou taper pour activer le mode placement`}
    >
      <MiniIcon kind={kind} size={vertical ? 22 : 18} color={isActive ? "#FFF" : "#0465C1"} />
      <span className={vertical ? "text-[9px] leading-tight font-medium" : ""}>{label}</span>
    </div>
  );
}
